/**
 * SupabaseService - Direct Supabase client queries for all PPM entities.
 *
 * This module mirrors every function in dataService.ts but fetches from
 * Supabase tables instead of mock JSON files or the REST API.
 *
 * Column naming: Supabase tables use snake_case. We convert to camelCase
 * on the way out so the rest of the app (types, components, hooks) is
 * unchanged.
 */

import { supabase } from '@/lib/supabase'
import { uploadObject } from '@/lib/objectStorage'
import { getCurrentOrganizationId, isDemoMode } from '@/lib/orgContext'
import { currentProjectEntitlement, mayReadRow, projectScopeFor } from '@/config/projectEntitlements'
import { evaluateOnEvent } from '@/services/sopEngine'
import type {
  Portfolio, Project, Risk, WbsElement, Task, Resource,
  BaselineModel, BaselineWbsSummary, BaselineTimePhaseEntry,
  BurdenRateDeck, ResourceCategory, ScheduleVersionSummary,
  BaselineChangeRequest, BcrAffectedElement, RevenuePlanEntry, Milestone,
} from '@/types/ppm'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert a snake_case DB row into camelCase matching the TS interfaces. */
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
}

function transformKeys(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    out[snakeToCamel(k)] = v
  }
  return out
}

function transformRow<T>(row: Record<string, unknown>): T {
  return transformKeys(row) as T
}

function transformRows<T>(rows: Record<string, unknown>[]): T[] {
  return rows.map(r => transformRow<T>(r))
}

/**
 * Re-assemble the nested `budget` and `schedule` objects that the app
 * expects on Portfolio / Project / WbsElement rows. The DB stores these
 * as flat columns (budget_total, schedule_spi, etc.).
 */
function assemblePortfolio(row: Record<string, unknown>): Portfolio {
  const r = transformKeys(row)
  return {
    ...r,
    budget: {
      total: row.budget_total as number ?? 0,
      spent: row.budget_spent as number ?? 0,
      committed: row.budget_committed as number ?? 0,
      available: row.budget_available as number ?? 0,
      currency: row.budget_currency as string ?? 'USD',
    },
    kpis: {
      activeProjects: row.kpi_active_projects as number ?? 0,
      completedProjects: row.kpi_completed_projects as number ?? 0,
      atRiskProjects: row.kpi_at_risk_projects as number ?? 0,
      avgSpi: row.kpi_avg_spi as number ?? 0,
      avgCpi: row.kpi_avg_cpi as number ?? 0,
      totalRisks: row.kpi_total_risks as number ?? 0,
      openIssues: row.kpi_open_issues as number ?? 0,
      childPortfolioCount: row.kpi_child_portfolio_count as number ?? 0,
    },
  } as unknown as Portfolio
}

function assembleProject(row: Record<string, unknown>): Project {
  const r = transformKeys(row)
  return {
    ...r,
    budget: {
      total: row.budget_total as number ?? 0,
      spent: row.budget_spent as number ?? 0,
      committed: row.budget_committed as number ?? 0,
      available: row.budget_available as number ?? 0,
      currency: row.budget_currency as string ?? 'USD',
    },
    schedule: {
      percentComplete: row.schedule_percent_complete as number ?? 0,
      spi: row.schedule_spi as number ?? 0,
      cpi: row.schedule_cpi as number ?? 0,
      scheduleVarianceDays: row.schedule_variance_days as number ?? 0,
    },
    fiscalYearBudgets: Array.isArray(row.fiscal_year_budgets) ? row.fiscal_year_budgets : [],
    agileMetrics: row.agile_metrics ?? undefined,
    wbsElements: row.wbs_elements ?? [],
  } as unknown as Project
}

function assembleWbs(row: Record<string, unknown>): WbsElement {
  const r = transformKeys(row)
  return {
    ...r,
    budget: {
      total: row.budget_total as number ?? 0,
      spent: row.budget_spent as number ?? 0,
      committed: row.budget_committed as number ?? 0,
      available: row.budget_available as number ?? 0,
      currency: row.budget_currency as string ?? 'USD',
    },
    schedule: {
      percentComplete: row.schedule_percent_complete as number ?? 0,
      spi: row.schedule_spi as number ?? 0,
      cpi: row.schedule_cpi as number ?? 0,
      scheduleVarianceDays: row.schedule_variance_days as number ?? 0,
    },
  } as unknown as WbsElement
}

function assembleTask(row: Record<string, unknown>): Task {
  return transformRow<Task>(row)
}

// Tables that support organization_id scoping.
// NOTE: `users` was previously included here, but public.users has no
// organization_id column on the remote schema, so the filter silently
// errored to empty results whenever an orgId was set or the app was
// out of demo mode. User records are global; org membership lives
// elsewhere (auth_users / portfolio_assignments).
const ORG_SCOPED_TABLES = new Set([
  'portfolios', 'projects', 'risks', 'wbs_elements', 'tasks', 'resources',
])

async function queryTable<T>(
  table: string,
  opts?: {
    filter?: Record<string, string | undefined>,
    transform?: (row: Record<string, unknown>) => T,
    select?: string,
    skipOrgFilter?: boolean,
  },
): Promise<T[]> {
  let q = supabase.from(table).select(opts?.select ?? '*')
  // A user restricted to chosen projects (app_access.allowed_projects, 00541)
  // reads only those projects, their WBS, tasks and risks, and the portfolios
  // that hold them, whatever organization is active (or none, as for a newly
  // approved outside user). This replaces the organization scope for them and
  // applies even to skipOrgFilter reads, so no list shows other projects.
  const projectScope = projectScopeFor(table, currentProjectEntitlement())
  if (projectScope) {
    q = q.in(projectScope.column, projectScope.ids)
  } else if (!opts?.skipOrgFilter && ORG_SCOPED_TABLES.has(table)) {
    // Auto-scope by organization_id for org-scoped tables
    const orgId = getCurrentOrganizationId()
    if (orgId) {
      q = q.eq('organization_id', orgId)
    } else if (!isDemoMode()) {
      // Non-demo mode with no org ID → only return rows that also have no org ID
      // This ensures data created before org setup is still accessible
      q = q.is('organization_id', null)
    }
  }
  if (opts?.filter) {
    for (const [col, val] of Object.entries(opts.filter)) {
      if (val !== undefined) q = q.eq(col, val)
    }
  }
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [${table}]: ${error.message}`)
  if (!data) return []
  if (opts?.transform) return data.map(r => opts.transform!(r as unknown as Record<string, unknown>))
  return transformRows<T>(data as unknown as Record<string, unknown>[])
}

async function queryOne<T>(
  table: string,
  id: string,
  transform?: (row: Record<string, unknown>) => T,
): Promise<T | null> {
  // A project-restricted user cannot open a project or portfolio outside their list by id.
  if (!mayReadRow(table, id, currentProjectEntitlement())) return null
  const { data, error } = await supabase.from(table).select('*').eq('id', id).maybeSingle()
  if (error) throw new Error(`Supabase query error [${table}/${id}]: ${error.message}`)
  if (!data) return null
  if (transform) return transform(data as Record<string, unknown>)
  return transformRow<T>(data as Record<string, unknown>)
}

// ---------------------------------------------------------------------------
// For tables that store the full record in a `data` jsonb column,
// we merge the id + data fields back into one object.
// ---------------------------------------------------------------------------
function mergeJsonbRow(row: Record<string, unknown>): Record<string, unknown> {
  const data = (row.data ?? {}) as Record<string, unknown>
  // Spread all columns (id, project_id, forecast_version_id, etc.) then overlay with data
  const { data: _data, created_at: _created_at, updated_at: _updated_at, ...columns } = row
  // Convert snake_case column keys to camelCase for the app layer
  const camelColumns: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(columns)) {
    camelColumns[k.replace(/_([a-z])/g, (_, c) => c.toUpperCase())] = v
  }
  return { ...camelColumns, ...data }
}

function mergeJsonbRows(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return rows.map(mergeJsonbRow)
}

async function queryJsonbTable(
  table: string,
  filterCol?: string,
  filterVal?: string,
): Promise<Array<Record<string, unknown>>> {
  let q = supabase.from(table).select('*')
  if (filterCol && filterVal) q = q.eq(filterCol, filterVal)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [${table}]: ${error.message}`)
  return mergeJsonbRows((data ?? []) as Record<string, unknown>[])
}

// ---------------------------------------------------------------------------
// Portfolios
// ---------------------------------------------------------------------------
export async function getPortfolios(): Promise<Portfolio[]> {
  return queryTable('portfolios', { transform: assemblePortfolio })
}

/** ALL portfolios regardless of the active-org scope. For org DISCOVERY (e.g.
 *  the sandbox org picker asking "which orgs own a portfolio?") — the scoped
 *  getPortfolios() can't answer that with no org selected (in sandbox it
 *  filters to organization_id IS NULL, a chicken-and-egg). */
export async function getPortfoliosUnscoped(): Promise<Portfolio[]> {
  return queryTable('portfolios', { transform: assemblePortfolio, skipOrgFilter: true })
}

export async function getPortfolioById(id: string): Promise<Portfolio | null> {
  return queryOne('portfolios', id, assemblePortfolio)
}

export async function getPortfolioHierarchy(): Promise<Portfolio[]> {
  const all = await getPortfolios()
  const byId = new Map<string, Portfolio & { children: Portfolio[] }>()
  all.forEach(p => byId.set(p.id, { ...p, children: [] }))
  const roots: Portfolio[] = []
  all.forEach(p => {
    const node = byId.get(p.id)!
    if (p.parentId && byId.has(p.parentId)) {
      byId.get(p.parentId)!.children.push(node)
    } else {
      roots.push(node)
    }
  })
  return roots
}

export async function createPortfolio(portfolio: Portfolio): Promise<void> {
  const orgId = getCurrentOrganizationId()
  const { error } = await supabase.from('portfolios').insert({
    id: portfolio.id,
    code: portfolio.code,
    name: portfolio.name,
    parent_id: portfolio.parentId,
    level: portfolio.level,
    path: portfolio.path,
    child_ids: portfolio.childIds,
    description: portfolio.description,
    status: portfolio.status,
    portfolio_type: portfolio.portfolioType ?? null,
    owner: portfolio.owner,
    owner_name: portfolio.ownerName,
    // Postgres rejects empty-string for date columns (PostgREST 22007).
    // The portfolio form leaves end_date optional, so coerce '' → null.
    start_date: portfolio.startDate || null,
    end_date: portfolio.endDate || null,
    budget_total: portfolio.budget.total,
    budget_spent: portfolio.budget.spent,
    budget_committed: portfolio.budget.committed,
    budget_available: portfolio.budget.available,
    budget_currency: portfolio.budget.currency,
    health_status: portfolio.healthStatus,
    projects: portfolio.projects,
    kpi_active_projects: portfolio.kpis.activeProjects,
    kpi_completed_projects: portfolio.kpis.completedProjects,
    kpi_at_risk_projects: portfolio.kpis.atRiskProjects,
    kpi_avg_spi: portfolio.kpis.avgSpi,
    kpi_avg_cpi: portfolio.kpis.avgCpi,
    kpi_total_risks: portfolio.kpis.totalRisks,
    kpi_open_issues: portfolio.kpis.openIssues,
    kpi_child_portfolio_count: portfolio.kpis.childPortfolioCount ?? 0,
    organization_id: orgId,
  })
  if (error) throw new Error(`Supabase insert error [portfolios]: ${error.message}`)

  // Update parent's child_ids if this is a child portfolio
  if (portfolio.parentId) {
    const { data: parent } = await supabase.from('portfolios').select('child_ids').eq('id', portfolio.parentId).maybeSingle()
    if (parent) {
      const existingIds = (parent.child_ids as string[]) ?? []
      await supabase.from('portfolios').update({
        child_ids: [...existingIds, portfolio.id],
        kpi_child_portfolio_count: existingIds.length + 1,
      }).eq('id', portfolio.parentId)
    }
  }

  void evaluateOnEvent({
    trigger: 'on_create',
    entityType: 'portfolios',
    entityId: portfolio.id,
    entityName: portfolio.name,
    payload: portfolio as unknown as Record<string, unknown>,
    portfolioId: portfolio.id,
    portfolioName: portfolio.name,
  })
}

/**
 * Patch update for an existing portfolio. Accepts a sparse Partial<Portfolio>
 * and writes only the columns the caller actually set. Used by
 * PortfolioTypeEditor and any future inline portfolio-edit affordances.
 *
 * Returns the updated row converted back to camelCase so the caller can
 * push it straight into the appStore.selectedPortfolio.
 */
export async function updatePortfolio(id: string, patch: Partial<Portfolio>): Promise<Portfolio> {
  const row: Record<string, unknown> = {}
  if (patch.name !== undefined) row.name = patch.name
  if (patch.description !== undefined) row.description = patch.description
  if (patch.status !== undefined) row.status = patch.status
  if (patch.portfolioType !== undefined) row.portfolio_type = patch.portfolioType ?? null
  if (patch.owner !== undefined) row.owner = patch.owner
  if (patch.ownerName !== undefined) row.owner_name = patch.ownerName
  if (patch.startDate !== undefined) row.start_date = patch.startDate || null
  if (patch.endDate !== undefined) row.end_date = patch.endDate || null
  if (patch.healthStatus !== undefined) row.health_status = patch.healthStatus
  if (patch.budget?.total !== undefined) row.budget_total = patch.budget.total
  if (patch.budget?.spent !== undefined) row.budget_spent = patch.budget.spent
  if (patch.budget?.committed !== undefined) row.budget_committed = patch.budget.committed
  if (patch.budget?.available !== undefined) row.budget_available = patch.budget.available
  if (patch.budget?.currency !== undefined) row.budget_currency = patch.budget.currency

  if (Object.keys(row).length === 0) {
    // Nothing to write - read and return the current row so callers can stay
    // simple and not branch on "is anything actually changing".
    const current = await queryOne<Portfolio>('portfolios', id, assemblePortfolio)
    if (!current) throw new Error(`Portfolio not found: ${id}`)
    return current
  }

  const { data, error } = await supabase
    .from('portfolios')
    .update(row)
    .eq('id', id)
    .select('*')
    .single()
  if (error) throw new Error(`Supabase update error [portfolios]: ${error.message}`)
  const saved = assemblePortfolio(data as Record<string, unknown>)
  void evaluateOnEvent({
    trigger: 'on_save',
    entityType: 'portfolios',
    entityId: id,
    entityName: saved.name,
    payload: saved as unknown as Record<string, unknown>,
    portfolioId: id,
    portfolioName: saved.name,
  })
  return saved
}

export async function checkPortfolioCodeExists(code: string): Promise<boolean> {
  const orgId = getCurrentOrganizationId()
  let q = supabase.from('portfolios').select('id', { count: 'exact', head: true }).eq('code', code)
  if (orgId) q = q.eq('organization_id', orgId)
  const { count, error } = await q
  if (error) throw new Error(`Supabase query error [portfolios/code-check]: ${error.message}`)
  return (count ?? 0) > 0
}

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------
export async function getProjects(portfolioId?: string): Promise<Project[]> {
  return queryTable('projects', {
    filter: portfolioId ? { portfolio_id: portfolioId } : undefined,
    transform: assembleProject,
  })
}

export async function getProjectById(id: string): Promise<Project | null> {
  return queryOne('projects', id, assembleProject)
}

/**
 * Insert a Draft project row from the direct/indirect startup wizard. Returns
 * the assembled Project so the caller can keep using the same shape it built.
 */
export async function createDraftProject(project: Project): Promise<Project> {
  const { error } = await supabase.from('projects').insert({
    id: project.id,
    code: project.code,
    name: project.name,
    description: project.description,
    portfolio_id: project.portfolioId || null,
    project_manager: project.projectManager || null,
    project_manager_name: project.projectManagerName || null,
    status: 'Draft',
    start_date: project.startDate || null,
    planned_end_date: project.plannedEndDate || null,
    actual_end_date: null,
    budget_total: project.budget.total,
    budget_spent: project.budget.spent,
    budget_committed: project.budget.committed,
    budget_available: project.budget.available,
    budget_currency: project.budget.currency,
    fiscal_year_budgets: project.fiscalYearBudgets ?? [],
    schedule_percent_complete: project.schedule.percentComplete,
    schedule_spi: project.schedule.spi,
    schedule_cpi: project.schedule.cpi,
    schedule_variance_days: project.schedule.scheduleVarianceDays,
    scheduling_method: project.schedulingMethod,
    delivery_methodology: project.deliveryMethodology ?? (project.schedulingMethod === 'agile' ? 'agile' : 'waterfall'),
    health_status: project.healthStatus,
    evm_enabled: project.evmEnabled,
    risk_score: project.riskScore,
    business_owner: project.businessOwner,
    priority: project.priority,
    project_type: project.projectType,
    funding_type: project.fundingType,
    contract_id: project.contractId,
    wbs_elements: project.wbsElements,
    is_capital_asset: project.isCapitalAsset ?? false,
    eac_cycle_frequency: project.eacCycleFrequency,
    phase: project.phase,
    lifecycle_profile_id: project.lifecycleProfileId,
    data_class: project.dataClass ?? 'production',
    // Localize to an organization: explicit assignment wins, else the active
    // org context (production projects resolve to the production org).
    organization_id: project.organizationId ?? getCurrentOrganizationId() ?? null,
    archived_at: null,
  })
  if (error) throw new Error(`Supabase insert error [projects]: ${error.message}`)
  return project
}

/** Patch an existing project's status (e.g., Draft -> Planning on activation). */
export async function updateProjectStatus(projectId: string, status: string): Promise<void> {
  const { data, error } = await supabase
    .from('projects')
    .update({ status })
    .eq('id', projectId)
    .select()
    .single()
  if (error) throw new Error(`Supabase update error [projects.status]: ${error.message}`)
  // Async SOP sweep on the persisted row. Block-severity gating for status
  // changes lives upstream when the wizards adopt the runSopGate pattern;
  // this is the always-on warn/info pass.
  void evaluateOnEvent({
    trigger: 'on_save',
    entityType: 'projects',
    entityId: projectId,
    entityName: (data as Record<string, unknown> | null)?.name as string | undefined,
    payload: ((data ?? { id: projectId, status }) as Record<string, unknown>),
  })
}

/** Set the overall project manager on an existing project row (e.g., when the
 *  Direct Project Startup wizard captures the PM after the draft is created). */
export async function setProjectManager(projectId: string, projectManager: string, projectManagerName: string): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .update({ project_manager: projectManager, project_manager_name: projectManagerName })
    .eq('id', projectId)
  if (error) throw new Error(`Supabase update error [projects.project_manager]: ${error.message}`)
}

/** Change the lifecycle profile on a project (the on-project editor lets a PM
 *  re-point a selected project at a different phase model after creation).
 *  Also moves the project onto the NEW profile's first phase: the phase stepper
 *  (ProjectDetail.PhaseStepper) chooses which lifecycle view to render from the
 *  project's phase NAME, so leaving a stale phase (e.g. an E&C "Concept" after
 *  switching to the SAP S/4HANA profile) would keep showing the old lifecycle and
 *  the stepper couldn't locate the current phase in the new phase set. */
export async function setProjectLifecycleProfile(projectId: string, lifecycleProfileId: string | null): Promise<void> {
  const update: Record<string, unknown> = { lifecycle_profile_id: lifecycleProfileId }
  if (lifecycleProfileId) {
    const { data: phaseRows } = await supabase
      .from('project_lifecycle_phases')
      .select('data')
      .eq('profile_id', lifecycleProfileId)
    const firstPhase = (phaseRows ?? [])
      .map((r) => (r as { data?: { name?: string; order?: number } }).data)
      .filter((d): d is { name?: string; order?: number } => !!d && typeof d.name === 'string')
      .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))[0]
    if (firstPhase?.name) update.phase = firstPhase.name
  }
  const { error } = await supabase
    .from('projects')
    .update(update)
    .eq('id', projectId)
  if (error) throw new Error(`Supabase update error [projects.lifecycle_profile_id]: ${error.message}`)
}

/** Flag / unflag a project as a reusable template (a copy source for the WBS +
 *  schedule "Copy from template" pickers). */
export async function setProjectIsTemplate(projectId: string, isTemplate: boolean): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .update({ is_template: isTemplate })
    .eq('id', projectId)
  if (error) throw new Error(`Supabase update error [projects.is_template]: ${error.message}`)
}

/** Soft-archive (or restore) a project. Sets/clears projects.archived_at. An
 *  archived project keeps its real status/history but drops out of the default
 *  project list. */
export async function setProjectArchived(projectId: string, archived: boolean): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .update({ archived_at: archived ? new Date().toISOString() : null })
    .eq('id', projectId)
  if (error) throw new Error(`Supabase update error [projects.archived_at]: ${error.message}`)
}

/** Align a project to an organization (localization). Pass null to clear. For
 *  production projects the caller enforces that this is a production org. */
export async function setProjectOrganization(projectId: string, organizationId: string | null): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .update({ organization_id: organizationId })
    .eq('id', projectId)
  if (error) throw new Error(`Supabase update error [projects.organization_id]: ${error.message}`)
}

// ---------------------------------------------------------------------------
// Award value categories + project award values + wizard drafts
// (migration 00240)
// ---------------------------------------------------------------------------

export interface AwardValueCategoryRow {
  id: string
  name: string
  kind: 'revenue' | 'cost' | 'hours'
  projectTypes: string[]
  sortOrder: number
  groupName?: string | null
  description?: string | null
  isReserve: boolean
}

export async function getAwardValueCategoryRows(): Promise<AwardValueCategoryRow[]> {
  const { data, error } = await supabase
    .from('award_value_categories')
    .select('id, name, kind, project_types, sort_order, group_name, description, is_reserve')
    .order('kind', { ascending: true })
    .order('sort_order', { ascending: true })
  if (error) throw new Error(`Supabase select [award_value_categories]: ${error.message}`)
  return (data ?? []).map((r) => ({
    id: r.id as string,
    name: r.name as string,
    kind: r.kind as 'revenue' | 'cost' | 'hours',
    projectTypes: (r.project_types as string[]) ?? [],
    sortOrder: (r.sort_order as number) ?? 0,
    groupName: (r.group_name as string | null) ?? null,
    description: (r.description as string | null) ?? null,
    isReserve: (r.is_reserve as boolean) ?? false,
  }))
}

export async function upsertAwardValueCategoryRow(row: AwardValueCategoryRow): Promise<void> {
  const { error } = await supabase.from('award_value_categories').upsert({
    id: row.id,
    name: row.name,
    kind: row.kind,
    project_types: row.projectTypes,
    sort_order: row.sortOrder,
    group_name: row.groupName ?? null,
    description: row.description ?? null,
    is_reserve: row.isReserve,
  })
  if (error) throw new Error(`Supabase upsert [award_value_categories]: ${error.message}`)
}

export async function deleteAwardValueCategoryRow(id: string): Promise<void> {
  const { error } = await supabase.from('award_value_categories').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete [award_value_categories]: ${error.message}`)
}

export interface ProjectAwardValueRow {
  id?: string
  projectId: string
  categoryId: string
  /** ISO month string (YYYY-MM) - switched from fiscal_year in migration 00267 so
   *  Direct project startup can phase as-awarded values by month. */
  period: string
  amount: number
  notes?: string | null
}

export async function getProjectAwardValues(projectId: string): Promise<ProjectAwardValueRow[]> {
  const { data, error } = await supabase
    .from('project_award_values')
    .select('id, project_id, category_id, period, amount, notes')
    .eq('project_id', projectId)
  if (error) throw new Error(`Supabase select [project_award_values]: ${error.message}`)
  return (data ?? []).map((r) => ({
    id: r.id as string,
    projectId: r.project_id as string,
    categoryId: r.category_id as string,
    period: r.period as string,
    amount: (r.amount as number) ?? 0,
    notes: (r.notes as string | null) ?? null,
  }))
}

export async function replaceProjectAwardValues(projectId: string, rows: ProjectAwardValueRow[]): Promise<void> {
  const del = await supabase.from('project_award_values').delete().eq('project_id', projectId)
  if (del.error) throw new Error(`Supabase delete [project_award_values]: ${del.error.message}`)
  if (rows.length === 0) return
  const ins = await supabase.from('project_award_values').insert(
    rows.map((r) => ({
      project_id: projectId,
      category_id: r.categoryId,
      period: r.period,
      amount: r.amount,
      notes: r.notes ?? null,
    })),
  )
  if (ins.error) throw new Error(`Supabase insert [project_award_values]: ${ins.error.message}`)
}

export interface ProjectWizardDraftRow {
  projectId: string
  kind: 'direct' | 'indirect' | 'request'
  currentStep: string | null
  state: Record<string, unknown>
}

export async function getProjectWizardDraft(projectId: string): Promise<ProjectWizardDraftRow | null> {
  const { data, error } = await supabase
    .from('project_wizard_drafts')
    .select('project_id, kind, current_step, state')
    .eq('project_id', projectId)
    .maybeSingle()
  if (error) throw new Error(`Supabase select [project_wizard_drafts]: ${error.message}`)
  if (!data) return null
  return {
    projectId: data.project_id as string,
    kind: data.kind as 'direct' | 'indirect' | 'request',
    currentStep: (data.current_step as string | null) ?? null,
    state: (data.state as Record<string, unknown>) ?? {},
  }
}

export async function upsertProjectWizardDraft(row: ProjectWizardDraftRow): Promise<void> {
  const { error } = await supabase.from('project_wizard_drafts').upsert({
    project_id: row.projectId,
    kind: row.kind,
    current_step: row.currentStep,
    state: row.state,
  })
  if (error) throw new Error(`Supabase upsert [project_wizard_drafts]: ${error.message}`)
}

export async function deleteProjectWizardDraft(projectId: string): Promise<void> {
  const { error } = await supabase.from('project_wizard_drafts').delete().eq('project_id', projectId)
  if (error) throw new Error(`Supabase delete [project_wizard_drafts]: ${error.message}`)
}

// ---------------------------------------------------------------------------
// ERP sync (migration 00262)
// ---------------------------------------------------------------------------

export interface ErpSyncConfigRow {
  orgId: string
  destination: string
  defaultProjectProfile: string | null
  defaultCodingMaskPrefix: string | null
  defaultGlobalHierarchyRootNode: string | null
  bapiTarget: 'sap_s4' | 'sap_ecc' | 'mock'
  enabled: boolean
}

export async function getErpSyncConfig(orgId: string): Promise<ErpSyncConfigRow | null> {
  const { data, error } = await supabase
    .from('erp_sync_config')
    .select('*')
    .eq('org_id', orgId)
    .maybeSingle()
  if (error) throw new Error(`Supabase select [erp_sync_config]: ${error.message}`)
  if (!data) return null
  return {
    orgId: data.org_id as string,
    destination: data.destination as string,
    defaultProjectProfile: (data.default_project_profile as string | null) ?? null,
    defaultCodingMaskPrefix: (data.default_coding_mask_prefix as string | null) ?? null,
    defaultGlobalHierarchyRootNode: (data.default_global_hierarchy_root_node as string | null) ?? null,
    bapiTarget: (data.bapi_target as 'sap_s4' | 'sap_ecc' | 'mock') ?? 'sap_s4',
    enabled: !!data.enabled,
  }
}

/**
 * Check whether the given Global Hierarchy root node is already in use by
 * another project. The reserve endpoint calls this before attempting to
 * create the SAP PD so we can surface a collision error before the BAPI
 * even runs. Returns the project id that owns the node, or null.
 */
export async function findProjectByGlobalHierarchyRootNode(node: string): Promise<{ projectId: string; projectName: string } | null> {
  const { data, error } = await supabase
    .from('projects')
    .select('id, name')
    .eq('global_hierarchy_root_node', node)
    .limit(1)
    .maybeSingle()
  if (error) throw new Error(`Supabase select [projects.global_hierarchy_root_node]: ${error.message}`)
  if (!data) return null
  return { projectId: data.id as string, projectName: data.name as string }
}

export async function setProjectGlobalHierarchyRootNode(projectId: string, node: string): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .update({ global_hierarchy_root_node: node })
    .eq('id', projectId)
  if (error) throw new Error(`Supabase update [projects.global_hierarchy_root_node]: ${error.message}`)
}

export interface ProjectErpSyncRunRow {
  id?: string
  projectId: string
  runType: 'reserve' | 'push_wbs' | 'push_wbs_execution' | 'push_wbs_financial' | 'push_wbs_cost_management' | 'push_attributes' | 'reconcile' | 'rollback'
  status: 'queued' | 'in_progress' | 'success' | 'error' | 'cancelled'
  globalHierarchyRootNode?: string | null
  externalProjectId?: string | null
  bapiCalled?: string | null
  requestPayload?: Record<string, unknown> | null
  responsePayload?: Record<string, unknown> | null
  errorCode?: string | null
  errorMessage?: string | null
  rowsAttempted?: number
  rowsSucceeded?: number
  rowsFailed?: number
  startedAt?: string
  completedAt?: string | null
  triggeredBy?: string | null
  notes?: string | null
}

export async function insertProjectErpSyncRun(row: ProjectErpSyncRunRow): Promise<string> {
  const { data, error } = await supabase
    .from('project_erp_sync_runs')
    .insert({
      project_id: row.projectId,
      run_type: row.runType,
      status: row.status,
      global_hierarchy_root_node: row.globalHierarchyRootNode ?? null,
      external_project_id: row.externalProjectId ?? null,
      bapi_called: row.bapiCalled ?? null,
      request_payload: row.requestPayload ?? null,
      response_payload: row.responsePayload ?? null,
      error_code: row.errorCode ?? null,
      error_message: row.errorMessage ?? null,
      rows_attempted: row.rowsAttempted ?? 0,
      rows_succeeded: row.rowsSucceeded ?? 0,
      rows_failed: row.rowsFailed ?? 0,
      completed_at: row.completedAt ?? null,
      triggered_by: row.triggeredBy ?? null,
      notes: row.notes ?? null,
    })
    .select('id')
    .single()
  if (error) throw new Error(`Supabase insert [project_erp_sync_runs]: ${error.message}`)
  return (data as { id: string }).id
}

/**
 * Update a previously-queued run row to its terminal state. Used by the
 * erpSync service to flip the in_progress placeholder to success / error
 * once the SAP call returns - replaces the older pattern of inserting a
 * second row (which left orphaned in_progress rows showing "in flight"
 * in the audit table forever).
 */
export async function updateProjectErpSyncRun(
  runId: string,
  patch: Partial<Omit<ProjectErpSyncRunRow, 'id' | 'projectId' | 'runType'>>,
): Promise<void> {
  const update: Record<string, unknown> = {}
  if (patch.status !== undefined) update.status = patch.status
  if (patch.globalHierarchyRootNode !== undefined) update.global_hierarchy_root_node = patch.globalHierarchyRootNode
  if (patch.externalProjectId !== undefined) update.external_project_id = patch.externalProjectId
  if (patch.bapiCalled !== undefined) update.bapi_called = patch.bapiCalled
  if (patch.requestPayload !== undefined) update.request_payload = patch.requestPayload
  if (patch.responsePayload !== undefined) update.response_payload = patch.responsePayload
  if (patch.errorCode !== undefined) update.error_code = patch.errorCode
  if (patch.errorMessage !== undefined) update.error_message = patch.errorMessage
  if (patch.rowsAttempted !== undefined) update.rows_attempted = patch.rowsAttempted
  if (patch.rowsSucceeded !== undefined) update.rows_succeeded = patch.rowsSucceeded
  if (patch.rowsFailed !== undefined) update.rows_failed = patch.rowsFailed
  if (patch.completedAt !== undefined) update.completed_at = patch.completedAt
  if (patch.notes !== undefined) update.notes = patch.notes
  const { error } = await supabase
    .from('project_erp_sync_runs')
    .update(update)
    .eq('id', runId)
  if (error) throw new Error(`Supabase update [project_erp_sync_runs]: ${error.message}`)
}

export async function getProjectErpSyncRuns(projectId: string, limit = 50): Promise<ProjectErpSyncRunRow[]> {
  const { data, error } = await supabase
    .from('project_erp_sync_runs')
    .select('*')
    .eq('project_id', projectId)
    .order('started_at', { ascending: false })
    .limit(limit)
  if (error) throw new Error(`Supabase select [project_erp_sync_runs]: ${error.message}`)
  return (data ?? []).map((r): ProjectErpSyncRunRow => ({
    id: r.id as string,
    projectId: r.project_id as string,
    runType: r.run_type as ProjectErpSyncRunRow['runType'],
    status: r.status as ProjectErpSyncRunRow['status'],
    globalHierarchyRootNode: (r.global_hierarchy_root_node as string | null) ?? null,
    externalProjectId: (r.external_project_id as string | null) ?? null,
    bapiCalled: (r.bapi_called as string | null) ?? null,
    requestPayload: (r.request_payload as Record<string, unknown> | null) ?? null,
    responsePayload: (r.response_payload as Record<string, unknown> | null) ?? null,
    errorCode: (r.error_code as string | null) ?? null,
    errorMessage: (r.error_message as string | null) ?? null,
    rowsAttempted: (r.rows_attempted as number) ?? 0,
    rowsSucceeded: (r.rows_succeeded as number) ?? 0,
    rowsFailed: (r.rows_failed as number) ?? 0,
    startedAt: r.started_at as string,
    completedAt: (r.completed_at as string | null) ?? null,
    triggeredBy: (r.triggered_by as string | null) ?? null,
    notes: (r.notes as string | null) ?? null,
  }))
}

export interface WbsErpSyncRow {
  id?: string
  projectId: string
  hierarchyNodeId: string
  hierarchyNodeCode?: string | null
  parentNodeId?: string | null
  level: number
  sapWbsId?: string | null
  status: 'Pending' | 'Pushed' | 'Error' | 'Drift' | 'Skipped'
  lastRunId?: string | null
  lastPushedAt?: string | null
  errorCode?: string | null
  errorMessage?: string | null
}

export async function getWbsErpSyncRows(projectId: string): Promise<WbsErpSyncRow[]> {
  const { data, error } = await supabase
    .from('wbs_erp_sync_rows')
    .select('*')
    .eq('project_id', projectId)
    .order('level', { ascending: true })
  if (error) throw new Error(`Supabase select [wbs_erp_sync_rows]: ${error.message}`)
  return (data ?? []).map((r): WbsErpSyncRow => ({
    id: r.id as string,
    projectId: r.project_id as string,
    hierarchyNodeId: r.hierarchy_node_id as string,
    hierarchyNodeCode: (r.hierarchy_node_code as string | null) ?? null,
    parentNodeId: (r.parent_node_id as string | null) ?? null,
    level: (r.level as number) ?? 0,
    sapWbsId: (r.sap_wbs_id as string | null) ?? null,
    status: r.status as WbsErpSyncRow['status'],
    lastRunId: (r.last_run_id as string | null) ?? null,
    lastPushedAt: (r.last_pushed_at as string | null) ?? null,
    errorCode: (r.error_code as string | null) ?? null,
    errorMessage: (r.error_message as string | null) ?? null,
  }))
}

export async function upsertWbsErpSyncRow(row: WbsErpSyncRow): Promise<void> {
  const { error } = await supabase.from('wbs_erp_sync_rows').upsert({
    project_id: row.projectId,
    hierarchy_node_id: row.hierarchyNodeId,
    hierarchy_node_code: row.hierarchyNodeCode ?? null,
    parent_node_id: row.parentNodeId ?? null,
    level: row.level,
    sap_wbs_id: row.sapWbsId ?? null,
    status: row.status,
    last_run_id: row.lastRunId ?? null,
    last_pushed_at: row.lastPushedAt ?? null,
    error_code: row.errorCode ?? null,
    error_message: row.errorMessage ?? null,
  }, { onConflict: 'project_id,hierarchy_node_id' })
  if (error) throw new Error(`Supabase upsert [wbs_erp_sync_rows]: ${error.message}`)
}

/** Patch the project's ERP-side state after a reserve/push completes. */
export async function setProjectErpState(projectId: string, patch: { externalProjectId?: string | null; externalSystem?: string | null; erpSyncStatus?: string | null }): Promise<void> {
  const update: Record<string, unknown> = {}
  if (patch.externalProjectId !== undefined) update.external_project_id = patch.externalProjectId
  if (patch.externalSystem !== undefined) update.external_system = patch.externalSystem
  if (patch.erpSyncStatus !== undefined) update.erp_sync_status = patch.erpSyncStatus
  if (Object.keys(update).length === 0) return
  const { error } = await supabase.from('projects').update(update).eq('id', projectId)
  if (error) throw new Error(`Supabase update [projects ERP state]: ${error.message}`)
}

// ---------------------------------------------------------------------------
// Project lead currency - drives the Currency Rates page filter on TCURR.
// One column on the projects table, set by the user on the Currency Rates
// page header.
// ---------------------------------------------------------------------------

/** Postgres "undefined_column" - raised when an instance hasn't run the
 *  migration that adds `lead_currency`. PostgREST surfaces it as code 42703. */
function isMissingLeadCurrencyColumn(error: { code?: string; message?: string }): boolean {
  return error.code === '42703' || /lead_currency/i.test(error.message ?? '')
}

export async function getProjectLeadCurrency(projectId: string): Promise<string> {
  const { data, error } = await supabase
    .from('projects')
    .select('lead_currency, budget_currency')
    .eq('id', projectId)
    .maybeSingle()
  if (error) {
    // Degrade gracefully on instances without the lead_currency column
    // (migration 00314 not applied): fall back to budget_currency so the
    // Currency Rates page still pulls/pushes instead of the whole query
    // erroring and the Pull button silently doing nothing.
    if (isMissingLeadCurrencyColumn(error)) {
      const { data: b } = await supabase
        .from('projects')
        .select('budget_currency')
        .eq('id', projectId)
        .maybeSingle()
      return (b as { budget_currency?: string | null } | null)?.budget_currency ?? 'USD'
    }
    throw new Error(`Supabase select [projects.lead_currency]: ${error.message}`)
  }
  if (!data) return 'USD'
  const row = data as { lead_currency?: string | null; budget_currency?: string | null }
  return row.lead_currency ?? row.budget_currency ?? 'USD'
}

export async function setProjectLeadCurrency(projectId: string, leadCurrency: string): Promise<void> {
  const code = (leadCurrency ?? '').trim().toUpperCase()
  if (code.length < 3 || code.length > 5) throw new Error(`Invalid currency code: "${leadCurrency}"`)
  const { error } = await supabase
    .from('projects')
    .update({ lead_currency: code })
    .eq('id', projectId)
  if (error) {
    if (isMissingLeadCurrencyColumn(error)) {
      throw new Error('Cannot save lead currency: the projects.lead_currency column is missing. Apply migration 00314_project_lead_currency.sql to this Supabase project, then retry.')
    }
    throw new Error(`Supabase update [projects.lead_currency]: ${error.message}`)
  }
}

// ---------------------------------------------------------------------------
// Project Currency Rates - project-wide FX rate cache, mirrored to SAP
// ZTMACH12_PRJ_FX via the ZUI_PRJ_FX_O4 service on push.
//
// Model: one row per (project_id, from_currency, to_currency). The
// wbs_element_code column is the SAP POSID stamped at push time
// (derived from the financial WBS root's sapPosid attribute); kept on
// the row for audit traceability.
// ---------------------------------------------------------------------------
export interface ProjectCurrencyRateRow {
  id: string
  projectId: string
  wbsElementCode: string | null
  fromCurrency: string
  toCurrency: string
  fromRatio: string
  exchangeRate: string
  toRatio: string
  rateType: string
  validFrom: string | null
  /** Whether the user keeps this rate for the project. Push to SAP only
   *  sends maintained rows. Defaults to true. */
  isMaintained: boolean
  sapSyncedAt: string | null
  sapSyncError: string | null
  updatedAt: string
}

// `isMaintained` is intentionally excluded from the write input: pull/upsert
// must NOT reset the user's selection. New rows take the column default
// (true); existing rows keep whatever the user set.
export type ProjectCurrencyRateInput = Omit<
  ProjectCurrencyRateRow,
  'id' | 'sapSyncedAt' | 'sapSyncError' | 'updatedAt' | 'isMaintained'
>

function assembleCurrencyRate(row: Record<string, unknown>): ProjectCurrencyRateRow {
  return {
    id: row.id as string,
    projectId: row.project_id as string,
    wbsElementCode: (row.wbs_element_code as string | null) ?? null,
    fromCurrency: row.from_currency as string,
    toCurrency: row.to_currency as string,
    fromRatio: String(row.from_ratio ?? '1'),
    exchangeRate: String(row.exchange_rate ?? '0'),
    toRatio: String(row.to_ratio ?? '1'),
    rateType: (row.rate_type as string | null) ?? 'M',
    validFrom: (row.valid_from as string | null) ?? null,
    isMaintained: (row.is_maintained as boolean | null) ?? true,
    sapSyncedAt: (row.sap_synced_at as string | null) ?? null,
    sapSyncError: (row.sap_sync_error as string | null) ?? null,
    updatedAt: row.updated_at as string,
  }
}

export async function getProjectCurrencyRates(projectId: string): Promise<ProjectCurrencyRateRow[]> {
  const { data, error } = await supabase
    .from('project_currency_rates')
    .select('*')
    .eq('project_id', projectId)
    .order('from_currency', { ascending: true })
    .order('to_currency', { ascending: true })
  if (error) throw new Error(`Supabase select [project_currency_rates]: ${error.message}`)
  return (data ?? []).map(assembleCurrencyRate)
}

export async function upsertProjectCurrencyRate(input: ProjectCurrencyRateInput): Promise<ProjectCurrencyRateRow> {
  const { data, error } = await supabase
    .from('project_currency_rates')
    .upsert({
      project_id: input.projectId,
      wbs_element_code: input.wbsElementCode,
      from_currency: input.fromCurrency,
      to_currency: input.toCurrency,
      from_ratio: input.fromRatio,
      exchange_rate: input.exchangeRate,
      to_ratio: input.toRatio,
      rate_type: input.rateType ?? 'M',
      valid_from: input.validFrom,
      sap_synced_at: null,
      sap_sync_error: null,
    }, { onConflict: 'project_id,from_currency,to_currency' })
    .select()
    .single()
  if (error) throw new Error(`Supabase upsert [project_currency_rates]: ${error.message}`)
  return assembleCurrencyRate(data as Record<string, unknown>)
}

export async function deleteProjectCurrencyRate(id: string): Promise<void> {
  const { error } = await supabase.from('project_currency_rates').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete [project_currency_rates]: ${error.message}`)
}

/**
 * Upsert many FX rates in one shot - used by the TCURR pull, which can
 * bring hundreds of pairs for a major lead currency. Chunked so a single
 * rejected row (e.g. a CHECK violation) only fails its own chunk and to
 * stay under PostgREST payload limits. Returns the number of rows written.
 */
export async function bulkUpsertProjectCurrencyRates(
  projectId: string,
  rows: Array<Omit<ProjectCurrencyRateInput, 'projectId'>>,
): Promise<{ written: number; failedChunks: number; firstError: string | null }> {
  const CHUNK = 200
  let written = 0
  let failedChunks = 0
  let firstError: string | null = null
  for (let i = 0; i < rows.length; i += CHUNK) {
    const slice = rows.slice(i, i + CHUNK).map((input) => ({
      project_id: projectId,
      wbs_element_code: input.wbsElementCode,
      from_currency: input.fromCurrency,
      to_currency: input.toCurrency,
      from_ratio: input.fromRatio,
      exchange_rate: input.exchangeRate,
      to_ratio: input.toRatio,
      rate_type: input.rateType ?? 'M',
      valid_from: input.validFrom,
      sap_synced_at: null,
      sap_sync_error: null,
    }))
    const { data, error } = await supabase
      .from('project_currency_rates')
      .upsert(slice, { onConflict: 'project_id,from_currency,to_currency' })
      .select('id')
    if (error) {
      failedChunks++
      if (!firstError) firstError = error.message
    } else {
      written += data?.length ?? 0
    }
  }
  return { written, failedChunks, firstError }
}

/**
 * Delete every FX rate for a project whose (from,to) pair is NOT in
 * keepPairs. Lets the TCURR pull act as a full replace of the rate list
 * rather than a merge. keepPairs entries are `${fromCurrency}>${toCurrency}`.
 * Returns the number of rows removed.
 */
export async function deleteProjectCurrencyRatesNotInPairs(
  projectId: string,
  keepPairs: string[],
): Promise<number> {
  const { data, error } = await supabase
    .from('project_currency_rates')
    .select('id, from_currency, to_currency')
    .eq('project_id', projectId)
  if (error) throw new Error(`Supabase select [project_currency_rates prune]: ${error.message}`)
  const keep = new Set(keepPairs)
  const ids = (data ?? [])
    .map((r) => r as { id: string; from_currency: string; to_currency: string })
    .filter((r) => !keep.has(`${r.from_currency}>${r.to_currency}`))
    .map((r) => r.id)
  if (ids.length === 0) return 0
  const CHUNK = 200
  for (let i = 0; i < ids.length; i += CHUNK) {
    const { error: delErr } = await supabase
      .from('project_currency_rates')
      .delete()
      .in('id', ids.slice(i, i + CHUNK))
    if (delErr) throw new Error(`Supabase delete [project_currency_rates prune]: ${delErr.message}`)
  }
  return ids.length
}

/**
 * Set the "maintain for this project" flag on one or many rates. Used by
 * the per-row checkbox and the select-all header. Chunked for large
 * selections (a major lead currency can bring hundreds of pairs).
 */
export async function setProjectCurrencyRatesMaintained(
  ids: string[],
  isMaintained: boolean,
): Promise<void> {
  if (ids.length === 0) return
  const CHUNK = 200
  for (let i = 0; i < ids.length; i += CHUNK) {
    const { error } = await supabase
      .from('project_currency_rates')
      .update({ is_maintained: isMaintained })
      .in('id', ids.slice(i, i + CHUNK))
    if (error) throw new Error(`Supabase update [project_currency_rates.is_maintained]: ${error.message}`)
  }
}

export async function markProjectCurrencyRateSynced(
  id: string,
  result: { ok: boolean; error?: string | null; posid?: string | null },
): Promise<void> {
  const patch: Record<string, unknown> = {
    sap_synced_at: result.ok ? new Date().toISOString() : null,
    sap_sync_error: result.ok ? null : (result.error ?? 'Unknown SAP error'),
  }
  if (result.posid !== undefined) patch.wbs_element_code = result.posid
  const { error } = await supabase
    .from('project_currency_rates')
    .update(patch)
    .eq('id', id)
  if (error) throw new Error(`Supabase update [project_currency_rates sync state]: ${error.message}`)
}

// ---------------------------------------------------------------------------
// WBS Elements
// ---------------------------------------------------------------------------
export async function getWbsElements(projectId?: string): Promise<WbsElement[]> {
  return queryTable('wbs_elements', {
    filter: projectId ? { project_id: projectId } : undefined,
    transform: assembleWbs,
  })
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------
export async function getTasks(wbsId?: string): Promise<Task[]> {
  return queryTable('tasks', {
    filter: wbsId ? { wbs_element_id: wbsId } : undefined,
    transform: assembleTask,
  })
}

export async function getTasksByProject(projectId: string): Promise<Task[]> {
  return queryTable('tasks', {
    filter: { project_id: projectId },
    transform: assembleTask,
  })
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------
export async function getUsers() {
  return queryTable<{ id: string; name: string; email: string; title: string; department: string; organization: string; primaryOrganizationId?: string | null; isPortfolioOwnerEligible?: boolean }>('users')
}

export async function createUser(input: {
  id?: string
  name: string
  email: string
  title?: string
  department?: string
  organization?: string
  primaryOrganizationId?: string | null
  isPortfolioOwnerEligible?: boolean
}) {
  // An email may belong to exactly one roster row. Duplicate rows for one email
  // break the login roster-bind (fetchRosterIdentityByEmail), so reject a
  // collision here. Case-insensitive pre-check for a friendly message; the DB's
  // unique index on lower(email) (migration 00460) is the authoritative backstop
  // that also closes the check-then-insert race.
  const emailTrimmed = input.email?.trim()
  if (emailTrimmed) {
    const { data: existing, error: exErr } = await supabase
      .from('users')
      .select('id, name, email')
      .ilike('email', emailTrimmed)
    if (exErr) throw exErr
    const clash = (existing ?? []).find(
      (r) => String((r as { email?: string }).email ?? '').trim().toLowerCase() === emailTrimmed.toLowerCase(),
    )
    if (clash) {
      throw new Error(`A user with the email ${emailTrimmed} already exists (${(clash as { name?: string }).name ?? 'existing user'}). Each person must have a unique email.`)
    }
  }
  const id = input.id || `user_${Math.random().toString(36).slice(2, 10)}`
  const { data, error } = await supabase.from('users').insert({
    id,
    name: input.name,
    email: input.email,
    title: input.title ?? null,
    department: input.department ?? null,
    organization: input.organization ?? null,
    primary_organization_id: input.primaryOrganizationId ?? null,
    is_portfolio_owner_eligible: input.isPortfolioOwnerEligible ?? false,
  }).select().single()
  if (error) {
    if ((error as { code?: string }).code === '23505') {
      throw new Error(`A user with the email ${input.email} already exists. Each person must have a unique email.`)
    }
    throw error
  }
  return data
}

export async function setUserPortfolioOwnerEligibility(userId: string, eligible: boolean) {
  const { error } = await supabase
    .from('users')
    .update({ is_portfolio_owner_eligible: eligible, updated_at: new Date().toISOString() })
    .eq('id', userId)
  if (error) throw error
}

/**
 * Assign one or more users a PRIMARY tenant organization (Personnel Studio).
 * Writes the home org to every place that keys off it:
 *  1. `public.users` - the org FK (`primary_organization_id`, which the login
 *     org-pin in App.tsx and the Organizations context read) plus the legacy
 *     display text `organization` (kept in sync with the org's name so older
 *     readers like scopeUsersToCompany keep working).
 *  2. `public.auth_users_config` - the SAME `primary_organization_id`, because
 *     the project / portfolio / resource visibility gate resolves a user's
 *     accessible orgs from `auth_users_config` (getAccessibleOrgIdsForUser ->
 *     filterByAccessibleOrgs), NOT from `public.users`. Without it a non-admin
 *     provisioned in auth_users_config carries an empty accessible-org set and
 *     every org-tagged row is filtered out, so they see ZERO projects despite
 *     being "assigned" to the org. Harmless no-op for users not yet in
 *     auth_users_config (the gate fails open for them).
 *  3. A matching `user_organization_access` membership row (the primary org is
 *     also a membership), so the multi-org membership model and the roster
 *     userId->orgIds map stay consistent.
 * Pass orgId=null to clear the primary in users + auth_users_config; memberships
 * are untouched (manage them via the membership editor).
 */
export async function assignUsersToOrganization(
  userIds: string[],
  orgId: string | null,
  orgName: string | null,
) {
  if (userIds.length === 0) return
  const now = new Date().toISOString()
  const { error } = await supabase
    .from('users')
    .update({
      primary_organization_id: orgId,
      organization: orgName,
      updated_at: now,
    })
    .in('id', userIds)
  if (error) throw error
  const { error: authErr } = await supabase
    .from('auth_users_config')
    .update({
      primary_organization_id: orgId,
      updated_at: now,
    })
    .in('id', userIds)
  if (authErr) throw authErr
  if (orgId) await addUsersOrgMembership(userIds, orgId)
}

/** One org membership row (public.user_organization_access, camelCased). */
export interface UserOrgMembership {
  userId: string
  organizationId: string
  accessLevel: string
  includeDescendants: boolean
}

/** All membership rows, for building a userId -> orgIds map on the roster. */
export async function getUserOrgMemberships(): Promise<UserOrgMembership[]> {
  const { data, error } = await supabase
    .from('user_organization_access')
    .select('user_id, organization_id, access_level, include_descendants')
  if (error) throw new Error(`Supabase query error [user_organization_access]: ${error.message}`)
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    userId: String(r.user_id),
    organizationId: String(r.organization_id),
    accessLevel: String(r.access_level ?? 'view'),
    includeDescendants: Boolean(r.include_descendants),
  }))
}

/** Id convention shared with ConfigUserManagement's org-access drawer. */
function orgMembershipId(userId: string, orgId: string): string {
  return `uoa_${userId}_${orgId}_${Date.now().toString(36)}`
}

/**
 * Ensure a membership row exists for each (user, org) pair. Upserts on the
 * (user_id, organization_id) unique constraint so re-adding is a no-op that
 * preserves any access_level/include_descendants set in Config > User
 * Management (`ignoreDuplicates` keeps existing rows untouched).
 */
export async function addUsersOrgMembership(userIds: string[], orgId: string): Promise<void> {
  if (userIds.length === 0) return
  const rows = userIds.map((userId) => ({
    id: orgMembershipId(userId, orgId),
    user_id: userId,
    organization_id: orgId,
    access_level: 'view',
    include_descendants: false,
  }))
  const { error } = await supabase
    .from('user_organization_access')
    .upsert(rows, { onConflict: 'user_id,organization_id', ignoreDuplicates: true })
  if (error) throw new Error(`Failed to add org membership: ${error.message}`)
}

/**
 * Reconcile ONE user's org memberships to exactly `orgIds`: removes rows for
 * orgs not in the list, adds missing ones (access_level 'view'; fine-grained
 * levels + include_descendants stay managed in Config > User Management).
 */
export async function setUserOrgMemberships(userId: string, orgIds: string[]): Promise<void> {
  const { data, error } = await supabase
    .from('user_organization_access')
    .select('organization_id')
    .eq('user_id', userId)
  if (error) throw new Error(`Failed to read org memberships: ${error.message}`)
  const current = new Set(((data ?? []) as Array<{ organization_id: string }>).map((r) => r.organization_id))
  const target = new Set(orgIds)
  const toRemove = [...current].filter((o) => !target.has(o))
  const toAdd = [...target].filter((o) => !current.has(o))
  if (toRemove.length > 0) {
    const { error: delErr } = await supabase
      .from('user_organization_access')
      .delete()
      .eq('user_id', userId)
      .in('organization_id', toRemove)
    if (delErr) throw new Error(`Failed to remove org membership: ${delErr.message}`)
  }
  for (const orgId of toAdd) {
    await addUsersOrgMembership([userId], orgId)
  }
}

// ---------------------------------------------------------------------------
// Resources
// ---------------------------------------------------------------------------
export async function getResources(): Promise<Resource[]> {
  return queryTable('resources')
}

/**
 * Insert (or upsert on id conflict) a Resource row.
 *
 * The app `Resource` type is camelCase; the Postgres `resources` table
 * is snake_case. We map known fields explicitly + spread anything else
 * onto the JSONB `data` column to keep flexibility (matches the pattern
 * used by other write helpers in this file).
 */
export async function createResource(resource: Resource): Promise<Resource> {
  const row = {
    id: resource.id,
    code: resource.code,
    name: resource.name,
    role: resource.role,
    department: resource.department ?? null,
    labor_category: resource.laborCategory ?? null,
    pay_band: resource.payBand ?? null,
    rate_code: resource.rateCode ?? null,
    available_hours: resource.availableHours ?? null,
    assigned_hours: resource.assignedHours ?? null,
    cost_rate: resource.costRate ?? null,
    organization_id: (resource as unknown as { organizationId?: string }).organizationId ?? null,
    organization_name: (resource as unknown as { organizationName?: string }).organizationName ?? null,
    expertise: (resource as unknown as { expertise?: unknown[] }).expertise ?? [],
    certifications: (resource as unknown as { certifications?: unknown[] }).certifications ?? [],
  }
  const { data, error } = await supabase
    .from('resources')
    .upsert(row, { onConflict: 'id' })
    .select('*')
    .single()
  if (error) throw new Error(`createResource failed: ${error.message}`)
  return transformRow<Resource>(data as Record<string, unknown>)
}

export async function getResourceDemand(portfolioId?: string) {
  return queryTable('resource_demand', {
    filter: portfolioId ? { portfolio_id: portfolioId } : undefined,
  })
}

// ---------------------------------------------------------------------------
// Risks
// ---------------------------------------------------------------------------
export async function getRisks(projectId?: string): Promise<Risk[]> {
  return queryTable('risks', {
    filter: projectId ? { project_id: projectId } : undefined,
  })
}

// ---------------------------------------------------------------------------
// Financial
// ---------------------------------------------------------------------------
export async function getBudgetData(projectId?: string) {
  return queryTable('budget_data', {
    filter: projectId ? { project_id: projectId } : undefined,
  })
}

export async function getFinancialActuals(projectId?: string) {
  return queryTable('financial_actuals', {
    filter: projectId ? { project_id: projectId } : undefined,
  })
}

export async function getRevenueRecognition(projectId?: string) {
  return queryTable('revenue_recognition', {
    filter: projectId ? { project_id: projectId } : undefined,
  })
}

// ---------------------------------------------------------------------------
// Issues
// ---------------------------------------------------------------------------
export async function getIssues(projectId?: string) {
  return queryTable('issues', {
    filter: projectId ? { project_id: projectId } : undefined,
  })
}

// ---------------------------------------------------------------------------
// Milestones
// ---------------------------------------------------------------------------
export async function getMilestones(projectId?: string): Promise<Milestone[]> {
  return queryTable<Milestone>('milestones', {
    filter: projectId ? { project_id: projectId } : undefined,
  })
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------
export async function getNotifications() {
  return queryTable('notifications')
}

// ---------------------------------------------------------------------------
// Resource Assignments
// ---------------------------------------------------------------------------
export async function getResourceAssignments(projectId?: string) {
  return queryTable('resource_assignments', {
    filter: projectId ? { project_id: projectId } : undefined,
  })
}

// ---------------------------------------------------------------------------
// Budget - Portfolio level
// ---------------------------------------------------------------------------
export async function getBudgetReleases(portfolioId?: string) {
  return queryJsonbTable('budget_releases', portfolioId ? 'portfolio_id' : undefined, portfolioId)
}

export async function getBudgetCategories() {
  return queryJsonbTable('budget_categories')
}

export async function getBudgetAllocations(portfolioId?: string) {
  return queryTable('budget_allocations', {
    filter: portfolioId ? { from_portfolio_id: portfolioId } : undefined,
  })
}

export async function getBudgetTransfers(portfolioId?: string) {
  return queryJsonbTable('budget_transfers', portfolioId ? 'portfolio_id' : undefined, portfolioId)
}

export async function getBudgetChangeRequests(portfolioId?: string) {
  return queryJsonbTable('budget_change_requests', portfolioId ? 'portfolio_id' : undefined, portfolioId)
}

export async function getBudgetLedger(portfolioId?: string) {
  return queryJsonbTable('budget_ledger', portfolioId ? 'portfolio_id' : undefined, portfolioId)
}

export async function getBudgetFundingSources(portfolioId?: string) {
  return queryJsonbTable('budget_funding_sources', portfolioId ? 'portfolio_id' : undefined, portfolioId)
}

export async function getBudgetFundingRequests(portfolioId?: string) {
  return queryJsonbTable('budget_funding_requests', portfolioId ? 'portfolio_id' : undefined, portfolioId)
}

// ---------------------------------------------------------------------------
// Project Requests
// ---------------------------------------------------------------------------
export async function getProjectRequests(portfolioId?: string) {
  return queryJsonbTable('project_requests', portfolioId ? 'portfolio_id' : undefined, portfolioId)
}

export async function createProjectRequest(request: Record<string, unknown>) {
  const { id, ...rest } = request
  const { error } = await supabase.from('project_requests').insert({
    id: id as string,
    portfolio_id: request.portfolioId as string,
    data: rest,
  })
  if (error) throw new Error(`Supabase insert error [project_requests]: ${error.message}`)
  return request
}

export async function getProjectRequestById(requestId: string) {
  const row = await queryOne<Record<string, unknown>>('project_requests', requestId)
  if (!row) return null
  return mergeJsonbRow(row as Record<string, unknown>)
}

/**
 * Approve / reject / advance a project request. Status lives inside the
 * `data` jsonb, so we read the current data, merge the new status + review
 * stamp, and write `data` back. (#109 — the detail's Approve/Reject buttons
 * were static no-ops; this is the real write path behind them.)
 */
export async function updateProjectRequestStatus(
  requestId: string,
  status: string,
  review?: { reviewedByName?: string; convertedToProjectId?: string },
) {
  const { data: current, error: readErr } = await supabase
    .from('project_requests').select('data').eq('id', requestId).single()
  if (readErr) throw new Error(`Supabase read error [project_requests]: ${readErr.message}`)
  const nextData = {
    ...((current?.data as Record<string, unknown>) ?? {}),
    status,
    reviewedDate: new Date().toISOString(),
    ...(review?.reviewedByName ? { reviewedByName: review.reviewedByName } : {}),
    ...(review?.convertedToProjectId ? { convertedToProjectId: review.convertedToProjectId } : {}),
  }
  const { error } = await supabase
    .from('project_requests').update({ data: nextData, updated_at: new Date().toISOString() }).eq('id', requestId)
  if (error) throw new Error(`Supabase update error [project_requests]: ${error.message}`)
  return { id: requestId, status }
}

// ---------------------------------------------------------------------------
// Strategic Priorities
// ---------------------------------------------------------------------------
export async function getStrategicPriorities(portfolioId?: string) {
  return queryJsonbTable('strategic_priorities', portfolioId ? 'portfolio_id' : undefined, portfolioId)
}

// ---------------------------------------------------------------------------
// CLM Documents (JSONB pattern in clm_documents; contract_id lives inside
// the data JSONB so we filter client-side after the generic JSONB read.)
// Page-level reads still go through clmDataLoader; these helpers exist for
// the create + delete write path that previously only updated in-memory state.
// ---------------------------------------------------------------------------
export async function getClmDocuments(contractId?: string | null) {
  const all = await queryJsonbTable('clm_documents')
  if (contractId === undefined) return all
  return all.filter((row) => (row as { contract_id?: string | null }).contract_id === contractId)
}

export async function createClmDocument(payload: {
  id: string
  contract_id: string | null
  name: string
  file_name: string
  file_type: string
  file_size: number
  category: string
  version: number
  uploaded_by: string
  uploaded_by_name: string
  uploaded_at: string
  description: string
  tags: string[]
}) {
  const { id, ...rest } = payload
  const { data, error } = await supabase
    .from('clm_documents')
    .insert({ id, data: rest, updated_at: new Date().toISOString() })
    .select('*')
    .single()
  if (error) throw new Error(`createClmDocument failed: ${error.message}`)
  return mergeJsonbRow(data as Record<string, unknown>)
}

export async function deleteClmDocument(id: string) {
  const { error } = await supabase.from('clm_documents').delete().eq('id', id)
  if (error) throw new Error(`deleteClmDocument failed: ${error.message}`)
  return { ok: true }
}

// ---------------------------------------------------------------------------
// Lessons Learned (JSONB pattern; project filter is applied client-side
// because the project_id lives inside the data JSONB, not as a column.)
// ---------------------------------------------------------------------------
export async function getLessonsLearned(projectId?: string) {
  const all = await queryJsonbTable('lessons_learned')
  if (!projectId) return all
  return all.filter((row) => (row as { projectId?: string }).projectId === projectId)
}

export async function createLessonLearned(payload: {
  id: string
  projectId: string
  title: string
  category: string
  phase: string
  context: string
  whatWorked: string
  whatDidntWork: string
  recommendation: string
  capturedBy: string
  capturedByName: string
  capturedDate: string
  status: string
  relatedRiskIds: string[]
  tags: string[]
}) {
  const { id, ...rest } = payload
  const { data, error } = await supabase
    .from('lessons_learned')
    .insert({ id, data: rest, updated_at: new Date().toISOString() })
    .select('*')
    .single()
  if (error) throw new Error(`createLessonLearned failed: ${error.message}`)
  return mergeJsonbRow(data as Record<string, unknown>)
}

// ---------------------------------------------------------------------------
// Scenarios
// ---------------------------------------------------------------------------
export async function getScenarios(portfolioId?: string) {
  return queryTable('scenarios', {
    filter: portfolioId ? { portfolio_id: portfolioId } : undefined,
  })
}

export async function getScenarioInitiatives(scenarioId?: string) {
  return queryJsonbTable('scenario_initiatives', scenarioId ? 'scenario_id' : undefined, scenarioId)
}

export async function getScenarioConstraints(portfolioId?: string) {
  return queryJsonbTable('scenario_constraints', portfolioId ? 'portfolio_id' : undefined, portfolioId)
}

export async function getScenarioChanges(scenarioId?: string) {
  return queryJsonbTable('scenario_changes', scenarioId ? 'scenario_id' : undefined, scenarioId)
}

export async function getScenarioTemplates() {
  return queryJsonbTable('scenario_templates')
}

// ---------------------------------------------------------------------------
// Capacity & Demand
// ---------------------------------------------------------------------------
export async function getCapacitySupply(portfolioId?: string) {
  return queryJsonbTable('capacity_supply', portfolioId ? 'portfolio_id' : undefined, portfolioId)
}

export async function getInitiativeDemands(portfolioId?: string) {
  return queryJsonbTable('initiative_demands', portfolioId ? 'portfolio_id' : undefined, portfolioId)
}

// ---------------------------------------------------------------------------
// Portfolio Workflows
// ---------------------------------------------------------------------------
export async function getPortfolioWorkflows(portfolioId?: string) {
  return queryJsonbTable('portfolio_workflows', portfolioId ? 'portfolio_id' : undefined, portfolioId)
}

// ---------------------------------------------------------------------------
// Portfolio Attributes
// ---------------------------------------------------------------------------
export async function getPortfolioAttributes(portfolioId?: string) {
  return queryJsonbTable('portfolio_attributes', portfolioId ? 'portfolio_id' : undefined, portfolioId)
}

// ---------------------------------------------------------------------------
// Portfolio Assignments
// ---------------------------------------------------------------------------
export async function getPortfolioAssignments(portfolioId?: string) {
  let q = supabase.from('portfolio_assignments').select('*')
  if (portfolioId) q = q.eq('portfolio_id', portfolioId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [portfolio_assignments]: ${error.message}`)
  const rows = (data ?? []) as Record<string, unknown>[]
  const people = rows.filter(r => r.type === 'people').map(mergeJsonbRow)
  const organizations = rows.filter(r => r.type === 'organizations').map(mergeJsonbRow)
  return { people, organizations }
}

// ---------------------------------------------------------------------------
// Attribute Rules
// ---------------------------------------------------------------------------
export async function getAttributeValidationRules(portfolioId?: string) {
  return queryJsonbTable('attribute_validation_rules', portfolioId ? 'portfolio_id' : undefined, portfolioId)
}

export async function getAttributeDeterminationRules(portfolioId?: string) {
  return queryJsonbTable('attribute_determination_rules', portfolioId ? 'portfolio_id' : undefined, portfolioId)
}

// ---------------------------------------------------------------------------
// Organizations
// ---------------------------------------------------------------------------
export async function getOrganizations() {
  return queryJsonbTable('organizations')
}

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------
export async function getMasterContracts() {
  return queryTable('master_contracts')
}

export async function getTaskOrders(contractId?: string) {
  return queryTable('task_orders', {
    filter: contractId ? { master_contract_id: contractId } : undefined,
  })
}

export async function getSalesOrders(taskOrderId?: string) {
  return queryTable('sales_orders', {
    filter: taskOrderId ? { task_order_id: taskOrderId } : undefined,
  })
}

export async function getContractLineItems(salesOrderId?: string) {
  return queryTable('contract_line_items', {
    filter: salesOrderId ? { sales_order_id: salesOrderId } : undefined,
  })
}

// ---------------------------------------------------------------------------
// EVM
// ---------------------------------------------------------------------------
export async function getEvmBaselines(projectId?: string) {
  const rows = await queryTable<Record<string, unknown>>('evm_baselines', {
    filter: projectId ? { project_id: projectId } : undefined,
  })
  return rows.map(r => ({ ...r, totalBAC: r.totalBac ?? r.totalBAC ?? 0 }))
}

export async function getEvmTimeSeries(projectId?: string) {
  return queryTable('evm_time_series', {
    filter: projectId ? { project_id: projectId } : undefined,
  })
}

export async function getPeriodicActuals(projectId?: string) {
  return queryTable('evm_periodic_actuals', {
    filter: projectId ? { project_id: projectId } : undefined,
  })
}

// ---------------------------------------------------------------------------
// EVM Phase 2
// ---------------------------------------------------------------------------
export async function getEvtAssignments(projectId?: string) {
  return queryTable('evm_evt_assignments', {
    filter: projectId ? { project_id: projectId } : undefined,
  })
}

export async function upsertEvtAssignment(payload: {
  id: string
  projectId: string
  wbsId: string
  wbsName: string
  nodeType: 'ControlAccount' | 'WorkPackage'
  technique: string
  baseWorkPackageId?: string | null
  baseWorkPackageName?: string | null
  apportionmentRatio?: number | null
  qbdSourceType?: string | null
}) {
  const row: Record<string, unknown> = {
    id: payload.id,
    project_id: payload.projectId,
    wbs_id: payload.wbsId,
    wbs_name: payload.wbsName,
    node_type: payload.nodeType,
    technique: payload.technique,
    base_work_package_id: payload.baseWorkPackageId ?? null,
    base_work_package_name: payload.baseWorkPackageName ?? null,
    apportionment_ratio: payload.apportionmentRatio ?? null,
    qbd_source_type: payload.qbdSourceType ?? null,
    updated_at: new Date().toISOString(),
  }
  const { data, error } = await supabase
    .from('evm_evt_assignments')
    .upsert(row, { onConflict: 'id' })
    .select('*')
    .single()
  if (error) throw new Error(`upsertEvtAssignment failed: ${error.message}`)
  return transformRow(data as Record<string, unknown>)
}

export async function getProgressEntries(projectId?: string, period?: string) {
  return queryTable('evm_progress_entries', {
    filter: {
      ...(projectId ? { project_id: projectId } : {}),
      ...(period ? { period } : {}),
    },
  })
}

export async function upsertProgressEntry(payload: {
  id: string
  projectId: string
  wbsId: string
  wbsName: string
  period: string
  technique: string
  status?: string
  percentComplete?: number | null
  actualCost?: number
  actualHours?: number
  notes?: string | null
  isAutoCalculated?: boolean
  submittedBy?: string | null
}) {
  const row: Record<string, unknown> = {
    id: payload.id,
    project_id: payload.projectId,
    wbs_id: payload.wbsId,
    wbs_name: payload.wbsName,
    period: payload.period,
    technique: payload.technique,
    status: payload.status ?? 'InProgress',
    percent_complete: payload.percentComplete ?? null,
    actual_cost: payload.actualCost ?? 0,
    actual_hours: payload.actualHours ?? 0,
    notes: payload.notes ?? null,
    is_auto_calculated: payload.isAutoCalculated ?? false,
    submitted_by: payload.submittedBy ?? null,
    submitted_date: new Date().toISOString().slice(0, 10),
    updated_at: new Date().toISOString(),
  }
  const { data, error } = await supabase
    .from('evm_progress_entries')
    .upsert(row, { onConflict: 'id' })
    .select('*')
    .single()
  if (error) throw new Error(`upsertProgressEntry failed: ${error.message}`)
  return transformRow(data as Record<string, unknown>)
}

export async function getEvmThresholdConfig(projectId: string) {
  const rows = await queryTable('evm_threshold_configs', {
    filter: { project_id: projectId },
  })
  return rows[0] ?? null
}

export async function upsertEvmThresholdConfig(
  projectId: string,
  config: Record<string, number | null>,
) {
  const row: Record<string, unknown> = {
    project_id: projectId,
    spi_green_min: config.spiGreenMin,
    spi_green_max: config.spiGreenMax,
    spi_yellow_min: config.spiYellowMin,
    spi_yellow_max: config.spiYellowMax,
    cpi_green_min: config.cpiGreenMin,
    cpi_green_max: config.cpiGreenMax,
    cpi_yellow_min: config.cpiYellowMin,
    cpi_yellow_max: config.cpiYellowMax,
    cv_dollar_threshold: config.cvDollarThreshold,
    sv_dollar_threshold: config.svDollarThreshold,
    eac_overrun_threshold_pct: config.eacOverrunThresholdPct,
    tcpi_alert_threshold: config.tcpiAlertThreshold,
    loe_cap_pct: config.loeCapPct,
    consecutive_decline_periods: config.consecutiveDeclinePeriods,
    updated_at: new Date().toISOString(),
  }
  const { data, error } = await supabase
    .from('evm_threshold_configs')
    .upsert(row, { onConflict: 'project_id' })
    .select('*')
    .single()
  if (error) throw new Error(`upsertEvmThresholdConfig failed: ${error.message}`)
  return transformRow(data as Record<string, unknown>)
}

export async function createVarianceNarrative(payload: {
  projectId: string
  wbsId: string
  wbsName: string
  period: string
  triggerType: 'SPI' | 'CPI' | 'CV_Dollar' | 'SV_Dollar' | 'EAC_Overrun' | 'TCPI'
  triggerValue: number
  thresholdValue: number
  rootCause: string
  impactAssessment?: string | null
  correctiveAction: string
  expectedRecoveryDate?: string | null
  responsibleParty: string
  status?: 'Draft' | 'Submitted' | 'Reviewed' | 'Closed'
  createdBy?: string | null
}) {
  const today = new Date().toISOString().slice(0, 10)
  const row: Record<string, unknown> = {
    // id auto-generated by gen_random_uuid()
    project_id: payload.projectId,
    wbs_id: payload.wbsId,
    wbs_name: payload.wbsName,
    period: payload.period,
    trigger_type: payload.triggerType,
    trigger_value: payload.triggerValue,
    threshold_value: payload.thresholdValue,
    root_cause: payload.rootCause,
    impact_assessment: payload.impactAssessment ?? null,
    corrective_action: payload.correctiveAction,
    expected_recovery_date: payload.expectedRecoveryDate ?? null,
    responsible_party: payload.responsibleParty,
    status: payload.status ?? 'Draft',
    created_by: payload.createdBy ?? null,
    created_date: today,
    updated_date: today,
  }
  const { data, error } = await supabase
    .from('evm_variance_narratives')
    .insert(row)
    .select('*')
    .single()
  if (error) throw new Error(`createVarianceNarrative failed: ${error.message}`)
  return transformRow(data as Record<string, unknown>)
}

export async function getVarianceNarratives(projectId?: string) {
  return queryTable('evm_variance_narratives', {
    filter: projectId ? { project_id: projectId } : undefined,
  })
}

export async function getEvmAlerts(projectId?: string, unacknowledgedOnly = false) {
  let q = supabase.from('evm_alerts').select('*')
  if (projectId) q = q.eq('project_id', projectId)
  if (unacknowledgedOnly) q = q.eq('acknowledged', false)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [evm_alerts]: ${error.message}`)
  if (!data) return []
  return transformRows(data as unknown as Record<string, unknown>[])
}

// ---------------------------------------------------------------------------
// Workflows
// ---------------------------------------------------------------------------
export async function getWorkflowDefinitions() {
  return queryJsonbTable('workflow_definitions')
}

export async function getWorkflowInstances() {
  return queryJsonbTable('workflow_instances')
}

export async function getWorkflowAuditLog() {
  return queryJsonbTable('workflow_audit_log')
}

export async function getWorkflowArtifacts() {
  const { data, error } = await supabase.from('workflow_artifacts').select('*')
  if (error) throw new Error(`Supabase query error [workflow_artifacts]: ${error.message}`)
  const rows = (data ?? []) as Record<string, unknown>[]
  const artifacts = rows.filter(r => r.type === 'artifact').map(mergeJsonbRow)
  const requirements = rows.filter(r => r.type === 'requirement').map(mergeJsonbRow)
  return { artifacts, requirements }
}

export async function getWorkflowSlaConfig() {
  return queryJsonbTable('workflow_sla_config')
}

// ---------------------------------------------------------------------------
// Workflows v3
// ---------------------------------------------------------------------------
import type { Workflow, PolicySet, WorkflowRun, WorkflowPlan, WorkflowEvent, WorkflowPlanStep, PlanSource } from '@/types/workflows-v3'
import type { WorkflowDraft, WorkflowSpec, WorkflowChatHistory, WorkflowDraftStatus, WorkflowContextRef } from '@/types/workflow-spec'
import { emptySpec } from '@/types/workflow-spec'
import type {
  FormDraft, FormSpec, FormChatHistory, FormDraftStatus, PublishedForm, FormSubmission, FormAttachmentRef, FormDeployStatus,
} from '@/types/form-spec'
import { emptyFormSpec } from '@/types/form-spec'
import type { PeopleGroup } from '@/types/people-group'
import { generateId } from '@/lib/utils'
import { normalizeMatrix, validateMatrix, type RoutingMatrix } from '@/services/workflows/routingMatrix'
import {
  normalizeOptionListItems, validateOptionListItems, listKeyError,
  type FormOptionList, type FormOptionListItem,
} from '@/services/forms/optionLists'

function rowToWorkflow(row: Record<string, unknown>): Workflow {
  const data = (row.data ?? {}) as Record<string, unknown>
  return {
    id: row.id as string,
    name: row.name as string,
    type: row.type as string,
    version: row.version as string,
    mode: row.mode as Workflow['mode'],
    goal: (row.goal as string) ?? null,
    scope: (row.scope as Workflow['scope']) ?? 'global',
    portfolioIds: (row.portfolio_ids as string[]) ?? [],
    triggerEvent: (row.trigger_event as string) ?? null,
    triggerCondition: (row.trigger_condition as string) ?? null,
    active: row.active !== false,
    aiGenerated: row.ai_generated === true,
    data: {
      gates: (data.gates ?? {}) as Workflow['data']['gates'],
      gateOrder: (data.gateOrder ?? data.gate_order ?? []) as string[],
      allowedActions: (data.allowedActions ?? data.allowed_actions) as string[] | undefined,
      examples: data.examples as Workflow['data']['examples'],
      revisions: data.revisions as Workflow['data']['revisions'],
      // The step spec as last published; new runs execute this (ensureLiveSpec).
      spec: (data.spec && typeof data.spec === 'object' ? data.spec : undefined) as Workflow['data']['spec'],
    },
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  }
}

function rowToPolicySet(row: Record<string, unknown>): PolicySet {
  const data = (row.data ?? {}) as Record<string, unknown>
  return {
    id: row.id as string,
    portfolioId: row.portfolio_id as string,
    name: row.name as string,
    version: (row.version as number) ?? 1,
    effectiveFrom: row.effective_from as string,
    effectiveTo: (row.effective_to as string) ?? null,
    data: {
      delegationAuthority: (data.delegationAuthority ?? data.delegation_authority ?? []) as PolicySet['data']['delegationAuthority'],
      thresholdLadder: (data.thresholdLadder ?? data.threshold_ladder ?? []) as PolicySet['data']['thresholdLadder'],
      requiredArtifactsByType: (data.requiredArtifactsByType ?? data.required_artifacts_by_type ?? {}) as Record<string, string[]>,
      governanceGates: (data.governanceGates ?? data.governance_gates ?? []) as string[],
      slaDefaults: (data.slaDefaults ?? data.sla_defaults ?? {}) as Record<string, number>,
      escalationRules: (data.escalationRules ?? data.escalation_rules ?? []) as PolicySet['data']['escalationRules'],
    },
  }
}

function rowToRun(row: Record<string, unknown>): WorkflowRun {
  return {
    id: row.id as string,
    workflowId: row.workflow_id as string,
    portfolioId: row.portfolio_id as string,
    projectId: (row.project_id as string) ?? null,
    contextType: (row.context_type as WorkflowRun['contextType']) ?? null,
    contextId: (row.context_id as string) ?? null,
    triggerEntity: (row.trigger_entity as string) ?? null,
    status: row.status as WorkflowRun['status'],
    currentNode: (row.current_node as string) ?? null,
    dueDate: (row.due_date as string) ?? null,
    priority: (row.priority as string) ?? null,
    policySetId: (row.policy_set_id as string) ?? null,
    context: (row.context ?? {}) as Record<string, unknown>,
    createdBy: (row.created_by as string) ?? null,
    createdAt: row.created_at as string,
    completedAt: (row.completed_at as string) ?? null,
  }
}

function rowToPlan(row: Record<string, unknown>): WorkflowPlan {
  return {
    id: row.id as string,
    runId: row.run_id as string,
    version: (row.version as number) ?? 1,
    source: row.source as WorkflowPlan['source'],
    plannerModel: (row.planner_model as string) ?? null,
    rationale: (row.rationale as string) ?? null,
    status: row.status as WorkflowPlan['status'],
    steps: (row.steps ?? []) as WorkflowPlanStep[],
    approvedBy: (row.approved_by as string) ?? null,
    approvedAt: (row.approved_at as string) ?? null,
    createdAt: row.created_at as string,
    updatedAt: (row.updated_at as string) ?? undefined,
  }
}

function rowToEvent(row: Record<string, unknown>): WorkflowEvent {
  return {
    id: row.id as string,
    runId: row.run_id as string,
    planStepId: (row.plan_step_id as string) ?? null,
    ts: row.ts as string,
    actor: (row.actor as string) ?? null,
    kind: row.kind as string,
    payload: (row.payload ?? {}) as Record<string, unknown>,
  }
}

export async function getWorkflowsV3(): Promise<Workflow[]> {
  const { data, error } = await supabase.from('workflows_v3').select('*').order('name')
  if (error) throw new Error(`Supabase query error [workflows_v3]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToWorkflow)
}

export async function getPolicySets(portfolioId?: string): Promise<PolicySet[]> {
  let q = supabase.from('policy_sets').select('*')
  if (portfolioId) q = q.eq('portfolio_id', portfolioId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [policy_sets]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToPolicySet)
}

export async function getWorkflowRuns(opts?: { portfolioId?: string; workflowId?: string; status?: string; contextType?: string; contextId?: string }): Promise<WorkflowRun[]> {
  let q = supabase.from('workflow_runs').select('*').order('created_at', { ascending: false })
  if (opts?.portfolioId) q = q.eq('portfolio_id', opts.portfolioId)
  if (opts?.workflowId) q = q.eq('workflow_id', opts.workflowId)
  if (opts?.status) q = q.eq('status', opts.status)
  if (opts?.contextType) q = q.eq('context_type', opts.contextType)
  if (opts?.contextId) q = q.eq('context_id', opts.contextId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [workflow_runs]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToRun)
}

export async function getWorkflowPlans(runId?: string): Promise<WorkflowPlan[]> {
  let q = supabase.from('workflow_plans').select('*').order('version', { ascending: false })
  if (runId) q = q.eq('run_id', runId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [workflow_plans]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToPlan)
}

export async function getWorkflowEvents(runId?: string): Promise<WorkflowEvent[]> {
  let q = supabase.from('workflow_events').select('*').order('ts', { ascending: false })
  if (runId) q = q.eq('run_id', runId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [workflow_events]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToEvent)
}

export async function createWorkflowPlan(input: {
  runId: string
  source: PlanSource
  plannerModel?: string | null
  rationale: string
  steps: WorkflowPlanStep[]
}): Promise<WorkflowPlan> {
  const id = `plan_${generateId()}`
  // Find existing version count
  const existing = await getWorkflowPlans(input.runId)
  const version = existing.length + 1
  const row = {
    id,
    run_id: input.runId,
    version,
    source: input.source,
    planner_model: input.plannerModel ?? null,
    rationale: input.rationale,
    status: 'draft',
    steps: input.steps,
  }
  const { data, error } = await supabase.from('workflow_plans').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [workflow_plans]: ${error.message}`)
  // Append plan.drafted event
  await supabase.from('workflow_events').insert({
    id: `evt_${generateId()}`,
    run_id: input.runId,
    plan_step_id: id,
    actor: input.source === 'ai_planner' ? 'ai' : input.source,
    kind: 'plan.drafted',
    payload: { planId: id, stepCount: input.steps.length },
  })
  return rowToPlan(data as Record<string, unknown>)
}

export async function approveWorkflowPlan(planId: string, approver: string): Promise<void> {
  const { data: planRow, error: planErr } = await supabase
    .from('workflow_plans').update({
      status: 'approved',
      approved_by: approver,
      approved_at: new Date().toISOString(),
    }).eq('id', planId).select().single()
  if (planErr) throw new Error(`Supabase update error [workflow_plans]: ${planErr.message}`)
  const runId = (planRow as Record<string, unknown>).run_id as string
  await supabase.from('workflow_events').insert({
    id: `evt_${generateId()}`,
    run_id: runId,
    plan_step_id: planId,
    actor: approver,
    kind: 'plan.approved',
    payload: { planId },
  })
  // SOP sweep for event-scope rules listening on workflow.stage.approved.
  void evaluateOnEvent({
    trigger: 'on_transition',
    entityType: 'workflow_plans',
    entityId: planId,
    payload: planRow as Record<string, unknown>,
    eventTarget: 'workflow.stage.approved',
    actor: approver,
  })
}

// ---------------------------------------------------------------------------
// Workflow Builder drafts (the Studio spec layer, migration 00352)
// ---------------------------------------------------------------------------
function rowToDraft(row: Record<string, unknown>): WorkflowDraft {
  const rawSpec = (row.spec ?? {}) as Record<string, unknown>
  const spec = (rawSpec && typeof rawSpec === 'object' && Array.isArray(rawSpec.steps))
    ? (rawSpec as unknown as WorkflowSpec)
    : emptySpec((row.name as string) ?? 'Untitled workflow')
  const chat = (row.chat_history ?? { messages: [] }) as WorkflowChatHistory
  return {
    id: row.id as string,
    name: (row.name as string) ?? 'Untitled workflow',
    status: (row.status as WorkflowDraftStatus) ?? 'draft',
    portfolioId: (row.portfolio_id as string) ?? null,
    ownerId: (row.owner_id as string) ?? null,
    aiGenerated: Boolean(row.ai_generated),
    spec,
    chatHistory: chat?.messages ? chat : { messages: [] },
    promotedWorkflowId: (row.promoted_workflow_id as string) ?? null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  }
}

export async function getWorkflowDrafts(opts?: { portfolioId?: string; status?: string }): Promise<WorkflowDraft[]> {
  let q = supabase.from('workflow_drafts').select('*').order('updated_at', { ascending: false })
  if (opts?.portfolioId) q = q.eq('portfolio_id', opts.portfolioId)
  if (opts?.status) q = q.eq('status', opts.status)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [workflow_drafts]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToDraft)
}

export async function getWorkflowDraft(id: string): Promise<WorkflowDraft | null> {
  const { data, error } = await supabase.from('workflow_drafts').select('*').eq('id', id).maybeSingle()
  if (error) throw new Error(`Supabase query error [workflow_drafts]: ${error.message}`)
  return data ? rowToDraft(data as Record<string, unknown>) : null
}

export async function createWorkflowDraft(input: {
  name?: string
  portfolioId?: string | null
  ownerId?: string | null
  spec?: WorkflowSpec
  aiGenerated?: boolean
}): Promise<WorkflowDraft> {
  const id = `wfd_${generateId()}`
  const spec = input.spec ?? emptySpec(input.name ?? 'Untitled workflow')
  const row = {
    id,
    name: input.name ?? spec.name ?? 'Untitled workflow',
    status: 'draft',
    portfolio_id: input.portfolioId ?? null,
    owner_id: input.ownerId ?? null,
    ai_generated: input.aiGenerated ?? false,
    spec,
    chat_history: { messages: [] },
  }
  const { data, error } = await supabase.from('workflow_drafts').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [workflow_drafts]: ${error.message}`)
  return rowToDraft(data as Record<string, unknown>)
}

export async function updateWorkflowDraft(id: string, patch: {
  name?: string
  status?: WorkflowDraftStatus
  spec?: WorkflowSpec
  chatHistory?: WorkflowChatHistory
  promotedWorkflowId?: string | null
}): Promise<WorkflowDraft> {
  const row: Record<string, unknown> = {}
  if (patch.name !== undefined) row.name = patch.name
  if (patch.status !== undefined) row.status = patch.status
  if (patch.spec !== undefined) row.spec = patch.spec
  if (patch.chatHistory !== undefined) row.chat_history = patch.chatHistory
  if (patch.promotedWorkflowId !== undefined) row.promoted_workflow_id = patch.promotedWorkflowId
  const { data, error } = await supabase.from('workflow_drafts').update(row).eq('id', id).select().single()
  if (error) throw new Error(`Supabase update error [workflow_drafts]: ${error.message}`)
  return rowToDraft(data as Record<string, unknown>)
}

export async function deleteWorkflowDraft(id: string): Promise<void> {
  const { error } = await supabase.from('workflow_drafts').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete error [workflow_drafts]: ${error.message}`)
}

// ---------------------------------------------------------------------------
// Forms Management (Forms Builder drafts, published forms, submissions,
// migrations 00384-00387). Mirrors the workflow_drafts layer above.
// ---------------------------------------------------------------------------
function rowToFormDraft(row: Record<string, unknown>): FormDraft {
  const rawSpec = (row.spec ?? {}) as Record<string, unknown>
  const spec = (rawSpec && typeof rawSpec === 'object' && Array.isArray(rawSpec.fields))
    ? (rawSpec as unknown as FormSpec)
    : emptyFormSpec((row.name as string) ?? 'Untitled form')
  const chat = (row.chat_history ?? { messages: [] }) as FormChatHistory
  return {
    id: row.id as string,
    name: (row.name as string) ?? 'Untitled form',
    status: (row.status as FormDraftStatus) ?? 'draft',
    portfolioId: (row.portfolio_id as string) ?? null,
    ownerId: (row.owner_id as string) ?? null,
    aiGenerated: Boolean(row.ai_generated),
    spec,
    chatHistory: chat?.messages ? chat : { messages: [] },
    publishedFormId: (row.published_form_id as string) ?? null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  }
}

function rowToForm(row: Record<string, unknown>): PublishedForm {
  const rawSpec = (row.spec ?? {}) as Record<string, unknown>
  const spec = (rawSpec && typeof rawSpec === 'object' && Array.isArray(rawSpec.fields))
    ? (rawSpec as unknown as FormSpec)
    : emptyFormSpec((row.name as string) ?? 'Untitled form')
  return {
    id: row.id as string,
    draftId: (row.draft_id as string) ?? null,
    name: (row.name as string) ?? 'Untitled form',
    version: Number(row.version ?? 1),
    status: (row.status as string) === 'archived' ? 'archived' : 'published',
    portfolioId: (row.portfolio_id as string) ?? null,
    ownerId: (row.owner_id as string) ?? null,
    spec,
    contextBinding: (row.context_binding as PublishedForm['contextBinding']) ?? null,
    publicSlug: (row.public_slug as string) ?? null,
    isPublic: Boolean(row.is_public),
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  }
}

function rowToSubmission(row: Record<string, unknown>): FormSubmission {
  return {
    id: row.id as string,
    formId: row.form_id as string,
    formVersion: Number(row.form_version ?? 1),
    values: (row.values ?? {}) as Record<string, unknown>,
    attachments: (Array.isArray(row.attachments) ? row.attachments : []) as FormAttachmentRef[],
    context: {
      type: (row.context_type as WorkflowContextRef['type']) ?? 'global',
      id: (row.context_id as string) ?? null,
      label: (row.context_label as string) ?? null,
    },
    status: (row.status as string) === 'draft' ? 'draft' : 'submitted',
    deploy: {
      status: ((row.deploy_status as FormDeployStatus) ?? 'none') || 'none',
      result: row.deploy_result ?? undefined,
      payload: (row.deploy_payload ?? null) as Record<string, unknown> | null,
      at: (row.deployed_at as string) ?? null,
      by: (row.deployed_by as string) ?? null,
    },
    requestStatus: typeof row.request_status === 'string' && row.request_status
      ? { key: row.request_status, at: (row.request_status_at as string) ?? null, by: (row.request_status_by as string) ?? null }
      : null,
    requestNumber: typeof row.request_number === 'string' && row.request_number ? row.request_number : null,
    historical: row.is_historical === true && typeof row.legacy_reference === 'string'
      ? {
          legacyReference: row.legacy_reference,
          sourceSystem: String((row.historical_meta as Record<string, unknown> | null)?.sourceSystem ?? 'another system'),
          batchId: (row.import_batch_id as string) ?? null,
          meta: (row.historical_meta as Record<string, unknown> | null) ?? {},
        }
      : null,
    createdBy: (row.created_by as string) ?? null,
    createdAt: row.created_at as string,
    cui: row.cui === true,
    cuiSource: (row.cui_source as FormSubmission['cuiSource']) ?? null,
  }
}

// --- Form drafts ---
export async function getFormDrafts(opts?: { portfolioId?: string; status?: string }): Promise<FormDraft[]> {
  let q = supabase.from('form_drafts').select('*').order('updated_at', { ascending: false })
  if (opts?.portfolioId) q = q.eq('portfolio_id', opts.portfolioId)
  if (opts?.status) q = q.eq('status', opts.status)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [form_drafts]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToFormDraft)
}

export async function getFormDraft(id: string): Promise<FormDraft | null> {
  const { data, error } = await supabase.from('form_drafts').select('*').eq('id', id).maybeSingle()
  if (error) throw new Error(`Supabase query error [form_drafts]: ${error.message}`)
  return data ? rowToFormDraft(data as Record<string, unknown>) : null
}

export async function createFormDraft(input: {
  name?: string
  portfolioId?: string | null
  ownerId?: string | null
  spec?: FormSpec
  aiGenerated?: boolean
}): Promise<FormDraft> {
  const id = `fmd_${generateId()}`
  const spec = input.spec ?? emptyFormSpec(input.name ?? 'Untitled form')
  const row = {
    id,
    name: input.name ?? spec.name ?? 'Untitled form',
    status: 'draft',
    portfolio_id: input.portfolioId ?? null,
    owner_id: input.ownerId ?? null,
    ai_generated: input.aiGenerated ?? false,
    spec,
    chat_history: { messages: [] },
  }
  const { data, error } = await supabase.from('form_drafts').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [form_drafts]: ${error.message}`)
  return rowToFormDraft(data as Record<string, unknown>)
}

export async function updateFormDraft(id: string, patch: {
  name?: string
  status?: FormDraftStatus
  spec?: FormSpec
  chatHistory?: FormChatHistory
  publishedFormId?: string | null
}): Promise<FormDraft> {
  const row: Record<string, unknown> = {}
  if (patch.name !== undefined) row.name = patch.name
  if (patch.status !== undefined) row.status = patch.status
  if (patch.spec !== undefined) row.spec = patch.spec
  if (patch.chatHistory !== undefined) row.chat_history = patch.chatHistory
  if (patch.publishedFormId !== undefined) row.published_form_id = patch.publishedFormId
  const { data, error } = await supabase.from('form_drafts').update(row).eq('id', id).select().single()
  if (error) throw new Error(`Supabase update error [form_drafts]: ${error.message}`)
  return rowToFormDraft(data as Record<string, unknown>)
}

export async function deleteFormDraft(id: string): Promise<void> {
  const { error } = await supabase.from('form_drafts').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete error [form_drafts]: ${error.message}`)
}

// --- Published forms ---
/** All published forms, reduced to the latest version per source draft. */
export async function getForms(opts?: { portfolioId?: string; allVersions?: boolean }): Promise<PublishedForm[]> {
  let q = supabase.from('forms').select('*').eq('status', 'published').order('version', { ascending: false })
  if (opts?.portfolioId) q = q.eq('portfolio_id', opts.portfolioId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [forms]: ${error.message}`)
  const all = ((data ?? []) as Record<string, unknown>[]).map(rowToForm)
  if (opts?.allVersions) return all
  const latest = new Map<string, PublishedForm>()
  const families = new Map<string, PublishedForm[]>()
  for (const f of all) {
    const key = f.draftId ?? f.id
    if (!latest.has(key)) latest.set(key, f)
    const family = families.get(key)
    if (family) family.push(f)
    else families.set(key, [f])
  }
  // A public link made before links were carried forward at publish can still
  // sit on an older version. Show it on the latest one, where it is managed.
  return [...latest.entries()]
    .map(([key, f]) => withFamilyPublicLink(f, families.get(key) ?? []))
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
}

export async function getForm(id: string): Promise<PublishedForm | null> {
  const { data, error } = await supabase.from('forms').select('*').eq('id', id).maybeSingle()
  if (error) throw new Error(`Supabase query error [forms]: ${error.message}`)
  return data ? rowToForm(data as Record<string, unknown>) : null
}

/** Every version row published from one draft (a form's version family). */
async function getFormFamilyRows(draftId: string): Promise<PublishedForm[]> {
  const { data, error } = await supabase.from('forms').select('*').eq('draft_id', draftId)
  if (error) throw new Error(`Supabase query error [forms]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToForm)
}

/**
 * The latest live version of the form `id` belongs to (services/forms/
 * formFamily.ts): the highest published version from the same draft. A form
 * with no draft, or with no newer version, is returned as it is. A workflow
 * stores the id of the version that was picked; this is how that id is read
 * after a republish.
 */
export async function getLatestFormVersion(id: string): Promise<PublishedForm | null> {
  const form = await getForm(id)
  if (!form || !form.draftId) return form
  // A failed family read falls back to the version that was asked for.
  const family = await getFormFamilyRows(form.draftId).catch(() => [] as PublishedForm[])
  return latestLiveVersions([form, ...family])[0] ?? form
}

/**
 * Load a publicly-shared form by its slug (anon-readable via permissive RLS).
 * The slug is carried to each new version at publish, but a link made before
 * that can still sit on an older version row: it then resolves to the latest
 * live version of the same form, so the link never serves a stale version.
 */
export async function getFormBySlug(slug: string): Promise<PublishedForm | null> {
  const { data, error } = await supabase.from('forms').select('*').eq('public_slug', slug).eq('is_public', true).maybeSingle()
  if (error) throw new Error(`Supabase query error [forms]: ${error.message}`)
  if (!data) return null
  const carrier = rowToForm(data as Record<string, unknown>)
  if (!carrier.draftId) return carrier
  const family = await getFormFamilyRows(carrier.draftId).catch(() => [] as PublishedForm[])
  const latest = latestLiveVersions(family)[0]
  if (!latest || latest.id === carrier.id) return carrier
  return { ...latest, publicSlug: carrier.publicSlug, isPublic: true }
}

/** Write the two public-link columns of one forms row. */
async function writeFormPublicLink(w: PublicLinkWrite): Promise<void> {
  const { error } = await supabase.from('forms').update({ public_slug: w.publicSlug, is_public: w.isPublic }).eq('id', w.formId)
  if (error) throw new Error(`Supabase update error [forms]: ${error.message}`)
}

/**
 * Move a family's public link onto another version, in the planned order (the
 * slug is unique, so the old row is cleared first). When the second write
 * fails the link is put back where it was. A failure throws
 * FormPublicLinkError, carrying `target` so the caller still knows what is live.
 */
async function applyPublicLinkPlan(plan: PublicLinkPlan, target: PublishedForm): Promise<PublishedForm> {
  const [clear, assign] = plan.steps
  const why = (e: unknown) => (e instanceof Error ? e.message : String(e)).replace(/[.\s]+$/, '')
  try {
    await writeFormPublicLink(clear)
  } catch (e) {
    // Nothing moved: the older version still holds the link, and the public
    // page resolves it to the latest version.
    throw new FormPublicLinkError(
      `Version ${target.version} is live, but its public link could not be moved to it (${why(e)}). The link still opens the latest version. To repair it, turn the public link off and on again on the Forms page.`,
      target, false,
    )
  }
  try {
    await writeFormPublicLink(assign)
  } catch (e) {
    let restored = true
    for (const w of plan.rollback) {
      try { await writeFormPublicLink(w) } catch { restored = false }
    }
    throw new FormPublicLinkError(
      restored
        ? `Version ${target.version} is live, but its public link could not be moved to it (${why(e)}). The link was put back on the earlier version and still opens the latest one. To repair it, turn the public link off and on again on the Forms page.`
        : `Version ${target.version} is live, but its public link (/f/${plan.slug}) could not be moved to it and could not be restored (${why(e)}). Check the Forms page: if the form no longer shows a public link, create a new one and send it to the people who use it.`,
      target, !restored,
    )
  }
  return { ...target, publicSlug: plan.slug, isPublic: plan.isPublic }
}

/**
 * Toggle a form's public share. Turning it on reuses the family's existing
 * slug when an older version still holds one (so the address people already
 * have keeps working), and mints a slug only when the form never had one.
 * Turning it off switches off EVERY version of the form, so a link left on an
 * older version cannot stay open behind the one shown on the Forms page.
 */
export async function setFormPublicShare(formId: string, isPublic: boolean): Promise<PublishedForm> {
  const form = await getForm(formId)
  if (!form) throw new Error(`Form not found: ${formId}`)
  if (isPublic && !form.publicSlug && form.draftId) {
    const plan = planPublicLinkCarryForward(await getFormFamilyRows(form.draftId), form.id, { forcePublic: true })
    if (plan) return applyPublicLinkPlan(plan, form)
  }
  const row: Record<string, unknown> = { is_public: isPublic }
  if (isPublic && !form.publicSlug) {
    const base = (form.name || 'form').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'form'
    row.public_slug = `${base}-${generateId().slice(0, 6)}`
  }
  const { data, error } = await supabase.from('forms').update(row).eq('id', formId).select().single()
  if (error) throw new Error(`Supabase update error [forms]: ${error.message}`)
  if (!isPublic && form.draftId) {
    const off = await supabase.from('forms').update({ is_public: false }).eq('draft_id', form.draftId).neq('id', formId).eq('is_public', true)
    if (off.error) throw new Error(`Supabase update error [forms]: ${off.error.message}`)
  }
  return rowToForm(data as Record<string, unknown>)
}

/**
 * Publish a draft: snapshot its spec into a NEW immutable forms row (so older
 * submissions keep their exact spec), incrementing version, then point the draft
 * at it and mark it published.
 */
export async function publishFormDraft(draftId: string): Promise<PublishedForm> {
  const draft = await getFormDraft(draftId)
  if (!draft) throw new Error(`Form draft not found: ${draftId}`)
  // Every earlier version of this form: the next version number, and the
  // public link (if any) that has to move to the new version.
  const existing = await supabase.from('forms').select('*').eq('draft_id', draftId)
  const family = ((existing.data ?? []) as Record<string, unknown>[]).map(rowToForm)
  const maxVersion = family.reduce((m, r) => Math.max(m, r.version || 0), 0)
  const id = `frm_${generateId()}`
  const row = {
    id,
    draft_id: draftId,
    name: draft.spec.name || draft.name,
    version: maxVersion + 1,
    status: 'published',
    portfolio_id: draft.portfolioId,
    owner_id: draft.ownerId,
    spec: draft.spec,
    context_binding: draft.spec.contextBinding ?? 'global',
  }
  const { data, error } = await supabase.from('forms').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [forms]: ${error.message}`)
  await updateFormDraft(draftId, { status: 'published', publishedFormId: id })
  const published = rowToForm(data as Record<string, unknown>)
  // Carry the public link forward so /f/<slug> serves this version and the
  // Forms page (which lists the latest version) can still turn it off. Runs
  // last: a failure here leaves the new version published and throws
  // FormPublicLinkError (with the published form) so the caller can say so.
  const plan = planPublicLinkCarryForward(family, id)
  return plan ? applyPublicLinkPlan(plan, published) : published
}

// --- Submissions ---
export async function createFormSubmission(input: {
  formId: string
  formVersion: number
  values: Record<string, unknown>
  attachments?: FormAttachmentRef[]
  context: WorkflowContextRef
  portfolioId?: string | null
  status?: 'submitted' | 'draft'
  createdBy?: string | null
  /** A caller-chosen id (the public form page names its request up front, migration 00535). */
  id?: string
}): Promise<FormSubmission> {
  const id = input.id ?? `fsub_${generateId()}`
  const row = {
    id,
    form_id: input.formId,
    form_version: input.formVersion,
    portfolio_id: input.portfolioId ?? null,
    context_type: input.context.type,
    context_id: input.context.id,
    context_label: input.context.label,
    values: input.values,
    attachments: input.attachments ?? [],
    status: input.status ?? 'submitted',
    created_by: input.createdBy ?? null,
  }
  const { data, error } = await supabase.from('form_submissions').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [form_submissions]: ${error.message}`)
  return rowToSubmission(data as Record<string, unknown>)
}

/**
 * Patch a submission's SAP deploy lifecycle (post-approval MDG execution) and/or
 * its captured `values` (the requester revising a submission that is still in
 * their queue, or field-level edits allowed while it awaits others' approval).
 */
export async function updateFormSubmission(id: string, patch: {
  values?: Record<string, unknown>
  deployStatus?: FormDeployStatus
  deployResult?: unknown
  deployPayload?: Record<string, unknown> | null
  deployedAt?: string | null
  deployedBy?: string | null
}): Promise<FormSubmission> {
  const row: Record<string, unknown> = {}
  if (patch.values !== undefined) row.values = patch.values
  if (patch.deployStatus !== undefined) row.deploy_status = patch.deployStatus
  if (patch.deployResult !== undefined) row.deploy_result = patch.deployResult
  if (patch.deployPayload !== undefined) row.deploy_payload = patch.deployPayload
  if (patch.deployedAt !== undefined) row.deployed_at = patch.deployedAt
  if (patch.deployedBy !== undefined) row.deployed_by = patch.deployedBy
  const { data, error } = await supabase.from('form_submissions').update(row).eq('id', id).select().single()
  if (error) throw new Error(`Supabase update error [form_submissions]: ${error.message}`)
  return rowToSubmission(data as Record<string, unknown>)
}

export async function getFormSubmissions(opts: { formId?: string; formIds?: string[]; contextType?: string; contextId?: string; createdBy?: string }): Promise<FormSubmission[]> {
  let q = supabase.from('form_submissions').select('*').order('created_at', { ascending: false })
  // formIds reads a whole form VERSION FAMILY at once (a republish creates a
  // new forms row and submissions stay pinned to the version they were made
  // under). It wins over formId when both are passed.
  if (opts.formIds && opts.formIds.length > 0) q = q.in('form_id', opts.formIds)
  else if (opts.formId) q = q.eq('form_id', opts.formId)
  if (opts.contextType) q = q.eq('context_type', opts.contextType)
  if (opts.contextId) q = q.eq('context_id', opts.contextId)
  if (opts.createdBy) q = q.eq('created_by', opts.createdBy)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [form_submissions]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToSubmission)
}

// --- Storage (form-attachments bucket, migration 00387) ---
import { signedUrl as signFormAttachmentUrl } from '@/lib/objectStorage'
import type { SubmissionRevision, SubmissionRevisionInput } from '@/services/forms/submissionRevisions'
import {
  FormPublicLinkError, latestLiveVersions, planPublicLinkCarryForward, withFamilyPublicLink,
  type PublicLinkPlan, type PublicLinkWrite,
} from '@/services/forms/formFamily'

export async function uploadFormAttachment(args: {
  formId: string
  submissionId: string
  fieldKey: string
  file: File
}): Promise<FormAttachmentRef> {
  const safeName = args.file.name.replace(/[^a-zA-Z0-9._-]+/g, '_')
  const path = `${args.formId}/${args.submissionId}/${args.fieldKey}/${generateId()}-${safeName}`
  try {
    await uploadObject('form-attachments', path, args.file)
  } catch (err) {
    throw new Error(`Storage error [form-attachments]: ${err instanceof Error ? err.message : String(err)}`)
  }
  return { fieldKey: args.fieldKey, path, filename: args.file.name, mime: args.file.type || null, sizeBytes: args.file.size }
}

/**
 * A short-lived link for opening a stored form attachment. The bucket is
 * private, so every open signs a fresh URL (Supabase Storage, or the Azure
 * storage broker: objectStorage.signedUrl picks). Throws a readable message
 * when the link cannot be made.
 */
export async function getFormAttachmentUrl(ref: Pick<FormAttachmentRef, 'path' | 'filename'>, expiresInSeconds = 300): Promise<string> {
  if (!ref.path) throw new Error(`"${ref.filename}" has no stored file to open.`)
  try {
    return await signFormAttachmentUrl('form-attachments', ref.path, expiresInSeconds)
  } catch (err) {
    throw new Error(`"${ref.filename}" could not be opened: ${err instanceof Error ? err.message : String(err)}. Try again, or ask an administrator to check file storage.`)
  }
}

// --- Submission revision history (form_submission_revisions, migration 00509) ---
// The table may not exist yet on every deployment. These two functions throw
// on error like the rest of this file; services/forms/submissionRevisions.ts is
// the caller and degrades (empty read, best-effort write).
function rowToSubmissionRevision(row: Record<string, unknown>): SubmissionRevision {
  const bag = (v: unknown) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {}) as Record<string, unknown>
  return {
    id: row.id as string,
    submissionId: row.submission_id as string,
    editedAt: (row.edited_at as string) ?? '',
    editedBy: (row.edited_by as string) ?? null,
    reason: (row.reason as string) ?? null,
    runState: (row.run_state as string) ?? null,
    changedKeys: Array.isArray(row.changed_keys) ? (row.changed_keys as unknown[]).map(String) : [],
    before: bag(row.before),
    after: bag(row.after),
  }
}

export async function insertFormSubmissionRevision(input: SubmissionRevisionInput): Promise<void> {
  const { error } = await supabase.from('form_submission_revisions').insert({
    id: `frev_${generateId()}`,
    submission_id: input.submissionId,
    edited_by: input.editedBy ?? null,
    reason: input.reason ?? null,
    run_state: input.runState ?? null,
    changed_keys: input.changedKeys,
    before: input.before,
    after: input.after,
  })
  if (error) throw new Error(`Supabase insert error [form_submission_revisions]: ${error.message}`)
}

/** Revisions of one submission (or several at once), newest first. */
export async function getFormSubmissionRevisions(submissionId: string | string[]): Promise<SubmissionRevision[]> {
  let q = supabase.from('form_submission_revisions').select('*').order('edited_at', { ascending: false })
  if (Array.isArray(submissionId)) {
    if (submissionId.length === 0) return []
    q = q.in('submission_id', submissionId)
  } else q = q.eq('submission_id', submissionId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [form_submission_revisions]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToSubmissionRevision)
}

// ---------------------------------------------------------------------------
// People Groups (migration 00402) — named lists of people usable as workflow
// assignees / parallel approvers / notification recipients.
// ---------------------------------------------------------------------------
function rowToPeopleGroup(row: Record<string, unknown>): PeopleGroup {
  return {
    id: row.id as string,
    name: (row.name as string) ?? 'Untitled group',
    description: (row.description as string) ?? null,
    members: Array.isArray(row.members) ? (row.members as unknown[]).map((m) => String(m)) : [],
    portfolioId: (row.portfolio_id as string) ?? null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  }
}

export async function getPeopleGroups(opts?: { portfolioId?: string }): Promise<PeopleGroup[]> {
  let q = supabase.from('people_groups').select('*').order('name', { ascending: true })
  if (opts?.portfolioId) q = q.eq('portfolio_id', opts.portfolioId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [people_groups]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToPeopleGroup)
}

export async function createPeopleGroup(input: { name: string; description?: string | null; members?: string[]; portfolioId?: string | null }): Promise<PeopleGroup> {
  const row = {
    id: `pg_${generateId()}`,
    name: input.name || 'Untitled group',
    description: input.description ?? null,
    members: input.members ?? [],
    portfolio_id: input.portfolioId ?? null,
  }
  const { data, error } = await supabase.from('people_groups').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [people_groups]: ${error.message}`)
  return rowToPeopleGroup(data as Record<string, unknown>)
}

export async function updatePeopleGroup(id: string, patch: { name?: string; description?: string | null; members?: string[] }): Promise<PeopleGroup> {
  const row: Record<string, unknown> = {}
  if (patch.name !== undefined) row.name = patch.name
  if (patch.description !== undefined) row.description = patch.description
  if (patch.members !== undefined) row.members = patch.members
  const { data, error } = await supabase.from('people_groups').update(row).eq('id', id).select().single()
  if (error) throw new Error(`Supabase update error [people_groups]: ${error.message}`)
  return rowToPeopleGroup(data as Record<string, unknown>)
}

export async function deletePeopleGroup(id: string): Promise<void> {
  const { error } = await supabase.from('people_groups').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete error [people_groups]: ${error.message}`)
}

// ---------------------------------------------------------------------------
// Shared select lists (migration 00522, form_option_lists): named value sets a
// select field reads by optionListId. Values are retired, never deleted, so
// there is no delete here. Item rules live in services/forms/optionLists.ts.
// ---------------------------------------------------------------------------
function rowToOptionList(row: Record<string, unknown>): FormOptionList {
  return {
    id: row.id as string,
    key: (row.key as string) ?? '',
    name: (row.name as string) ?? 'Untitled list',
    description: (row.description as string) ?? null,
    items: normalizeOptionListItems(row.items),
    parentListId: typeof row.parent_list_id === 'string' && row.parent_list_id ? row.parent_list_id : null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
    createdBy: (row.created_by as string) ?? null,
    updatedBy: (row.updated_by as string) ?? null,
  }
}

/** Reject items that would fail the table's trigger, with a readable message. */
function assertOptionListItems(items: FormOptionListItem[]): FormOptionListItem[] {
  const clean = normalizeOptionListItems(items)
  if (clean.length !== items.length) throw new Error('Every value in the list needs a value.')
  const errors = validateOptionListItems(clean)
  if (errors.length) throw new Error(errors.join(' '))
  return clean
}

export async function getOptionLists(): Promise<FormOptionList[]> {
  const { data, error } = await supabase.from('form_option_lists').select('*').order('name', { ascending: true })
  if (error) throw new Error(`Supabase query error [form_option_lists]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToOptionList)
}

export async function getOptionList(id: string): Promise<FormOptionList | null> {
  const { data, error } = await supabase.from('form_option_lists').select('*').eq('id', id).maybeSingle()
  if (error) throw new Error(`Supabase query error [form_option_lists]: ${error.message}`)
  return data ? rowToOptionList(data as Record<string, unknown>) : null
}

export async function createOptionList(input: {
  key: string
  name: string
  description?: string | null
  items?: FormOptionListItem[]
  parentListId?: string | null
  actor?: string | null
}): Promise<FormOptionList> {
  const keyError = listKeyError(input.key)
  if (keyError) throw new Error(keyError)
  if (!input.name.trim()) throw new Error('Give the list a name.')
  const row = {
    id: `fol_${generateId()}`,
    key: input.key,
    name: input.name.trim(),
    description: input.description ?? null,
    items: assertOptionListItems(input.items ?? []),
    // Only sent when set, so a database without migration 00532 still creates plain lists.
    ...(input.parentListId ? { parent_list_id: input.parentListId } : {}),
    created_by: input.actor ?? null,
    updated_by: input.actor ?? null,
  }
  const { data, error } = await supabase.from('form_option_lists').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [form_option_lists]: ${error.message}`)
  return rowToOptionList(data as Record<string, unknown>)
}

export async function updateOptionList(id: string, patch: {
  key?: string
  name?: string
  description?: string | null
  items?: FormOptionListItem[]
  parentListId?: string | null
  actor?: string | null
}): Promise<FormOptionList> {
  const row: Record<string, unknown> = {}
  if (patch.parentListId !== undefined) {
    if (patch.parentListId === id) throw new Error('A list cannot be its own parent.')
    row.parent_list_id = patch.parentListId
  }
  if (patch.key !== undefined) {
    const keyError = listKeyError(patch.key)
    if (keyError) throw new Error(keyError)
    row.key = patch.key
  }
  if (patch.name !== undefined) {
    if (!patch.name.trim()) throw new Error('Give the list a name.')
    row.name = patch.name.trim()
  }
  if (patch.description !== undefined) row.description = patch.description
  if (patch.items !== undefined) row.items = assertOptionListItems(patch.items)
  row.updated_by = patch.actor ?? null
  const { data, error } = await supabase.from('form_option_lists').update(row).eq('id', id).select().single()
  if (error) throw new Error(`Supabase update error [form_option_lists]: ${error.message}`)
  return rowToOptionList(data as Record<string, unknown>)
}

/** Retire (active=false) or restore (active=true) values of a list by value. */
export async function setOptionListItemsActive(id: string, values: string[], active: boolean, actor?: string | null): Promise<FormOptionList> {
  const list = await getOptionList(id)
  if (!list) throw new Error(`Select list not found: ${id}`)
  const pick = new Set(values)
  const items = list.items.map((it) => (pick.has(it.value) ? { ...it, active } : it))
  return updateOptionList(id, { items, actor })
}

// ---------------------------------------------------------------------------
// Routing matrices (migration 00533, wff_routing_matrices): departments, rules
// and role + site assignments that Matrix review and Department review steps
// resolve when they open. Retired (active=false), never deleted. Shape rules
// live in services/workflows/routingMatrix.ts (validateMatrix).
// ---------------------------------------------------------------------------
function rowToRoutingMatrix(row: Record<string, unknown>): RoutingMatrix {
  return normalizeMatrix({
    id: row.id as string,
    key: row.key as string,
    name: row.name as string,
    description: (row.description as string) ?? null,
    active: row.active !== false,
    siteField: (row.site_field as string) ?? 'site_id',
    departments: row.departments as RoutingMatrix['departments'],
    rules: row.rules as RoutingMatrix['rules'],
    assignments: row.assignments as RoutingMatrix['assignments'],
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
    updatedBy: (row.updated_by as string) ?? null,
  })
}

function routingMatrixRow(m: RoutingMatrix): Record<string, unknown> {
  const errors = validateMatrix(m)
  if (errors.length) throw new Error(errors.join(' '))
  return {
    key: m.key,
    name: m.name.trim(),
    description: m.description ?? null,
    active: m.active,
    site_field: m.siteField || 'site_id',
    departments: m.departments,
    rules: m.rules,
    assignments: m.assignments,
  }
}

export async function getRoutingMatrices(): Promise<RoutingMatrix[]> {
  const { data, error } = await supabase.from('wff_routing_matrices').select('*').order('name', { ascending: true })
  if (error) throw new Error(`Supabase query error [wff_routing_matrices]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToRoutingMatrix)
}

export async function getRoutingMatrix(id: string): Promise<RoutingMatrix | null> {
  const { data, error } = await supabase.from('wff_routing_matrices').select('*').eq('id', id).maybeSingle()
  if (error) throw new Error(`Supabase query error [wff_routing_matrices]: ${error.message}`)
  return data ? rowToRoutingMatrix(data as Record<string, unknown>) : null
}

export async function createRoutingMatrix(m: RoutingMatrix, actor?: string | null): Promise<RoutingMatrix> {
  const row = { id: `rm_${generateId()}`, ...routingMatrixRow(m), created_by: actor ?? null, updated_by: actor ?? null }
  const { data, error } = await supabase.from('wff_routing_matrices').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [wff_routing_matrices]: ${error.message}`)
  return rowToRoutingMatrix(data as Record<string, unknown>)
}

export async function updateRoutingMatrix(id: string, m: RoutingMatrix, actor?: string | null): Promise<RoutingMatrix> {
  const row = { ...routingMatrixRow(m), updated_by: actor ?? null }
  const { data, error } = await supabase.from('wff_routing_matrices').update(row).eq('id', id).select().single()
  if (error) throw new Error(`Supabase update error [wff_routing_matrices]: ${error.message}`)
  return rowToRoutingMatrix(data as Record<string, unknown>)
}

// ---------------------------------------------------------------------------
// Workflow Builder execution support (create/update on the v3 runtime tables)
// ---------------------------------------------------------------------------
export async function createWorkflowV3(input: {
  name: string
  type: string
  mode: Workflow['mode']
  goal?: string | null
  scope?: Workflow['scope']
  portfolioIds?: string[]
  triggerEvent?: string | null
  triggerCondition?: string | null
  aiGenerated?: boolean
  data: Workflow['data']
}): Promise<Workflow> {
  const id = `wf_${generateId()}`
  const row = {
    id,
    name: input.name,
    type: input.type,
    version: '1.0',
    mode: input.mode,
    goal: input.goal ?? null,
    scope: input.scope ?? 'portfolio',
    portfolio_ids: input.portfolioIds ?? [],
    trigger_event: input.triggerEvent ?? null,
    trigger_condition: input.triggerCondition ?? null,
    active: true,
    ai_generated: input.aiGenerated ?? false,
    data: input.data,
  }
  const { data, error } = await supabase.from('workflows_v3').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [workflows_v3]: ${error.message}`)
  return rowToWorkflow(data as Record<string, unknown>)
}

/** Re-promote path: update an existing workflows_v3 row IN PLACE (same id, so
 *  forms, runs, and links keep pointing at it) with a bumped version label. */
export async function updateWorkflowV3(id: string, patch: {
  name?: string
  type?: string
  mode?: Workflow['mode']
  goal?: string | null
  triggerEvent?: string | null
  triggerCondition?: string | null
  version?: string
  active?: boolean
  data?: Workflow['data']
}): Promise<Workflow> {
  const row: Record<string, unknown> = {}
  if (patch.name !== undefined) row.name = patch.name
  if (patch.type !== undefined) row.type = patch.type
  if (patch.mode !== undefined) row.mode = patch.mode
  if (patch.goal !== undefined) row.goal = patch.goal
  if (patch.triggerEvent !== undefined) row.trigger_event = patch.triggerEvent
  if (patch.triggerCondition !== undefined) row.trigger_condition = patch.triggerCondition
  if (patch.version !== undefined) row.version = patch.version
  if (patch.active !== undefined) row.active = patch.active
  if (patch.data !== undefined) row.data = patch.data
  const { data, error } = await supabase.from('workflows_v3').update(row).eq('id', id).select().single()
  if (error) throw new Error(`Supabase update error [workflows_v3]: ${error.message}`)
  return rowToWorkflow(data as Record<string, unknown>)
}

export async function createWorkflowRun(input: {
  workflowId: string
  portfolioId: string
  projectId?: string | null
  contextType?: string | null
  contextId?: string | null
  triggerEntity?: string | null
  priority?: string | null
  policySetId?: string | null
  context?: Record<string, unknown>
  createdBy?: string | null
}): Promise<WorkflowRun> {
  const id = `run_${generateId()}`
  const row = {
    id,
    workflow_id: input.workflowId,
    portfolio_id: input.portfolioId,
    project_id: input.projectId ?? null,
    context_type: input.contextType ?? null,
    context_id: input.contextId ?? null,
    trigger_entity: input.triggerEntity ?? null,
    status: 'queued',
    priority: input.priority ?? 'Medium',
    policy_set_id: input.policySetId ?? null,
    context: input.context ?? {},
    created_by: input.createdBy ?? null,
  }
  const { data, error } = await supabase.from('workflow_runs').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [workflow_runs]: ${error.message}`)
  await supabase.from('workflow_events').insert({
    id: `evt_${generateId()}`, run_id: id, actor: input.createdBy ?? 'system',
    kind: 'run.created', payload: { workflowId: input.workflowId },
  })
  return rowToRun(data as Record<string, unknown>)
}

export async function updateWorkflowRun(id: string, patch: {
  status?: WorkflowRun['status']
  currentNode?: string | null
  completedAt?: string | null
  /** When the open step is due (mirrors the active step's SLA; null clears it). */
  dueDate?: string | null
  /** The run dimensions bag (form values land under context.form). */
  context?: Record<string, unknown>
}): Promise<void> {
  const row: Record<string, unknown> = {}
  if (patch.status !== undefined) row.status = patch.status
  if (patch.dueDate !== undefined) row.due_date = patch.dueDate
  if (patch.currentNode !== undefined) row.current_node = patch.currentNode
  if (patch.completedAt !== undefined) row.completed_at = patch.completedAt
  if (patch.context !== undefined) row.context = patch.context
  const { error } = await supabase.from('workflow_runs').update(row).eq('id', id)
  if (error) throw new Error(`Supabase update error [workflow_runs]: ${error.message}`)
}

/** Thrown when a plan save loses to a newer save of the same plan. */
export class WorkflowPlanConflictError extends Error {
  constructor() {
    super('This request was just updated by someone else. Close it and open it again to see the latest before acting.')
    this.name = 'WorkflowPlanConflictError'
  }
}

/**
 * Save a plan. With `expectedUpdatedAt` (the updatedAt the caller read) the
 * write only lands if nobody saved the plan in between; otherwise it throws
 * WorkflowPlanConflictError instead of overwriting their decision. Returns the
 * row's new updatedAt so the caller can chain further saves.
 */
export async function updateWorkflowPlan(id: string, patch: {
  status?: WorkflowPlan['status']
  steps?: WorkflowPlanStep[]
}, expectedUpdatedAt?: string): Promise<string | undefined> {
  const row: Record<string, unknown> = {}
  if (patch.status !== undefined) row.status = patch.status
  if (patch.steps !== undefined) row.steps = patch.steps
  let q = supabase.from('workflow_plans').update(row).eq('id', id)
  if (expectedUpdatedAt) q = q.eq('updated_at', expectedUpdatedAt)
  const { data, error } = await q.select('updated_at')
  if (error) throw new Error(`Supabase update error [workflow_plans]: ${error.message}`)
  const saved = (data ?? []) as Array<{ updated_at?: string }>
  if (expectedUpdatedAt && saved.length === 0) throw new WorkflowPlanConflictError()
  return saved[0]?.updated_at ?? undefined
}

export async function appendWorkflowEvent(input: {
  runId: string
  planStepId?: string | null
  actor?: string | null
  kind: string
  payload?: Record<string, unknown>
}): Promise<WorkflowEvent> {
  const id = `evt_${generateId()}`
  const row = {
    id,
    run_id: input.runId,
    plan_step_id: input.planStepId ?? null,
    actor: input.actor ?? 'system',
    kind: input.kind,
    payload: input.payload ?? {},
  }
  const { data, error } = await supabase.from('workflow_events').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [workflow_events]: ${error.message}`)
  return rowToEvent(data as Record<string, unknown>)
}

// ---------------------------------------------------------------------------
// Baseline Management
// ---------------------------------------------------------------------------
export async function getBaselineModels(projectId?: string): Promise<BaselineModel[]> {
  return queryTable('baseline_models', {
    filter: projectId ? { project_id: projectId } : undefined,
  })
}

export async function getBaselineWbsSummaries(baselineModelId?: string): Promise<BaselineWbsSummary[]> {
  return queryTable<BaselineWbsSummary>('baseline_wbs_summaries', {
    filter: baselineModelId ? { baseline_model_id: baselineModelId } : undefined,
    transform: (row) => ({
      ...transformKeys(row),
      costByCategory: Array.isArray(row.cost_by_category) ? row.cost_by_category : [],
    } as unknown as BaselineWbsSummary),
  })
}

export async function getBaselineTimePhaseEntries(baselineModelId?: string): Promise<BaselineTimePhaseEntry[]> {
  // Postgres numeric columns return as strings via supabase-js (precision preservation).
  // Coerce to numbers so charts that sum these values get arithmetic, not string concat.
  return queryTable<BaselineTimePhaseEntry>('baseline_time_phase_entries', {
    filter: baselineModelId ? { baseline_model_id: baselineModelId } : undefined,
    transform: (row) => {
      const r = transformKeys(row) as Record<string, unknown>
      return {
        ...r,
        baseAmount: Number(r.baseAmount ?? 0),
        burdenAmount: Number(r.burdenAmount ?? 0),
        totalAmount: Number(r.totalAmount ?? 0),
        hours: r.hours == null ? null : Number(r.hours),
      } as unknown as BaselineTimePhaseEntry
    },
  })
}

export async function getBurdenRateDecks(projectId?: string): Promise<BurdenRateDeck[]> {
  return queryTable('burden_rate_decks', {
    filter: projectId ? { project_id: projectId } : undefined,
  })
}

export async function getResourceCategories(): Promise<ResourceCategory[]> {
  return queryTable('resource_categories')
}

export async function upsertResourceCategory(cat: ResourceCategory): Promise<void> {
  // App layer is camelCase; DB columns are snake_case. Convert before upsert.
  const row = {
    id: cat.id,
    code: cat.code,
    name: cat.name,
    type: cat.type,
    description: cat.description ?? null,
    display_order: cat.displayOrder ?? 0,
    color: cat.color ?? null,
    active: cat.active ?? true,
    updated_at: new Date().toISOString(),
  }
  const { error } = await supabase.from('resource_categories').upsert(row)
  if (error) throw new Error(`upsert resource_categories failed: ${error.message}`)
}

export async function deleteResourceCategory(id: string): Promise<void> {
  const { error } = await supabase.from('resource_categories').delete().eq('id', id)
  if (error) throw new Error(`delete resource_categories failed: ${error.message}`)
}

// ---------------------------------------------------------------------------
// ROI Links
// ---------------------------------------------------------------------------
export interface ProjectRoiLink {
  id: string
  internalProjectId: string
  externalProjectId: string
  benefitValue: number
  benefitType: 'CostSavings' | 'RevenueEnablement' | 'RiskReduction' | 'TimeToMarket' | 'CapabilityUnlock'
  benefitDescription: string | null
  realizationPeriod: string | null
  confidence: 'Low' | 'Medium' | 'High'
  status: 'Projected' | 'Realized' | 'AtRisk' | 'Cancelled'
  notes: string | null
  // ProjectTasks #89 Phase 1 - win-probability uplift this investment
  // provides on the linked external opportunity. Range [0,1]. Null =
  // unestimated; the app math treats null as 1.0 for back-compat.
  pWinDelta?: number | null
}

export async function getProjectRoiLinks(): Promise<ProjectRoiLink[]> {
  return queryTable<ProjectRoiLink>('project_roi_links')
}

/**
 * ProjectTasks #89 Phase 2 part B - patch a single ROI link's
 * pWinDelta (the only field editable from the inline UI today). The
 * field is nullable in the schema; passing null lets the user clear
 * a previously-set probability back to "unestimated".
 */
/**
 * ProjectTasks #94c - patch a single revenue_plan_entries row's
 * plannedAmount. Used by the editable Revenue Planning view matrix
 * on the Baseline Management page. Variance is recomputed in
 * Postgres / app layer from planned - actual when read.
 */
export async function updateRevenuePlanAmount(entryId: string, plannedAmount: number): Promise<void> {
  const { error } = await supabase
    .from('revenue_plan_entries')
    .update({ planned_amount: plannedAmount })
    .eq('id', entryId)
  if (error) throw new Error(`updateRevenuePlanAmount failed: ${error.message}`)
}

/**
 * ProjectTasks #94b first slice - patch a single baseline_time_phase_entries
 * row's totalAmount. Used by the editable Time-Phased View matrix on
 * the Baseline Management page. Recomputes the row's base/burden
 * split proportionally if a previous split existed; otherwise
 * stores totalAmount with baseAmount = totalAmount, burdenAmount = 0
 * (matches the SEED-DATA-GAP fallback the page already tolerates).
 */
/**
 * Burden share this baseline actually uses, as a 0..1 fraction, derived from
 * the entries that DO carry a split. Returns 0 when the baseline has no split
 * anywhere, which preserves the old base-only behaviour for models that
 * genuinely do not track burden separately.
 */
async function getPrevailingBurdenRatio(baselineModelId: string | undefined): Promise<number> {
  if (!baselineModelId) return 0
  const { data, error } = await supabase
    .from('baseline_time_phase_entries')
    .select('base_amount, burden_amount')
    .eq('baseline_model_id', baselineModelId)
    .gt('burden_amount', 0)
    .limit(500)
  if (error || !data || data.length === 0) return 0
  let base = 0
  let burden = 0
  for (const r of data as Record<string, unknown>[]) {
    base += Number(r.base_amount ?? 0)
    burden += Number(r.burden_amount ?? 0)
  }
  const total = base + burden
  if (total <= 0) return 0
  const ratio = burden / total
  // Guard against a nonsense stored split producing a ratio outside [0,1).
  return ratio > 0 && ratio < 1 ? ratio : 0
}

/**
 * Refuse any write against a LOCKED baseline model.
 *
 * A locked baseline is the performance measurement baseline: every BCWS, PV,
 * SV and VAC downstream is measured against it. The EVM series lives in a
 * different table, so an edit here moves the yardstick with nothing flagging
 * a variance. The UI disables the affordance, but the UI is not the control -
 * a stale tab, a replayed request or a direct call must fail too.
 * Rebaselining goes through a change request, never a grid cell.
 */
async function assertBaselineUnlocked(baselineModelId: string): Promise<void> {
  const { data, error } = await supabase
    .from('baseline_models')
    .select('locked_by, name')
    .eq('id', baselineModelId)
    .single()
  // Fail closed on a read error: if we cannot prove the baseline is unlocked,
  // we do not write to it.
  if (error) throw new Error(`Baseline lock check failed: ${error.message}`)
  const row = data as Record<string, unknown> | null
  if (row?.locked_by) {
    throw new Error(
      `Baseline "${String(row.name ?? baselineModelId)}" is locked and cannot be edited. ` +
      `Raise a baseline change request to rebaseline.`,
    )
  }
}

/**
 * Write edited WBS budget rows for one baseline (WBS Cost Breakdown "Edit
 * budget"). Rows carrying a stored id are updated in place; rows for elements
 * with no stored row yet are inserted with a deterministic id so a second save
 * updates rather than duplicates. Refuses a locked baseline, like every other
 * baseline write.
 */
export async function saveBaselineWbsSummaries(
  baselineModelId: string,
  rows: Array<BaselineWbsSummary & { id?: string }>,
): Promise<void> {
  await assertBaselineUnlocked(baselineModelId)
  const now = new Date().toISOString()
  const payload = rows.map((r) => ({
    id: r.id ?? `bws_${baselineModelId}_${r.wbsElementId}`,
    baseline_model_id: baselineModelId,
    wbs_element_id: r.wbsElementId,
    wbs_element_code: r.wbsElementCode,
    wbs_element_name: r.wbsElementName,
    wbs_type: r.wbsType,
    parent_wbs_element_id: r.parentWbsElementId,
    level: r.level,
    burden_rate_deck_override_id: r.burdenRateDeckOverrideId ?? null,
    burden_rate_deck_override_name: r.burdenRateDeckOverrideName ?? null,
    total_base_cost: r.totalBaseCost,
    total_burden_cost: r.totalBurdenCost,
    total_cost: r.totalCost,
    total_base_revenue: r.totalBaseRevenue,
    total_burden_revenue: r.totalBurdenRevenue,
    total_revenue: r.totalRevenue,
    cost_by_category: r.costByCategory,
    updated_at: now,
  }))
  for (let i = 0; i < payload.length; i += 500) {
    const { error } = await supabase.from('baseline_wbs_summaries').upsert(payload.slice(i, i + 500))
    if (error) throw new Error(`Saving WBS budget failed: ${error.message}`)
  }
}

export async function updateBaselineTimePhaseTotal(entryId: string, totalAmount: number): Promise<BaselineTimePhaseEntry> {
  // Read the existing row so we can preserve the base/burden ratio
  // when one is set. If neither is set we put the whole new total
  // into baseAmount (and zero out burden) so the row totals stay
  // self-consistent.
  const { data: existing, error: readErr } = await supabase
    .from('baseline_time_phase_entries')
    .select('base_amount, burden_amount, baseline_model_id')
    .eq('id', entryId)
    .single()
  if (readErr) throw new Error(`updateBaselineTimePhaseTotal read failed: ${readErr.message}`)
  const parentModelId = (existing as Record<string, unknown>)?.baseline_model_id
  if (typeof parentModelId === 'string' && parentModelId) {
    await assertBaselineUnlocked(parentModelId)
  }
  const exBase = Number((existing as Record<string, unknown>)?.base_amount ?? 0)
  const exBurden = Number((existing as Record<string, unknown>)?.burden_amount ?? 0)
  const exTotal = exBase + exBurden
  let nextBase: number, nextBurden: number
  // `!== 0`, not `> 0`. A credit line carries a NEGATIVE total with a perfectly
  // good base/burden split; the old `> 0` test sent it down the fallback branch
  // and destroyed that split along with its sign relationship.
  if (exTotal !== 0) {
    const ratio = totalAmount / exTotal
    nextBase = exBase * ratio
    nextBurden = exBurden * ratio
  } else {
    // No split to preserve (a seeded 0/0 placeholder period). Putting the whole
    // amount in base and zeroing burden understated the baseline's burdened
    // total by the burden on every such cell - on a model carrying 24% burden
    // everywhere else, a $100,000 entry quietly lost $24,000. Fall back to the
    // ratio this baseline actually uses, derived from its other entries.
    const prevailing = await getPrevailingBurdenRatio(
      (existing as Record<string, unknown>)?.baseline_model_id as string | undefined,
    )
    nextBase = totalAmount * (1 - prevailing)
    nextBurden = totalAmount * prevailing
  }
  const { data, error } = await supabase
    .from('baseline_time_phase_entries')
    .update({
      total_amount: totalAmount,
      base_amount: nextBase,
      burden_amount: nextBurden,
    })
    .eq('id', entryId)
    .select('*')
    .single()
  if (error) throw new Error(`updateBaselineTimePhaseTotal write failed: ${error.message}`)
  return transformRow(data as Record<string, unknown>) as BaselineTimePhaseEntry
}

/**
 * ProjectTasks #94b architectural rework - INSERT a fresh time-phase entry
 * for a (baseline × WBS × resource category × period) that has no entry
 * yet. Used by the inline "+ add" affordance on empty-WBS rows in the
 * Time-Phased View matrix so users can populate baseline data inline
 * instead of waiting for a seed migration.
 *
 * Mirrors the updateBaselineTimePhaseTotal split convention: writes
 * the whole totalAmount into baseAmount and zeroes burdenAmount. The
 * Cost & Revenue chart renders correctly either way because the
 * "hasCostBreakdown" gate falls back to a single solid bar when no
 * row carries a base/burden split.
 */
export async function createBaselineTimePhaseEntry(args: {
  id: string
  baselineModelId: string
  wbsElementId: string
  wbsElementCode: string
  wbsElementName: string
  resourceCategoryId: string
  resourceCategoryName: string
  costRevenueType: 'Cost' | 'Revenue'
  period: string
  totalAmount: number
}): Promise<BaselineTimePhaseEntry> {
  await assertBaselineUnlocked(args.baselineModelId)
  const { data, error } = await supabase
    .from('baseline_time_phase_entries')
    .insert({
      id: args.id,
      baseline_model_id: args.baselineModelId,
      wbs_element_id: args.wbsElementId,
      wbs_element_code: args.wbsElementCode,
      wbs_element_name: args.wbsElementName,
      resource_category_id: args.resourceCategoryId,
      resource_category_name: args.resourceCategoryName,
      cost_revenue_type: args.costRevenueType,
      period: args.period,
      base_amount: args.totalAmount,
      burden_amount: 0,
      total_amount: args.totalAmount,
    })
    .select('*')
    .single()
  if (error) throw new Error(`createBaselineTimePhaseEntry failed: ${error.message}`)
  return transformRow(data as Record<string, unknown>) as BaselineTimePhaseEntry
}

export async function updateRoiLinkPWinDelta(linkId: string, pWinDelta: number | null): Promise<ProjectRoiLink> {
  const { data, error } = await supabase
    .from('project_roi_links')
    .update({ p_win_delta: pWinDelta })
    .eq('id', linkId)
    .select('*')
    .single()
  if (error) throw new Error(`updateRoiLinkPWinDelta failed: ${error.message}`)
  return transformRow(data as Record<string, unknown>) as ProjectRoiLink
}

export async function getScheduleVersions(projectId?: string): Promise<ScheduleVersionSummary[]> {
  return queryTable('schedule_versions', {
    filter: projectId ? { project_id: projectId } : undefined,
  })
}

export async function getBaselineChangeRequests(projectId?: string, baselineModelId?: string): Promise<BaselineChangeRequest[]> {
  const filter: Record<string, string | undefined> = {}
  if (projectId) filter.project_id = projectId
  if (baselineModelId) filter.baseline_model_id = baselineModelId
  const bcrs = await queryTable<BaselineChangeRequest>('baseline_change_requests', { filter })
  if (bcrs.length === 0) return bcrs
  // Attach affected elements (the aligned control accounts + before/after BAC).
  // They live in a child table that the flat queryTable above doesn't join.
  const { data: aeRows } = await supabase
    .from('bcr_affected_elements')
    .select('*')
    .in('bcr_id', bcrs.map(b => b.id))
  const byBcr = new Map<string, BcrAffectedElement[]>()
  for (const r of ((aeRows ?? []) as Record<string, unknown>[])) {
    const ae = transformRow<BcrAffectedElement & { bcrId: string }>(r)
    const list = byBcr.get(ae.bcrId) ?? []
    list.push(ae)
    byBcr.set(ae.bcrId, list)
  }
  return bcrs.map(b => ({ ...b, affectedElements: byBcr.get(b.id) ?? [] }))
}

// BCR statuses that count as "in flight" - a control account in any of these is
// locked and cannot be aligned to another BCR until this one finishes.
const BCR_ACTIVE_STATUSES: ReadonlySet<string> = new Set([
  'Draft', 'Submitted', 'Under Review', 'PendingApproval', 'Approved', 'ApprovedIncorporated',
])

/** Map a control account id -> the active BCR (id+code) currently locking it. */
export async function getControlAccountBcrLocks(projectId: string): Promise<Map<string, { bcrId: string; bcrCode: string; status: string }>> {
  const bcrs = await getBaselineChangeRequests(projectId)
  const locks = new Map<string, { bcrId: string; bcrCode: string; status: string }>()
  for (const b of bcrs) {
    if (!BCR_ACTIVE_STATUSES.has(b.status)) continue
    for (const ae of (b.affectedElements ?? [])) {
      locks.set(ae.wbsElementId, { bcrId: b.id, bcrCode: b.code, status: b.status })
    }
  }
  return locks
}

function bcrMainRow(b: BaselineChangeRequest): Record<string, unknown> {
  return {
    id: b.id, code: b.code, project_id: b.projectId, baseline_model_id: b.baselineModelId,
    baseline_model_name: b.baselineModelName, title: b.title, description: b.description,
    justification: b.justification, status: b.status, change_type: b.changeType,
    change_type_label: b.changeTypeLabel, approval_authority: b.approvalAuthority,
    cost_impact: b.costImpact, revenue_impact: b.revenueImpact, mr_impact: b.mrImpact,
    ub_impact: b.ubImpact, cbb_impact: b.cbbImpact, revenue_reserve: b.revenueReserve ?? null,
    linked_sap_claim_number: b.linkedSapClaimNumber ?? null,
    is_retroactive: b.isRetroactive, contract_mod_number: b.contractModNumber,
    internal_directive_ref: b.internalDirectiveRef, effective_date: b.effectiveDate ?? null,
    schedule_version_id: b.scheduleVersionId, schedule_version_name: b.scheduleVersionName,
    requested_by: b.requestedBy, requested_by_name: b.requestedByName, requested_date: b.requestedDate,
    reviewed_by: b.reviewedBy, reviewed_by_name: b.reviewedByName, reviewed_date: b.reviewedDate,
    approved_by: b.approvedBy, approved_by_name: b.approvedByName, approved_date: b.approvedDate,
    implemented_date: b.implementedDate, review_comments: b.reviewComments ?? null,
  }
}

async function replaceBcrAffectedElements(bcrId: string, elements: BcrAffectedElement[]): Promise<void> {
  await supabase.from('bcr_affected_elements').delete().eq('bcr_id', bcrId)
  if (elements.length === 0) return
  const rows = elements.map((ae, i) => ({
    id: `bae_${bcrId}_${i}`, bcr_id: bcrId,
    wbs_element_id: ae.wbsElementId, wbs_element_code: ae.wbsElementCode,
    wbs_element_name: ae.wbsElementName, wbs_type: ae.wbsType,
    bac_before: ae.bacBefore, bac_after: ae.bacAfter,
    budget_delta: ae.budgetDelta, hours_delta: ae.hoursDelta ?? null,
    new_start_date: ae.newStartDate ?? null, new_finish_date: ae.newFinishDate ?? null,
  }))
  const { error } = await supabase.from('bcr_affected_elements').insert(rows)
  if (error) throw new Error(`insert bcr_affected_elements failed: ${error.message}`)
}

/** Insert a new BCR (main row + its aligned control accounts). */
export async function createBaselineChangeRequest(bcr: BaselineChangeRequest): Promise<void> {
  const { error } = await supabase.from('baseline_change_requests').insert(bcrMainRow(bcr))
  if (error) throw new Error(`createBaselineChangeRequest failed: ${error.message}`)
  await replaceBcrAffectedElements(bcr.id, bcr.affectedElements ?? [])
}

/** Patch a BCR's main columns (status transitions, pushed impacts, etc.). When
 *  `affectedElements` is provided, the child rows are fully replaced. */
export async function updateBaselineChangeRequest(
  id: string,
  patch: Partial<BaselineChangeRequest>,
): Promise<void> {
  const snake: Record<string, unknown> = {}
  const map: Record<string, string> = {
    status: 'status', costImpact: 'cost_impact', revenueImpact: 'revenue_impact',
    mrImpact: 'mr_impact', ubImpact: 'ub_impact', cbbImpact: 'cbb_impact',
    revenueReserve: 'revenue_reserve', reviewComments: 'review_comments',
    linkedSapClaimNumber: 'linked_sap_claim_number',
    reviewedBy: 'reviewed_by', reviewedByName: 'reviewed_by_name', reviewedDate: 'reviewed_date',
    approvedBy: 'approved_by', approvedByName: 'approved_by_name', approvedDate: 'approved_date',
    implementedDate: 'implemented_date',
    // Editable descriptive / configuration fields (BCR edit dialog)
    title: 'title', description: 'description', justification: 'justification',
    changeType: 'change_type', changeTypeLabel: 'change_type_label', approvalAuthority: 'approval_authority',
    isRetroactive: 'is_retroactive', contractModNumber: 'contract_mod_number',
    internalDirectiveRef: 'internal_directive_ref', effectiveDate: 'effective_date',
  }
  for (const [k, col] of Object.entries(map)) {
    if (k in patch) snake[col] = (patch as Record<string, unknown>)[k]
  }
  if (Object.keys(snake).length > 0) {
    const { error } = await supabase.from('baseline_change_requests').update(snake).eq('id', id)
    if (error) throw new Error(`updateBaselineChangeRequest failed: ${error.message}`)
  }
  if (patch.affectedElements) await replaceBcrAffectedElements(id, patch.affectedElements)
}

// ---------------------------------------------------------------------------
// Forecasting
// ---------------------------------------------------------------------------
export async function getProjectForecastConfig(projectId?: string) {
  return queryJsonbTable('project_forecast_configs', projectId ? 'project_id' : undefined, projectId)
}

export async function getForecastVersions(projectId?: string) {
  return queryJsonbTable('forecast_versions', projectId ? 'project_id' : undefined, projectId)
}

/**
 * Insert a new forecast version. forecast_versions is an `id text + data jsonb`
 * table, so the whole typed row lives in `data` (mirrors the PTW seed path in
 * ptwForecastService). Caller owns id generation and total roll-ups.
 */
export async function createForecastVersion(
  version: import('@/types/forecasting').ForecastVersion,
): Promise<import('@/types/forecasting').ForecastVersion> {
  const { error } = await supabase
    .from('forecast_versions')
    .insert({ id: version.id, project_id: version.projectId, data: version })
  if (error) throw new Error(`Supabase insert [forecast_versions]: ${error.message}`)
  return version
}

// ---------------------------------------------------------------------------
// Rates Management - labor rates, costing-sheet rate decks, deck assignments
// (all id + project_id + data jsonb; migrations 00338-00340)
// ---------------------------------------------------------------------------
export async function getProjectLaborRates(projectId?: string) {
  return queryJsonbTable('project_labor_rates', projectId ? 'project_id' : undefined, projectId)
}

export async function upsertProjectLaborRate(
  rate: import('@/types/rates').LaborRateOrgDiscipline,
): Promise<import('@/types/rates').LaborRateOrgDiscipline> {
  const { error } = await supabase
    .from('project_labor_rates')
    .upsert({ id: rate.id, project_id: rate.projectId, data: rate })
  if (error) throw new Error(`Supabase upsert [project_labor_rates]: ${error.message}`)
  return rate
}

export async function deleteProjectLaborRate(id: string): Promise<void> {
  const { error } = await supabase.from('project_labor_rates').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete [project_labor_rates]: ${error.message}`)
}

export async function getProjectRateDecks(projectId?: string) {
  return queryJsonbTable('project_rate_decks', projectId ? 'project_id' : undefined, projectId)
}

export async function upsertProjectRateDeck(
  version: import('@/types/rates').CostingSheetVersion,
): Promise<import('@/types/rates').CostingSheetVersion> {
  const { error } = await supabase
    .from('project_rate_decks')
    .upsert({ id: version.id, project_id: version.projectId, version_group_id: version.versionGroupId, data: version })
  if (error) throw new Error(`Supabase upsert [project_rate_decks]: ${error.message}`)
  return version
}

export async function deleteProjectRateDeck(id: string): Promise<void> {
  const { error } = await supabase.from('project_rate_decks').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete [project_rate_decks]: ${error.message}`)
}

export async function getRateDeckAssignments(projectId?: string) {
  return queryJsonbTable('project_rate_deck_assignments', projectId ? 'project_id' : undefined, projectId)
}

// ---------------------------------------------------------------------------
// CAS / DCAA Rates Management (Organizational Management context).
// Four org-wide tables backing the cost waterfall (migration 00382):
//   cas_rate_segments -> cas_rate_pools -> cas_cost_pools -> cas_cost_records
// All use the id + key-columns + data jsonb convention; reads merge data over
// columns (mergeJsonbRow). Scoped by org_id when provided.
// ---------------------------------------------------------------------------
type CasSegment = import('@/types/casRates').RateSegment
type CasRatePool = import('@/types/casRates').RatePool
type CasCostPool = import('@/types/casRates').CostPool
type CasCostRecord = import('@/types/casRates').CostRecord

export async function getCasRateSegments(orgId?: string) {
  return queryJsonbTable('cas_rate_segments', orgId ? 'org_id' : undefined, orgId)
}
export async function upsertCasRateSegment(segment: CasSegment): Promise<CasSegment> {
  const { error } = await supabase
    .from('cas_rate_segments')
    .upsert({ id: segment.id, org_id: segment.orgId, data: segment })
  if (error) throw new Error(`Supabase upsert [cas_rate_segments]: ${error.message}`)
  return segment
}
export async function deleteCasRateSegment(id: string): Promise<void> {
  const { error } = await supabase.from('cas_rate_segments').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete [cas_rate_segments]: ${error.message}`)
}

export async function getCasRatePools(orgId?: string) {
  return queryJsonbTable('cas_rate_pools', orgId ? 'org_id' : undefined, orgId)
}
export async function upsertCasRatePool(pool: CasRatePool): Promise<CasRatePool> {
  const { error } = await supabase
    .from('cas_rate_pools')
    .upsert({ id: pool.id, segment_id: pool.segmentId, org_id: pool.orgId, data: pool })
  if (error) throw new Error(`Supabase upsert [cas_rate_pools]: ${error.message}`)
  return pool
}
export async function deleteCasRatePool(id: string): Promise<void> {
  const { error } = await supabase.from('cas_rate_pools').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete [cas_rate_pools]: ${error.message}`)
}

export async function getCasCostPools(orgId?: string) {
  return queryJsonbTable('cas_cost_pools', orgId ? 'org_id' : undefined, orgId)
}
export async function upsertCasCostPool(pool: CasCostPool): Promise<CasCostPool> {
  const { error } = await supabase
    .from('cas_cost_pools')
    .upsert({ id: pool.id, rate_pool_id: pool.ratePoolId, segment_id: pool.segmentId, org_id: pool.orgId, data: pool })
  if (error) throw new Error(`Supabase upsert [cas_cost_pools]: ${error.message}`)
  return pool
}
export async function deleteCasCostPool(id: string): Promise<void> {
  const { error } = await supabase.from('cas_cost_pools').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete [cas_cost_pools]: ${error.message}`)
}

export async function getCasCostRecords(orgId?: string) {
  return queryJsonbTable('cas_cost_records', orgId ? 'org_id' : undefined, orgId)
}
export async function upsertCasCostRecord(rec: CasCostRecord): Promise<CasCostRecord> {
  const { error } = await supabase
    .from('cas_cost_records')
    .upsert({ id: rec.id, cost_pool_id: rec.costPoolId, org_id: rec.orgId, fiscal_year: rec.fiscalYear, data: rec })
  if (error) throw new Error(`Supabase upsert [cas_cost_records]: ${error.message}`)
  return rec
}

/**
 * The named resources loaded against a project = distinct named_resource_ids
 * referenced by the project's plan_lines, joined to the named_resources master.
 * Feeds the "By Resource" labor-rate sub-tab. Returns camelCased master rows.
 */
export async function getProjectLoadedResources(projectId: string): Promise<Record<string, unknown>[]> {
  const { data: lines, error: lerr } = await supabase
    .from('plan_lines')
    .select('data')
    .eq('project_id', projectId)
  if (lerr) throw new Error(`Supabase query error [plan_lines]: ${lerr.message}`)
  const ids = new Set<string>()
  for (const row of (lines ?? []) as Array<{ data?: Record<string, unknown> }>) {
    const nr = row.data?.namedResourceId as string | undefined
    if (nr) ids.add(nr)
  }
  if (ids.size === 0) return []
  const { data: res, error: rerr } = await supabase
    .from('named_resources')
    .select('*')
    .in('id', [...ids])
  if (rerr) throw new Error(`Supabase query error [named_resources]: ${rerr.message}`)
  return mergeJsonbRows((res ?? []) as Record<string, unknown>[])
}

export async function upsertRateDeckAssignment(
  a: import('@/types/rates').RateDeckAssignment,
): Promise<import('@/types/rates').RateDeckAssignment> {
  const { error } = await supabase.from('project_rate_deck_assignments').upsert({
    id: a.id,
    project_id: a.projectId,
    scope: a.scope,
    wbs_node_id: a.wbsNodeId,
    rate_class: a.rateClass,
    costing_sheet_version_id: a.costingSheetVersionId,
    data: a,
  })
  if (error) throw new Error(`Supabase upsert [project_rate_deck_assignments]: ${error.message}`)
  return a
}

export async function deleteRateDeckAssignment(id: string): Promise<void> {
  const { error } = await supabase.from('project_rate_deck_assignments').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete [project_rate_deck_assignments]: ${error.message}`)
}

export async function getForecastWbsSummaries(forecastVersionId?: string) {
  return queryJsonbTable('forecast_wbs_summaries', forecastVersionId ? 'forecast_version_id' : undefined, forecastVersionId)
}

export async function getForecastResourceLines(forecastVersionId?: string, costNodeId?: string) {
  let q = supabase.from('forecast_resource_lines').select('*')
  if (forecastVersionId) q = q.eq('forecast_version_id', forecastVersionId)
  if (costNodeId) q = q.eq('cost_node_id', costNodeId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [forecast_resource_lines]: ${error.message}`)
  return mergeJsonbRows((data ?? []) as Record<string, unknown>[])
}

export async function getForecastTimePhaseEntries(forecastVersionId?: string, costNodeId?: string) {
  let q = supabase.from('forecast_time_phase_entries').select('*')
  if (forecastVersionId) q = q.eq('forecast_version_id', forecastVersionId)
  if (costNodeId) q = q.eq('cost_node_id', costNodeId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [forecast_time_phase_entries]: ${error.message}`)
  return mergeJsonbRows((data ?? []) as Record<string, unknown>[])
}

export async function upsertForecastResourceLines(
  lines: import('@/types/forecasting').ForecastResourceLine[],
): Promise<void> {
  if (lines.length === 0) return
  const rows = lines.map(({ id, forecastVersionId, costNodeId, ...rest }) => ({
    id,
    forecast_version_id: forecastVersionId,
    cost_node_id: costNodeId ?? null,
    data: rest,
  }))
  const { error } = await supabase
    .from('forecast_resource_lines')
    .upsert(rows, { onConflict: 'id' })
  if (error) throw new Error(`Supabase upsert [forecast_resource_lines]: ${error.message}`)
}

export async function upsertForecastTimePhaseEntries(
  entries: import('@/types/forecasting').ForecastTimePhaseEntry[],
): Promise<void> {
  if (entries.length === 0) return
  const rows = entries.map(({ id, forecastVersionId, wbsElementId, ...rest }) => ({
    id,
    forecast_version_id: forecastVersionId,
    cost_node_id: wbsElementId,
    data: rest,
  }))
  const { error } = await supabase
    .from('forecast_time_phase_entries')
    .upsert(rows, { onConflict: 'id' })
  if (error) throw new Error(`Supabase upsert [forecast_time_phase_entries]: ${error.message}`)
}

export async function getRevenueForecastLines(forecastVersionId?: string, costNodeId?: string) {
  let q = supabase.from('revenue_forecast_lines').select('*')
  if (forecastVersionId) q = q.eq('forecast_version_id', forecastVersionId)
  if (costNodeId) q = q.eq('cost_node_id', costNodeId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [revenue_forecast_lines]: ${error.message}`)
  return mergeJsonbRows((data ?? []) as Record<string, unknown>[])
}

export async function getRevenueTimePhaseEntries(forecastVersionId?: string, costNodeId?: string) {
  let q = supabase.from('revenue_time_phase_entries').select('*')
  if (forecastVersionId) q = q.eq('forecast_version_id', forecastVersionId)
  if (costNodeId) q = q.eq('cost_node_id', costNodeId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [revenue_time_phase_entries]: ${error.message}`)
  return mergeJsonbRows((data ?? []) as Record<string, unknown>[])
}

export async function getForecastSubmissions(forecastVersionId?: string) {
  return queryJsonbTable('forecast_submissions', forecastVersionId ? 'forecast_version_id' : undefined, forecastVersionId)
}

export async function getActualsSyncRecords(forecastVersionId?: string) {
  return queryJsonbTable('actuals_sync_records', forecastVersionId ? 'forecast_version_id' : undefined, forecastVersionId)
}

// ---------------------------------------------------------------------------
// Forecast Actuals - SAP-imported actual cost/hours at WBS+period+category grain
// (migration 00333_forecast_actuals). Source of truth for forecast actuals.
// ---------------------------------------------------------------------------

export interface ForecastActualRowInput {
  forecastVersionId: string
  projectId: string
  wbsElementId: string
  wbsElementCode: string
  period: string
  costCategory: 'Labor' | 'Material' | 'ODC' | 'Subcontract' | 'Overhead'
  actualCost: number
  actualHours: number
  sourceCostElement?: string | null
  sourceWbsPosid?: string | null
  currency?: string | null
  data?: Record<string, unknown>
  syncRecordId?: string | null
}

export async function getForecastActuals(forecastVersionId?: string) {
  return queryJsonbTable('forecast_actuals', forecastVersionId ? 'forecast_version_id' : undefined, forecastVersionId)
}

/**
 * Idempotent import: drop this version's actuals for the covered periods, then
 * insert the fresh set. Periods outside `periods` (future ETC) are untouched.
 */
/**
 * DELTA upsert of SAP actuals. Never deletes.
 *
 * This replaced replaceForecastActualsForVersion, which issued a DELETE over
 * every covered period and then a separate INSERT - two independent statements,
 * no transaction. A re-sync that failed after the delete wiped the period and
 * put nothing back, and because the caller wrote its audit row first, the sync
 * history showed a clean import over empty data.
 *
 * Now one atomic RPC that upserts on the natural grain (version + WBS + period
 * + category + cost element), which is exactly what the unique index added in
 * 00333 was built for. Re-syncing is idempotent, a changed figure updates in
 * place, and previously-synced lines are never destroyed.
 *
 * Reversal caveat, which is the deliberate cost of not deleting: a line is
 * corrected whenever SAP reports it, INCLUDING when SAP reports it as zero. If
 * SAP stops returning a line entirely, that line keeps its last synced value.
 */
export async function upsertForecastActualsForVersion(args: {
  forecastVersionId: string
  rows: ForecastActualRowInput[]
}): Promise<number> {
  const { forecastVersionId, rows } = args
  if (rows.length === 0) return 0
  const payload = rows.map(r => ({
    project_id: r.projectId,
    wbs_element_id: r.wbsElementId,
    wbs_element_code: r.wbsElementCode,
    period: r.period,
    cost_category: r.costCategory,
    actual_cost: r.actualCost,
    actual_hours: r.actualHours,
    source_cost_element: r.sourceCostElement ?? null,
    source_wbs_posid: r.sourceWbsPosid ?? null,
    currency: r.currency ?? null,
    data: r.data ?? {},
    sync_record_id: r.syncRecordId ?? null,
  }))
  const { data, error } = await supabase.rpc('upsert_forecast_actuals', {
    p_version_id: forecastVersionId,
    p_rows: payload,
  })
  if (error) throw new Error(`Supabase rpc [upsert_forecast_actuals]: ${error.message}`)
  return typeof data === 'number' ? data : rows.length
}

/**
 * Patch an actuals_sync_records audit row after the import has actually run.
 *
 * The record has to be created BEFORE the rows, because every imported row
 * carries its id as a foreign key. That is exactly why the old flow reported
 * success over data that never landed. Creating it first is unavoidable;
 * leaving it saying "Completed" when the write failed is not.
 */
export async function finalizeActualsSyncRecord(args: {
  syncRecordId: string
  patch: Partial<import('@/types/forecasting').ActualsSyncRecord>
}): Promise<void> {
  const { data: existing, error: readErr } = await supabase
    .from('actuals_sync_records')
    .select('data')
    .eq('id', args.syncRecordId)
    .single()
  if (readErr) throw new Error(`Supabase read [actuals_sync_records]: ${readErr.message}`)
  const current = ((existing as Record<string, unknown>)?.data ?? {}) as Record<string, unknown>
  const { error } = await supabase
    .from('actuals_sync_records')
    .update({ data: { ...current, ...args.patch } })
    .eq('id', args.syncRecordId)
  if (error) throw new Error(`Supabase update [actuals_sync_records]: ${error.message}`)
}

/** Insert an actuals_sync_records audit row (id + forecast_version_id + jsonb). */
export async function createActualsSyncRecord(args: {
  forecastVersionId: string
  record: Omit<import('@/types/forecasting').ActualsSyncRecord, 'id' | 'forecastVersionId'>
}): Promise<string> {
  const { data, error } = await supabase
    .from('actuals_sync_records')
    .insert({ forecast_version_id: args.forecastVersionId, data: args.record })
    .select('id')
    .single()
  if (error) throw new Error(`Supabase insert [actuals_sync_records]: ${error.message}`)
  return (data as { id: string }).id
}

// ---------------------------------------------------------------------------
// Forecast Management - Phase 5/6 entities
// ---------------------------------------------------------------------------

export async function getWorkAuthorizations(projectId?: string) {
  let q = supabase.from('work_authorizations').select(`
    *,
    charge_lines (*)
  `)
  if (projectId) q = q.eq('project_id', projectId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [work_authorizations]: ${error.message}`)
  return (data ?? []).map((row: Record<string, unknown>) => {
    const merged = transformKeys(row) as Record<string, unknown>
    if (Array.isArray(row.charge_lines)) {
      merged.chargeLines = (row.charge_lines as Record<string, unknown>[]).map(cl => transformKeys(cl))
    }
    return merged
  })
}

// ---------------------------------------------------------------------------
// Work Authorization staffing - resource-demand-to-people assignments
// (migration 00342). See web/src/components/forecasting/resourceDemandStaffing.ts
// for the demand-slot read model that drives the staffing panel.
// ---------------------------------------------------------------------------

export async function getWorkAuthorizationAssignments(projectId?: string) {
  let q = supabase.from('work_authorization_assignments').select('*')
  if (projectId) q = q.eq('project_id', projectId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [work_authorization_assignments]: ${error.message}`)
  return (data ?? []).map((row: Record<string, unknown>) => transformKeys(row))
}

/**
 * Upsert (insert or overwrite) the staffing record for a demand slot. Keyed on
 * plan_line_id (unique), so re-assigning a slot overwrites in place. The full
 * camelCase object is mirrored into the `data` jsonb for forward-compat.
 */
export async function upsertWorkAuthorizationAssignment(
  a: import('@/types/forecasting').WorkAuthorizationAssignment,
) {
  const row = {
    id: a.id,
    project_id: a.projectId,
    plan_line_id: a.planLineId,
    plan_version_id: a.planVersionId,
    work_authorization_id: a.workAuthorizationId,
    wbs_element_id: a.wbsElementId,
    wbs_element_code: a.wbsElementCode,
    wbs_element_name: a.wbsElementName,
    discipline_code: a.disciplineCode,
    discipline_name: a.disciplineName,
    role: a.role,
    level: a.level,
    named_resource_id: a.namedResourceId,
    named_resource_name: a.namedResourceName,
    planned_hours: a.plannedHours,
    status: a.status,
    assigned_by: a.assignedBy,
    assigned_by_name: a.assignedByName,
    assigned_date: a.assignedDate,
    notes: a.notes,
    data: a,
  }
  const { error } = await supabase
    .from('work_authorization_assignments')
    .upsert(row, { onConflict: 'plan_line_id' })
  if (error) throw new Error(`Supabase upsert [work_authorization_assignments]: ${error.message}`)
  return a
}

/** Remove the staffing record for a demand slot (slot reverts to unstaffed). */
export async function deleteWorkAuthorizationAssignmentByLine(planLineId: string) {
  const { error } = await supabase
    .from('work_authorization_assignments')
    .delete()
    .eq('plan_line_id', planLineId)
  if (error) throw new Error(`Supabase delete [work_authorization_assignments]: ${error.message}`)
}

/** Active named-resource roster (camelCased), for the staffing picker. */
export async function getNamedResourceRoster() {
  const { data, error } = await supabase
    .from('named_resources')
    .select('*')
    .eq('active', true)
  if (error) throw new Error(`Supabase query error [named_resources]: ${error.message}`)
  return (data ?? []).map((row: Record<string, unknown>) => transformKeys(row))
}

export async function getEacCycles(projectId?: string) {
  let q = supabase.from('eac_cycles').select('*')
  if (projectId) q = q.eq('project_id', projectId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [eac_cycles]: ${error.message}`)
  return (data ?? []).map((row: Record<string, unknown>) => transformKeys(row))
}

export async function getEacSubmissions(cycleId?: string) {
  let q = supabase.from('eac_submissions').select(`
    *,
    eac_approvals (*)
  `)
  if (cycleId) q = q.eq('cycle_id', cycleId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [eac_submissions]: ${error.message}`)
  return (data ?? []).map((row: Record<string, unknown>) => {
    const merged = transformKeys(row) as Record<string, unknown>
    if (Array.isArray(row.eac_approvals)) {
      merged.approvalChain = (row.eac_approvals as Record<string, unknown>[]).map(a => transformKeys(a))
    }
    return merged
  })
}

/** Project-level convenience: all EAC submissions across all cycles for a project. */
export async function getEacSubmissionsByProject(projectId: string) {
  const { data: cycles, error: cycleErr } = await supabase
    .from('eac_cycles')
    .select('id')
    .eq('project_id', projectId)
  if (cycleErr) throw new Error(`Supabase query error [eac_cycles]: ${cycleErr.message}`)
  const cycleIds = (cycles ?? []).map((c: { id: string }) => c.id)
  if (cycleIds.length === 0) return []
  const { data, error } = await supabase
    .from('eac_submissions')
    .select(`*, eac_approvals (*)`)
    .in('cycle_id', cycleIds)
  if (error) throw new Error(`Supabase query error [eac_submissions]: ${error.message}`)
  return (data ?? []).map((row: Record<string, unknown>) => {
    const merged = transformKeys(row) as Record<string, unknown>
    if (Array.isArray(row.eac_approvals)) {
      merged.approvalChain = (row.eac_approvals as Record<string, unknown>[]).map(a => transformKeys(a))
    }
    return merged
  })
}

// ─── EAC write mutations ─────────────────────────────────────────────────────
// All four respect the schema CHECK constraints on eac_submissions.status and
// eac_approvals.action. The read path in getEacSubmissions joins approvals
// onto submissions, so invalidating either query key picks up both sides.

export async function submitEacSubmission(
  submissionId: string,
  submittedBy: string,
  submittedByName: string,
) {
  const { data, error } = await supabase
    .from('eac_submissions')
    .update({
      status: 'Submitted',
      submitted_by: submittedBy,
      submitted_by_name: submittedByName,
      submitted_date: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', submissionId)
    .select('*')
    .single()
  if (error) throw new Error(`submitEacSubmission failed: ${error.message}`)
  return transformRow(data as Record<string, unknown>)
}

/**
 * Approve one step in the approval chain. If `isLastStep` is true, the parent
 * submission flips to 'Approved'. Otherwise it moves to 'UnderReview' so the
 * next approver in the chain can act.
 */
export async function approveEacApprovalStep(
  approvalId: string,
  submissionId: string,
  isLastStep: boolean,
  comments?: string,
) {
  const now = new Date().toISOString()
  const { error: stepErr } = await supabase
    .from('eac_approvals')
    .update({ action: 'Approved', action_date: now, comments: comments ?? null, updated_at: now })
    .eq('id', approvalId)
  if (stepErr) throw new Error(`approveEacApprovalStep failed: ${stepErr.message}`)
  const nextStatus = isLastStep ? 'Approved' : 'UnderReview'
  const { error: subErr } = await supabase
    .from('eac_submissions')
    .update({ status: nextStatus, updated_at: now })
    .eq('id', submissionId)
  if (subErr) throw new Error(`submission status update failed: ${subErr.message}`)
  return { ok: true, nextStatus }
}

export async function rejectEacApprovalStep(
  approvalId: string,
  submissionId: string,
  comments?: string,
) {
  const now = new Date().toISOString()
  const { error: stepErr } = await supabase
    .from('eac_approvals')
    .update({ action: 'Rejected', action_date: now, comments: comments ?? null, updated_at: now })
    .eq('id', approvalId)
  if (stepErr) throw new Error(`rejectEacApprovalStep failed: ${stepErr.message}`)
  const { error: subErr } = await supabase
    .from('eac_submissions')
    .update({ status: 'Rejected', updated_at: now })
    .eq('id', submissionId)
  if (subErr) throw new Error(`submission status update failed: ${subErr.message}`)
  return { ok: true }
}

export async function requestEacRevision(
  approvalId: string,
  submissionId: string,
  comments?: string,
) {
  const now = new Date().toISOString()
  const { error: stepErr } = await supabase
    .from('eac_approvals')
    .update({ action: 'RevisionRequested', action_date: now, comments: comments ?? null, updated_at: now })
    .eq('id', approvalId)
  if (stepErr) throw new Error(`requestEacRevision failed: ${stepErr.message}`)
  const { error: subErr } = await supabase
    .from('eac_submissions')
    .update({ status: 'Revised', updated_at: now })
    .eq('id', submissionId)
  if (subErr) throw new Error(`submission status update failed: ${subErr.message}`)
  return { ok: true }
}

export async function getMaterialForecastLines(forecastVersionId?: string) {
  let q = supabase.from('material_forecast_lines').select('*')
  if (forecastVersionId) q = q.eq('forecast_version_id', forecastVersionId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [material_forecast_lines]: ${error.message}`)
  return (data ?? []).map((row: Record<string, unknown>) => {
    const merged = transformKeys(row) as Record<string, unknown>
    // manufactured_breakdown is already JSONB, pass through with camelCase keys
    if (row.manufactured_breakdown && typeof row.manufactured_breakdown === 'object') {
      merged.manufacturedBreakdown = transformKeys(row.manufactured_breakdown as Record<string, unknown>)
    }
    // Overflow `data` jsonb (migration 00344) carries the full camelCase line,
    // including fields with no typed column (periods, glAccount*). Merge it last
    // for full fidelity; null on seeded rows is a no-op.
    if (row.data && typeof row.data === 'object') Object.assign(merged, row.data as Record<string, unknown>)
    return merged
  })
}

const MATERIAL_LINE_COLS = ['id','forecastVersionId','wbsElementId','wbsElementCode','wbsElementName','materialType','itemDescription','partNumber','vendorName','quantity','unitCost','totalPlannedCost','committedAmount','actualAmount','eacAmount','etcAmount','baselineAmount','varianceAmount','deliveryPeriod','consumptionPeriod','status','manufacturedBreakdown','varianceNarrative','createdBy','createdDate','lastModifiedBy','lastModifiedDate'] as const
const MATERIAL_NUMERIC = new Set(['quantity','unitCost','totalPlannedCost','committedAmount','actualAmount','eacAmount','etcAmount','baselineAmount','varianceAmount'])
const MATERIAL_REQ_TEXT = new Set(['wbsElementId','wbsElementCode','wbsElementName','materialType','itemDescription','deliveryPeriod','status'])

const ODC_LINE_COLS = ['id','forecastVersionId','wbsElementId','wbsElementCode','wbsElementName','categoryId','categoryName','description','vendorName','totalPlannedCost','totalActualCost','totalCommittedCost','eacAmount','etcAmount','baselineAmount','varianceAmount','periods','varianceNarrative','createdBy','createdDate','lastModifiedBy','lastModifiedDate'] as const
const ODC_NUMERIC = new Set(['totalPlannedCost','totalActualCost','totalCommittedCost','eacAmount','etcAmount','baselineAmount','varianceAmount'])
const ODC_REQ_TEXT = new Set(['wbsElementId','wbsElementCode','wbsElementName','categoryId','categoryName','description'])

const camelToSnake = (k: string) => k.replace(/[A-Z]/g, (m) => '_' + m.toLowerCase())

/** Build a snake_case DB row from a camelCase line, mapping only known columns,
 *  coercing missing NOT NULL values, and stashing the full line in `data`. */
function lineToRow(line: Record<string, unknown>, cols: readonly string[], numeric: Set<string>, reqText: Set<string>): Record<string, unknown> {
  const row: Record<string, unknown> = {}
  for (const camel of cols) {
    let v = line[camel]
    if (v === undefined || v === null) {
      if (numeric.has(camel)) v = 0
      else if (camel === 'periods') v = []
      else if (reqText.has(camel)) v = ''
      else v = null
    }
    row[camelToSnake(camel)] = v
  }
  row.data = line
  return row
}

export async function upsertMaterialForecastLine(line: Record<string, unknown>): Promise<void> {
  const { error } = await supabase.from('material_forecast_lines').upsert(lineToRow(line, MATERIAL_LINE_COLS, MATERIAL_NUMERIC, MATERIAL_REQ_TEXT))
  if (error) throw new Error(`Supabase upsert [material_forecast_lines]: ${error.message}`)
}
export async function deleteMaterialForecastLine(id: string): Promise<void> {
  const { error } = await supabase.from('material_forecast_lines').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete [material_forecast_lines]: ${error.message}`)
}
export async function upsertOdcForecastLine(line: Record<string, unknown>): Promise<void> {
  const { error } = await supabase.from('odc_forecast_lines').upsert(lineToRow(line, ODC_LINE_COLS, ODC_NUMERIC, ODC_REQ_TEXT))
  if (error) throw new Error(`Supabase upsert [odc_forecast_lines]: ${error.message}`)
}
export async function deleteOdcForecastLine(id: string): Promise<void> {
  const { error } = await supabase.from('odc_forecast_lines').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete [odc_forecast_lines]: ${error.message}`)
}

export async function getOdcCategories() {
  const { data, error } = await supabase.from('odc_categories').select('*').order('display_order')
  if (error) throw new Error(`Supabase query error [odc_categories]: ${error.message}`)
  return (data ?? []).map((row: Record<string, unknown>) => transformKeys(row))
}

export async function getOdcForecastLines(forecastVersionId?: string) {
  let q = supabase.from('odc_forecast_lines').select('*')
  if (forecastVersionId) q = q.eq('forecast_version_id', forecastVersionId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [odc_forecast_lines]: ${error.message}`)
  return (data ?? []).map((row: Record<string, unknown>) => {
    const merged = transformKeys(row) as Record<string, unknown>
    // periods is already JSONB array, camelCase each entry
    if (Array.isArray(row.periods)) {
      merged.periods = (row.periods as Record<string, unknown>[]).map(p => transformKeys(p))
    }
    // Overflow `data` jsonb (migration 00344) carries the full camelCase line.
    if (row.data && typeof row.data === 'object') Object.assign(merged, row.data as Record<string, unknown>)
    return merged
  })
}

export async function getCapitalAssetForecastLines(forecastVersionId?: string) {
  let q = supabase.from('capital_asset_forecast_lines').select('*')
  if (forecastVersionId) q = q.eq('forecast_version_id', forecastVersionId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [capital_asset_forecast_lines]: ${error.message}`)
  return (data ?? []).map((row: Record<string, unknown>) => transformKeys(row))
}

export async function getRevenueAdjustmentCategories() {
  const { data, error } = await supabase.from('revenue_adjustment_categories').select('*').order('display_order')
  if (error) throw new Error(`Supabase query error [revenue_adjustment_categories]: ${error.message}`)
  return (data ?? []).map((row: Record<string, unknown>) => transformKeys(row))
}

export async function getRevenueAdjustments(revenueForecastLineId?: string) {
  let q = supabase.from('revenue_adjustments').select('*')
  if (revenueForecastLineId) q = q.eq('revenue_forecast_line_id', revenueForecastLineId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [revenue_adjustments]: ${error.message}`)
  return (data ?? []).map((row: Record<string, unknown>) => transformKeys(row))
}

// ---------------------------------------------------------------------------
// Revenue Plan Entries
// ---------------------------------------------------------------------------
export async function getRevenuePlanEntries(baselineModelId?: string): Promise<RevenuePlanEntry[]> {
  return queryTable<RevenuePlanEntry>('revenue_plan_entries', {
    filter: baselineModelId ? { baseline_model_id: baselineModelId } : undefined,
  })
}

// ---------------------------------------------------------------------------
// Admin catalogs (expertise, certifications, cost-planning accounts)
// + per-user profile. Mirror the localStorage libs at lib/expertiseCatalog.ts,
// lib/certificationsCatalog.ts, lib/costPlanningAccounts.ts, lib/myProfileStore.ts.
//
// Tables created in 00079_localStorage_persistence_tables.sql.
// ---------------------------------------------------------------------------

interface ExpertiseCatalogRow {
  id: string
  name: string
  category: string | null
  description: string | null
}

export async function getExpertiseCatalogRows(): Promise<ExpertiseCatalogRow[]> {
  const { data, error } = await supabase
    .from('expertise_catalog')
    .select('id, name, category, description')
    .eq('is_active', true)
    .order('display_order', { ascending: true })
    .order('name', { ascending: true })
  if (error) throw new Error(`Supabase query error [expertise_catalog]: ${error.message}`)
  return data ?? []
}

export async function upsertExpertiseTagRow(tag: { id: string; name: string; category?: string; description?: string }): Promise<void> {
  const { error } = await supabase.from('expertise_catalog').upsert({
    id: tag.id,
    name: tag.name,
    category: tag.category ?? null,
    description: tag.description ?? null,
    updated_at: new Date().toISOString(),
  })
  if (error) throw new Error(`upsert expertise_catalog failed: ${error.message}`)
}

export async function deleteExpertiseTagRow(id: string): Promise<void> {
  const { error } = await supabase.from('expertise_catalog').delete().eq('id', id)
  if (error) throw new Error(`delete expertise_catalog failed: ${error.message}`)
}

// ---------------------------------------------------------------------------
// Disciplines catalog (00285_disciplines.sql)
// Hierarchical labor taxonomy: type → category → discipline (leaf).
// Per-project overrides (enable / progressible / project code) live in the
// project_disciplines table - see project-discipline helpers below.
// ---------------------------------------------------------------------------

export interface DisciplineCatalogRow {
  id: string
  parent_id: string | null
  node_type: 'type' | 'category' | 'discipline'
  name: string
  global_code: string | null
  code_type: string | null
  default_cost_element_group: string | null
  display_order: number
  is_active: boolean
  /** Owning organization (migration 00465). Null means the node is shared
   *  across every org. Set on the root Type and inherited by its subtree - see
   *  lib/disciplineOrgScope. Distinct from the dormant uuid `org_id` tenant
   *  placeholder, which organizations.id (text) cannot populate. */
  owner_org_id: string | null
}

export async function getDisciplinesCatalogRows(): Promise<DisciplineCatalogRow[]> {
  const { data, error } = await supabase
    .from('disciplines_catalog')
    .select('id, parent_id, node_type, name, global_code, code_type, default_cost_element_group, display_order, is_active, owner_org_id')
    .eq('is_active', true)
    .order('display_order', { ascending: true })
    .order('name', { ascending: true })
  if (error) throw new Error(`Supabase query error [disciplines_catalog]: ${error.message}`)
  return (data ?? []) as DisciplineCatalogRow[]
}

export async function upsertDisciplineRow(row: {
  id: string
  parent_id: string | null
  node_type: 'type' | 'category' | 'discipline'
  name: string
  global_code?: string | null
  code_type?: string | null
  default_cost_element_group?: string | null
  display_order?: number
  owner_org_id?: string | null
}): Promise<void> {
  const { error } = await supabase.from('disciplines_catalog').upsert({
    id: row.id,
    parent_id: row.parent_id,
    node_type: row.node_type,
    name: row.name,
    global_code: row.global_code ?? null,
    code_type: row.code_type ?? 'Global Code',
    default_cost_element_group: row.default_cost_element_group ?? null,
    display_order: row.display_order ?? 0,
    owner_org_id: row.owner_org_id ?? null,
    is_active: true,
    updated_at: new Date().toISOString(),
  })
  if (error) throw new Error(`upsert disciplines_catalog failed: ${error.message}`)
}

export async function bulkInsertDisciplineRows(rows: Array<{
  id: string
  parent_id: string | null
  node_type: 'type' | 'category' | 'discipline'
  name: string
  global_code?: string | null
  code_type?: string | null
  default_cost_element_group?: string | null
  display_order?: number
  owner_org_id?: string | null
}>): Promise<void> {
  if (rows.length === 0) return
  const payload = rows.map((r) => ({
    id: r.id,
    parent_id: r.parent_id,
    node_type: r.node_type,
    name: r.name,
    global_code: r.global_code ?? null,
    code_type: r.code_type ?? 'Global Code',
    default_cost_element_group: r.default_cost_element_group ?? null,
    display_order: r.display_order ?? 0,
    owner_org_id: r.owner_org_id ?? null,
    is_active: true,
  }))
  const { error } = await supabase.from('disciplines_catalog').upsert(payload)
  if (error) throw new Error(`bulk insert disciplines_catalog failed: ${error.message}`)
}

export interface CostElementGroupOption {
  group: string
  /** Sample of one chart it appears in, for display context. */
  chart_name?: string | null
  account_count: number
  /** Slash-delimited hierarchy path of account groups, e.g.
   *  "ICS_ALL/ALLOW_ALL/BILL_ALL/LAB_ALL/CON_LAB/CON_ENG". Drives the treed
   *  group picker. Null for legacy rows with no rollup path. */
  group_path?: string | null
  /** Depth of the group within `group_path` (0 = root). */
  group_level?: number | null
}

export async function getCostElementGroups(chartOfAccountsId?: string | null): Promise<CostElementGroupOption[]> {
  let query = supabase
    .from('chart_of_accounts_accounts')
    .select('account_group, group_path, group_level, chart_of_accounts_id, chart_of_accounts:chart_of_accounts_id (name)')
    .not('account_group', 'is', null)
  if (chartOfAccountsId) query = query.eq('chart_of_accounts_id', chartOfAccountsId)
  const { data, error } = await query
  if (error) throw new Error(`Supabase query error [chart_of_accounts_accounts]: ${error.message}`)
  const rows = (data ?? []) as unknown as Array<{
    account_group: string | null; group_path: string | null; group_level: number | null
    chart_of_accounts: { name: string } | null
  }>
  const byGroup = new Map<string, { chart: string | null; count: number; path: string | null; level: number | null }>()
  for (const r of rows) {
    if (!r.account_group) continue
    const existing = byGroup.get(r.account_group) ?? { chart: r.chart_of_accounts?.name ?? null, count: 0, path: null, level: null }
    existing.count += 1
    // Keep the most specific (deepest) path seen for this group so the tree
    // places it correctly even when the same group appears under multiple charts.
    if (r.group_path && (existing.path == null || (r.group_level ?? 0) > (existing.level ?? -1))) {
      existing.path = r.group_path
      existing.level = r.group_level
    }
    byGroup.set(r.account_group, existing)
  }
  return [...byGroup.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([group, v]) => ({ group, chart_name: v.chart, account_count: v.count, group_path: v.path, group_level: v.level }))
}

export async function deleteDisciplineRow(id: string): Promise<void> {
  const { error } = await supabase.from('disciplines_catalog').delete().eq('id', id)
  if (error) throw new Error(`delete disciplines_catalog failed: ${error.message}`)
}

export async function clearDisciplinesCatalog(): Promise<void> {
  // Delete only the type-level rows; FK cascade removes categories + leaves.
  const { error } = await supabase.from('disciplines_catalog').delete().eq('node_type', 'type')
  if (error) throw new Error(`clear disciplines_catalog failed: ${error.message}`)
}

export interface ProjectDisciplineRow {
  id: string
  project_id: string
  discipline_id: string
  is_enabled: boolean
  is_progressible: boolean
  project_code: string | null
  cost_element_group: string | null
}

export async function getProjectDisciplineRows(projectId: string): Promise<ProjectDisciplineRow[]> {
  const { data, error } = await supabase
    .from('project_disciplines')
    .select('id, project_id, discipline_id, is_enabled, is_progressible, project_code, cost_element_group')
    .eq('project_id', projectId)
  if (error) throw new Error(`Supabase query error [project_disciplines]: ${error.message}`)
  return (data ?? []) as ProjectDisciplineRow[]
}

export async function upsertProjectDisciplineRow(row: {
  project_id: string
  discipline_id: string
  is_enabled: boolean
  is_progressible: boolean
  project_code?: string | null
  cost_element_group?: string | null
}): Promise<void> {
  const { error } = await supabase.from('project_disciplines').upsert({
    project_id: row.project_id,
    discipline_id: row.discipline_id,
    is_enabled: row.is_enabled,
    is_progressible: row.is_progressible,
    project_code: row.project_code ?? null,
    cost_element_group: row.cost_element_group ?? null,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'project_id,discipline_id' })
  if (error) throw new Error(`upsert project_disciplines failed: ${error.message}`)
}

/**
 * Insert project_discipline rows in one round trip, skipping any that already
 * exist. Used to seed a project from its organization's discipline hierarchy:
 * `ignoreDuplicates` means a re-seed never clobbers a PM's manual unchecks,
 * project codes, or cost-element overrides.
 */
export async function bulkSeedProjectDisciplineRows(rows: Array<{
  project_id: string
  discipline_id: string
  is_enabled: boolean
  is_progressible: boolean
}>): Promise<void> {
  if (rows.length === 0) return
  const { error } = await supabase
    .from('project_disciplines')
    .upsert(rows, { onConflict: 'project_id,discipline_id', ignoreDuplicates: true })
  if (error) throw new Error(`seed project_disciplines failed: ${error.message}`)
}

export async function getProjectChartOfAccountsId(projectId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('projects')
    .select('chart_of_accounts_id')
    .eq('id', projectId)
    .maybeSingle()
  if (error) throw new Error(`Supabase query error [projects.chart_of_accounts_id]: ${error.message}`)
  return (data?.chart_of_accounts_id as string | null) ?? null
}

export async function deleteProjectDisciplineRow(projectId: string, disciplineId: string): Promise<void> {
  const { error } = await supabase
    .from('project_disciplines')
    .delete()
    .eq('project_id', projectId)
    .eq('discipline_id', disciplineId)
  if (error) throw new Error(`delete project_disciplines failed: ${error.message}`)
}

interface ToolsCatalogRow {
  id: string
  name: string
  category: string | null
  description: string | null
}

export async function getToolsCatalogRows(): Promise<ToolsCatalogRow[]> {
  const { data, error } = await supabase
    .from('tools_catalog')
    .select('id, name, category, description')
    .eq('is_active', true)
    .order('display_order', { ascending: true })
    .order('name', { ascending: true })
  if (error) throw new Error(`Supabase query error [tools_catalog]: ${error.message}`)
  return data ?? []
}

export async function upsertToolRow(tag: { id: string; name: string; category?: string; description?: string }): Promise<void> {
  const { error } = await supabase.from('tools_catalog').upsert({
    id: tag.id,
    name: tag.name,
    category: tag.category ?? null,
    description: tag.description ?? null,
    updated_at: new Date().toISOString(),
  })
  if (error) throw new Error(`upsert tools_catalog failed: ${error.message}`)
}

export async function deleteToolRow(id: string): Promise<void> {
  const { error } = await supabase.from('tools_catalog').delete().eq('id', id)
  if (error) throw new Error(`delete tools_catalog failed: ${error.message}`)
}

interface CertificationCatalogRow {
  id: string
  name: string
  category: string | null
  issuer: string | null
  description: string | null
}

export async function getCertificationsCatalogRows(): Promise<CertificationCatalogRow[]> {
  const { data, error } = await supabase
    .from('certifications_catalog')
    .select('id, name, category, issuer, description')
    .eq('is_active', true)
    .order('display_order', { ascending: true })
    .order('name', { ascending: true })
  if (error) throw new Error(`Supabase query error [certifications_catalog]: ${error.message}`)
  return data ?? []
}

export async function upsertCertificationTagRow(tag: { id: string; name: string; category?: string; issuer?: string; description?: string }): Promise<void> {
  const { error } = await supabase.from('certifications_catalog').upsert({
    id: tag.id,
    name: tag.name,
    category: tag.category ?? null,
    issuer: tag.issuer ?? null,
    description: tag.description ?? null,
    updated_at: new Date().toISOString(),
  })
  if (error) throw new Error(`upsert certifications_catalog failed: ${error.message}`)
}

export async function deleteCertificationTagRow(id: string): Promise<void> {
  const { error } = await supabase.from('certifications_catalog').delete().eq('id', id)
  if (error) throw new Error(`delete certifications_catalog failed: ${error.message}`)
}

interface CostPlanningAccountRow {
  id: string
  code: string
  name: string
  account_type: string
  description: string | null
  is_active: boolean
}

export async function getCostPlanningAccountRows(): Promise<CostPlanningAccountRow[]> {
  const { data, error } = await supabase
    .from('cost_planning_accounts')
    .select('id, code, name, account_type, description, is_active')
    .order('display_order', { ascending: true })
    .order('code', { ascending: true })
  if (error) throw new Error(`Supabase query error [cost_planning_accounts]: ${error.message}`)
  return data ?? []
}

export async function upsertCostPlanningAccountRow(acct: { id: string; code: string; name: string; accountType: string; description?: string; isActive?: boolean }): Promise<void> {
  const { error } = await supabase.from('cost_planning_accounts').upsert({
    id: acct.id,
    code: acct.code,
    name: acct.name,
    account_type: acct.accountType,
    description: acct.description ?? null,
    is_active: acct.isActive ?? true,
    updated_at: new Date().toISOString(),
  })
  if (error) throw new Error(`upsert cost_planning_accounts failed: ${error.message}`)
}

export async function deleteCostPlanningAccountRow(id: string): Promise<void> {
  const { error } = await supabase.from('cost_planning_accounts').delete().eq('id', id)
  if (error) throw new Error(`delete cost_planning_accounts failed: ${error.message}`)
}

interface UserProfileRow {
  user_id: string
  title: string | null
  bio: string | null
  organization_id: string | null
  organization_name: string | null
  tools: string[]
  expertise: unknown[]
  certifications: unknown[]
  date_format: string | null
  updated_at: string
}

export async function getUserProfileRow(userId: string): Promise<UserProfileRow | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('user_id, title, bio, organization_id, organization_name, tools, expertise, certifications, date_format, updated_at')
    .eq('user_id', userId)
    .maybeSingle()
  if (error) throw new Error(`Supabase query error [user_profiles]: ${error.message}`)
  return (data as UserProfileRow | null) ?? null
}

export async function upsertUserProfileRow(profile: {
  userId: string
  title?: string
  bio?: string
  organizationId?: string
  organizationName?: string
  tools: string[]
  expertise: unknown[]
  certifications: unknown[]
  dateFormat?: string | null
}): Promise<void> {
  const { error } = await supabase.from('user_profiles').upsert({
    user_id: profile.userId,
    title: profile.title ?? null,
    bio: profile.bio ?? null,
    organization_id: profile.organizationId ?? null,
    organization_name: profile.organizationName ?? null,
    tools: profile.tools,
    expertise: profile.expertise,
    certifications: profile.certifications,
    date_format: profile.dateFormat ?? null,
    updated_at: new Date().toISOString(),
  })
  if (error) throw new Error(`upsert user_profiles failed: ${error.message}`)
}

// ---------------------------------------------------------------------------
// Sub-portal - vendors, teaming agreements, sub time entries.
// Tables created in 00097_sub_portal_demo_persistence.sql.
// ---------------------------------------------------------------------------

export interface SubVendorRow {
  id: string
  name: string
  description: string | null
  is_active: boolean
}

export interface TeamingAgreementRow {
  id: string
  sub_vendor_id: string
  rate_markup: number
  lcat_mappings: Array<{ fromLcat: string; toLcat: string }>
  is_active: boolean
}

export interface SubTimeEntryRow {
  id: string
  sub_vendor_id: string
  employee_id: string
  entry_date: string
  prime_charge_code_id: string
  prime_charge_code_label: string | null
  sub_labor_category: string
  sub_hourly_rate: number
  hours: number
  // SUB-3 snapshot of teaming-agreement terms frozen at prime-accept (nullable).
  accepted_rate_markup: number | null
  accepted_prime_labor_category: string | null
  accepted_lcat_mapped: boolean | null
}

export async function getSubVendors(): Promise<SubVendorRow[]> {
  const { data, error } = await supabase
    .from('sub_vendors')
    .select('id, name, description, is_active')
    .eq('is_active', true)
    .order('display_order', { ascending: true })
    .order('name', { ascending: true })
  if (error) throw new Error(`Supabase query error [sub_vendors]: ${error.message}`)
  return (data ?? []) as SubVendorRow[]
}

export async function getTeamingAgreements(): Promise<TeamingAgreementRow[]> {
  const { data, error } = await supabase
    .from('teaming_agreements')
    .select('id, sub_vendor_id, rate_markup, lcat_mappings, is_active')
    .eq('is_active', true)
  if (error) throw new Error(`Supabase query error [teaming_agreements]: ${error.message}`)
  // Postgres returns numeric as string when precision is set; coerce.
  return (data ?? []).map((r) => ({
    ...(r as TeamingAgreementRow),
    rate_markup: Number((r as { rate_markup: number | string }).rate_markup),
  }))
}

export async function getSubTimeEntries(): Promise<SubTimeEntryRow[]> {
  const { data, error } = await supabase
    .from('sub_time_entries')
    .select('id, sub_vendor_id, employee_id, entry_date, prime_charge_code_id, prime_charge_code_label, sub_labor_category, sub_hourly_rate, hours, accepted_rate_markup, accepted_prime_labor_category, accepted_lcat_mapped')
    .order('entry_date', { ascending: false })
  if (error) throw new Error(`Supabase query error [sub_time_entries]: ${error.message}`)
  return (data ?? []).map((r) => {
    const row = r as Record<string, unknown>
    return {
      id: row.id as string,
      sub_vendor_id: row.sub_vendor_id as string,
      employee_id: row.employee_id as string,
      entry_date: row.entry_date as string,
      prime_charge_code_id: row.prime_charge_code_id as string,
      prime_charge_code_label: (row.prime_charge_code_label as string | null) ?? null,
      sub_labor_category: row.sub_labor_category as string,
      sub_hourly_rate: Number(row.sub_hourly_rate),
      hours: Number(row.hours),
      accepted_rate_markup: row.accepted_rate_markup == null ? null : Number(row.accepted_rate_markup),
      accepted_prime_labor_category: (row.accepted_prime_labor_category as string | null) ?? null,
      accepted_lcat_mapped: (row.accepted_lcat_mapped as boolean | null) ?? null,
    }
  })
}

// ---------------------------------------------------------------------------
// Cost Management Studio - universal ledger (migration 00432)
//
//   cm_ledger_lines  - the ACDOCA line-item mirror every cost function reads
//   cm_ledger_syncs  - sync run log (SAP pull or seed load)
//   cm_co_objects    - CO object master + rule/classification attributes
//
// Reads are SCOPED, not "select *": a fiscal year of line items is tens of
// thousands of rows, so every read pushes its filters to the server and pages
// through in 1000-row chunks (the PostgREST default cap). Never fetch the whole
// mirror unfiltered.
// ---------------------------------------------------------------------------
type CmLine = import('@/types/costManagement').CmLedgerLine
type CmSync = import('@/types/costManagement').CmLedgerSync
type CmObject = import('@/types/costManagement').CmCoObject
type CmQuery = import('@/types/costManagement').LedgerQuery

const CM_PAGE = 1000
const CM_DEFAULT_LIMIT = 20000

export async function getCmLedgerLines(q: CmQuery = {}): Promise<CmLine[]> {
  const limit = q.limit ?? CM_DEFAULT_LIMIT
  const out: Record<string, unknown>[] = []

  for (let from = 0; from < limit; from += CM_PAGE) {
    let query = supabase.from('cm_ledger_lines').select('*')
    if (q.orgId) query = query.eq('org_id', q.orgId)
    if (q.companyCode) query = query.eq('company_code', q.companyCode)
    if (q.fiscalYear != null) query = query.eq('fiscal_year', q.fiscalYear)
    if (q.periodFrom != null) query = query.gte('fiscal_period', q.periodFrom)
    if (q.periodTo != null) query = query.lte('fiscal_period', q.periodTo)
    if (q.source) query = query.eq('source', q.source)
    if (q.costCenter) query = query.eq('cost_center', q.costCenter)
    if (q.wbsElement) query = query.eq('wbs_element', q.wbsElement)
    if (q.glAccount) query = query.eq('gl_account', q.glAccount)
    if (q.documentNumber) query = query.eq('document_number', q.documentNumber)

    const to = Math.min(from + CM_PAGE, limit) - 1
    const { data, error } = await query
      .order('fiscal_period', { ascending: true })
      .order('document_number', { ascending: true })
      .order('line_item', { ascending: true })
      .range(from, to)
    if (error) throw new Error(`Supabase query error [cm_ledger_lines]: ${error.message}`)
    const rows = (data ?? []) as Record<string, unknown>[]
    out.push(...rows)
    if (rows.length < CM_PAGE) break
  }
  return mergeJsonbRows(out) as unknown as CmLine[]
}

/** Row count for a scope, without pulling the rows. */
export async function getCmLedgerLineCount(q: CmQuery = {}): Promise<number> {
  let query = supabase.from('cm_ledger_lines').select('id', { count: 'exact', head: true })
  if (q.orgId) query = query.eq('org_id', q.orgId)
  if (q.companyCode) query = query.eq('company_code', q.companyCode)
  if (q.fiscalYear != null) query = query.eq('fiscal_year', q.fiscalYear)
  if (q.periodFrom != null) query = query.gte('fiscal_period', q.periodFrom)
  if (q.periodTo != null) query = query.lte('fiscal_period', q.periodTo)
  if (q.source) query = query.eq('source', q.source)
  const { count, error } = await query
  if (error) throw new Error(`Supabase count error [cm_ledger_lines]: ${error.message}`)
  return count ?? 0
}

/** Upsert generated or synced lines. Chunked so a big run does not blow the body limit. */
export async function upsertCmLedgerLines(lines: CmLine[]): Promise<number> {
  const CHUNK = 500
  let written = 0
  for (let i = 0; i < lines.length; i += CHUNK) {
    const rows = lines.slice(i, i + CHUNK).map((l) => ({
      id: l.id,
      org_id: l.orgId,
      company_code: l.companyCode,
      fiscal_year: l.fiscalYear,
      fiscal_period: l.fiscalPeriod,
      document_number: l.documentNumber,
      line_item: l.lineItem,
      source: l.source,
      gl_account: l.glAccount,
      cost_center: l.costCenter,
      wbs_element: l.wbsElement,
      posting_date: l.postingDate,
      amount: l.amount,
      quantity: l.quantity,
      data: l,
    }))
    const { error } = await supabase.from('cm_ledger_lines').upsert(rows, { onConflict: 'id' })
    if (error) throw new Error(`Supabase upsert [cm_ledger_lines]: ${error.message}`)
    written += rows.length
  }
  return written
}

/** Delete every line a run produced (reversing an unposted run). */
export async function deleteCmLedgerLinesByRun(runId: string): Promise<void> {
  const { error } = await supabase.from('cm_ledger_lines').delete().eq('data->>sourceRunId', runId)
  if (error) throw new Error(`Supabase delete [cm_ledger_lines]: ${error.message}`)
}

export async function getCmLedgerSyncs(orgId?: string): Promise<CmSync[]> {
  let query = supabase.from('cm_ledger_syncs').select('*').order('started_at', { ascending: false }).limit(50)
  if (orgId) query = query.eq('org_id', orgId)
  const { data, error } = await query
  if (error) throw new Error(`Supabase query error [cm_ledger_syncs]: ${error.message}`)
  return mergeJsonbRows((data ?? []) as Record<string, unknown>[]) as unknown as CmSync[]
}

export async function upsertCmLedgerSync(sync: CmSync): Promise<CmSync> {
  const { error } = await supabase.from('cm_ledger_syncs').upsert({
    id: sync.id,
    org_id: sync.orgId,
    status: sync.status,
    source: sync.source,
    company_code: sync.companyCode,
    fiscal_year: sync.fiscalYear,
    started_at: sync.startedAt,
    data: sync,
  })
  if (error) throw new Error(`Supabase upsert [cm_ledger_syncs]: ${error.message}`)
  return sync
}

/**
 * The CO object master, ALL of it.
 *
 * PostgREST caps a select at 1,000 rows and says nothing about it, so a plain
 * read of this table silently returns the first thousand of whatever the
 * planner happened to hand back. That is fine for a seeded demo and wrong for
 * a real company code: 1710 alone has ~5,000 objects through the SAP sync, and
 * the ones a rule names are as likely to sit at row 4,900 as row 3. A picklist
 * that is quietly missing the object you asked for is worse than a slow one,
 * so this pages to the end with a stable order.
 */
export async function getCmCoObjects(orgId?: string): Promise<CmObject[]> {
  const PAGE = 1000
  const MAX_PAGES = 100
  const rows: Record<string, unknown>[] = []

  for (let page = 0; page < MAX_PAGES; page++) {
    let q = supabase
      .from('cm_co_objects')
      .select('*')
      .order('id', { ascending: true })
      .range(page * PAGE, page * PAGE + PAGE - 1)
    if (orgId) q = q.eq('org_id', orgId)
    const { data, error } = await q
    if (error) throw new Error(`Supabase query error [cm_co_objects]: ${error.message}`)
    const batch = (data ?? []) as Record<string, unknown>[]
    rows.push(...batch)
    if (batch.length < PAGE) break
  }

  return mergeJsonbRows(rows) as unknown as CmObject[]
}

export async function upsertCmCoObjects(objects: CmObject[]): Promise<number> {
  const CHUNK = 500
  let written = 0
  for (let i = 0; i < objects.length; i += CHUNK) {
    const rows = objects.slice(i, i + CHUNK).map((o) => ({
      id: o.id,
      org_id: o.orgId,
      object_type: o.objectType,
      object_key: o.objectKey,
      company_code: o.companyCode,
      data: o,
    }))
    const { error } = await supabase.from('cm_co_objects').upsert(rows, { onConflict: 'id' })
    if (error) throw new Error(`Supabase upsert [cm_co_objects]: ${error.message}`)
    written += rows.length
  }
  return written
}

export async function upsertCmCoObject(o: CmObject): Promise<CmObject> {
  await upsertCmCoObjects([o])
  return o
}

// ---------------------------------------------------------------------------
// Cost Management Studio - pool membership + rate segment rules (migration 00434)
// ---------------------------------------------------------------------------
type CasRule = import('@/lib/costmgmt/segmentRules').CasSegmentRule
type CasAssign = import('@/lib/costmgmt/segmentRules').CasObjectAssignment

export async function getCasSegmentRules(orgId?: string): Promise<CasRule[]> {
  return queryJsonbTable('cas_segment_rules', orgId ? 'org_id' : undefined, orgId) as unknown as Promise<CasRule[]>
}
export async function upsertCasSegmentRule(rule: CasRule): Promise<CasRule> {
  const { error } = await supabase.from('cas_segment_rules').upsert({
    id: rule.id,
    org_id: rule.orgId,
    segment_id: rule.segmentId,
    priority: rule.priority,
    status: rule.status,
    data: rule,
  })
  if (error) throw new Error(`Supabase upsert [cas_segment_rules]: ${error.message}`)
  return rule
}
export async function deleteCasSegmentRule(id: string): Promise<void> {
  const { error } = await supabase.from('cas_segment_rules').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete [cas_segment_rules]: ${error.message}`)
}

export async function getCasObjectAssignments(orgId?: string): Promise<CasAssign[]> {
  return queryJsonbTable('cas_object_assignments', orgId ? 'org_id' : undefined, orgId) as unknown as Promise<CasAssign[]>
}
export async function upsertCasObjectAssignment(a: CasAssign): Promise<CasAssign> {
  const { error } = await supabase.from('cas_object_assignments').upsert({
    id: a.id,
    org_id: a.orgId,
    object_type: a.objectType,
    object_key: a.objectKey,
    segment_id: a.segmentId,
    cost_pool_id: a.costPoolId,
    data: a,
  })
  if (error) throw new Error(`Supabase upsert [cas_object_assignments]: ${error.message}`)
  return a
}
export async function deleteCasObjectAssignment(id: string): Promise<void> {
  const { error } = await supabase.from('cas_object_assignments').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete [cas_object_assignments]: ${error.message}`)
}

// ---------------------------------------------------------------------------
// Cost Management Studio - allocations (migration 00436)
// ---------------------------------------------------------------------------
type CmAllocRule = import('@/lib/costmgmt/allocationEngine').AllocationRule
type CmAllocRun = import('@/services/costmgmt/allocationService').AllocationRun

export async function getCmAllocationRules(orgId?: string): Promise<CmAllocRule[]> {
  return queryJsonbTable('cm_allocation_rules', orgId ? 'org_id' : undefined, orgId) as unknown as Promise<CmAllocRule[]>
}
export async function upsertCmAllocationRule(rule: CmAllocRule): Promise<CmAllocRule> {
  const { error } = await supabase.from('cm_allocation_rules').upsert({
    id: rule.id, org_id: rule.orgId, code: rule.code, name: rule.name, status: rule.status, data: rule,
  })
  if (error) throw new Error(`Supabase upsert [cm_allocation_rules]: ${error.message}`)
  return rule
}
export async function deleteCmAllocationRule(id: string): Promise<void> {
  const { error } = await supabase.from('cm_allocation_rules').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete [cm_allocation_rules]: ${error.message}`)
}

export async function getCmAllocationRuns(orgId?: string): Promise<CmAllocRun[]> {
  let query = supabase.from('cm_allocation_runs').select('*').order('executed_at', { ascending: false }).limit(200)
  if (orgId) query = query.eq('org_id', orgId)
  const { data, error } = await query
  if (error) throw new Error(`Supabase query error [cm_allocation_runs]: ${error.message}`)
  return mergeJsonbRows((data ?? []) as Record<string, unknown>[]) as unknown as CmAllocRun[]
}
export async function upsertCmAllocationRun(run: CmAllocRun): Promise<CmAllocRun> {
  const { error } = await supabase.from('cm_allocation_runs').upsert({
    id: run.id,
    org_id: run.orgId,
    rule_id: run.ruleId,
    fiscal_year: run.fiscalYear,
    fiscal_period: run.fiscalPeriod,
    status: run.status,
    executed_at: run.executedAt,
    data: run,
  })
  if (error) throw new Error(`Supabase upsert [cm_allocation_runs]: ${error.message}`)
  return run
}

// ---------------------------------------------------------------------------
// Cost Management Studio - burden rates (migration 00437)
// ---------------------------------------------------------------------------
type CmBurdenBase = import('@/lib/costmgmt/burdenEngine').BurdenBase
type CmBurdenDef = import('@/lib/costmgmt/burdenEngine').BurdenDef
type CmBurdenRun = import('@/services/costmgmt/burdenService').BurdenRun

export async function getCmBurdenBases(orgId?: string): Promise<CmBurdenBase[]> {
  return queryJsonbTable('cm_burden_bases', orgId ? 'org_id' : undefined, orgId) as unknown as Promise<CmBurdenBase[]>
}
export async function upsertCmBurdenBase(b: CmBurdenBase): Promise<CmBurdenBase> {
  const { error } = await supabase.from('cm_burden_bases').upsert({
    id: b.id, org_id: b.orgId, code: b.code, name: b.name, status: b.status, data: b,
  })
  if (error) throw new Error(`Supabase upsert [cm_burden_bases]: ${error.message}`)
  return b
}
export async function deleteCmBurdenBase(id: string): Promise<void> {
  const { error } = await supabase.from('cm_burden_bases').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete [cm_burden_bases]: ${error.message}`)
}

export async function getCmBurdenDefs(orgId?: string): Promise<CmBurdenDef[]> {
  return queryJsonbTable('cm_burden_defs', orgId ? 'org_id' : undefined, orgId) as unknown as Promise<CmBurdenDef[]>
}
export async function upsertCmBurdenDef(d: CmBurdenDef): Promise<CmBurdenDef> {
  const { error } = await supabase.from('cm_burden_defs').upsert({
    id: d.id, org_id: d.orgId, code: d.code, name: d.name, status: d.status,
    valid_from: d.validFrom || null, valid_to: d.validTo || null,
    tier: d.tier, segment_id: d.rateSegmentId, data: d,
  })
  if (error) throw new Error(`Supabase upsert [cm_burden_defs]: ${error.message}`)
  return d
}
export async function deleteCmBurdenDef(id: string): Promise<void> {
  const { error } = await supabase.from('cm_burden_defs').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete [cm_burden_defs]: ${error.message}`)
}

export async function getCmBurdenRuns(orgId?: string): Promise<CmBurdenRun[]> {
  let query = supabase.from('cm_burden_runs').select('*').order('executed_at', { ascending: false }).limit(200)
  if (orgId) query = query.eq('org_id', orgId)
  const { data, error } = await query
  if (error) throw new Error(`Supabase query error [cm_burden_runs]: ${error.message}`)
  return mergeJsonbRows((data ?? []) as Record<string, unknown>[]) as unknown as CmBurdenRun[]
}
export async function upsertCmBurdenRun(run: CmBurdenRun): Promise<CmBurdenRun> {
  const { error } = await supabase.from('cm_burden_runs').upsert({
    id: run.id, org_id: run.orgId, fiscal_year: run.fiscalYear, fiscal_period: run.fiscalPeriod,
    status: run.status, executed_at: run.executedAt, data: run,
  })
  if (error) throw new Error(`Supabase upsert [cm_burden_runs]: ${error.message}`)
  return run
}

// ---------------------------------------------------------------------------
// Cost Management Studio - cost transfers (migration 00439)
// ---------------------------------------------------------------------------
type CmTransfer = import('@/services/costmgmt/transferService').CostTransfer

export async function getCmTransfers(orgId?: string): Promise<CmTransfer[]> {
  let query = supabase.from('cm_transfers').select('*').order('requested_at', { ascending: false }).limit(300)
  if (orgId) query = query.eq('org_id', orgId)
  const { data, error } = await query
  if (error) throw new Error(`Supabase query error [cm_transfers]: ${error.message}`)
  return mergeJsonbRows((data ?? []) as Record<string, unknown>[]) as unknown as CmTransfer[]
}
export async function upsertCmTransfer(t: CmTransfer): Promise<CmTransfer> {
  const { error } = await supabase.from('cm_transfers').upsert({
    id: t.id,
    org_id: t.orgId,
    code: t.code,
    status: t.status,
    transfer_type: t.transferType,
    fiscal_year: t.fiscalYear,
    fiscal_period: t.fiscalPeriod,
    requested_by: t.requestedBy,
    requested_at: t.requestedAt,
    workflow_run_id: t.workflowRunId,
    data: t,
  })
  if (error) throw new Error(`Supabase upsert [cm_transfers]: ${error.message}`)
  return t
}
export async function deleteCmTransfer(id: string): Promise<void> {
  const { error } = await supabase.from('cm_transfers').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete [cm_transfers]: ${error.message}`)
}

// ---------------------------------------------------------------------------
// Cost Management Studio - incurred cost reporting (migration 00440)
// ---------------------------------------------------------------------------
type CmIcrSub = import('@/services/costmgmt/icrService').IcrSubmission
type CmIcrInput = import('@/services/costmgmt/icrService').IcrInputRow

export async function getCmIcrSubmissions(orgId?: string): Promise<CmIcrSub[]> {
  let query = supabase.from('cm_icr_submissions').select('*').order('generated_at', { ascending: false }).limit(100)
  if (orgId) query = query.eq('org_id', orgId)
  const { data, error } = await query
  if (error) throw new Error(`Supabase query error [cm_icr_submissions]: ${error.message}`)
  return mergeJsonbRows((data ?? []) as Record<string, unknown>[]) as unknown as CmIcrSub[]
}
export async function upsertCmIcrSubmission(s: CmIcrSub): Promise<CmIcrSub> {
  const { error } = await supabase.from('cm_icr_submissions').upsert({
    id: s.id, org_id: s.orgId, segment_id: s.segmentId, fiscal_year: s.fiscalYear,
    status: s.status, generated_at: s.generatedAt, data: s,
  })
  if (error) throw new Error(`Supabase upsert [cm_icr_submissions]: ${error.message}`)
  return s
}

export async function getCmIcrInputs(orgId?: string): Promise<CmIcrInput[]> {
  return queryJsonbTable('cm_icr_inputs', orgId ? 'org_id' : undefined, orgId) as unknown as Promise<CmIcrInput[]>
}
export async function upsertCmIcrInput(row: CmIcrInput): Promise<CmIcrInput> {
  const { error } = await supabase.from('cm_icr_inputs').upsert({
    id: row.id, org_id: row.orgId, segment_id: row.segmentId,
    fiscal_year: row.fiscalYear, input_key: row.inputKey, data: row.data,
  })
  if (error) throw new Error(`Supabase upsert [cm_icr_inputs]: ${error.message}`)
  return row
}
